(() => {
  const init = () => {
    const main = document.getElementById('main-content');
    if (!main) return;
    let frame;
    const visible = (element, measure) => {
      const closedDetails = element.closest('details:not([open])');
      if (closedDetails && element !== closedDetails && !closedDetails.querySelector('summary')?.contains(element)) return false;
      const { rect, style } = measure(element);
      return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && !['fixed', 'absolute'].includes(style.position);
    };
    const update = () => {
      frame = null;
      const footer = document.querySelector('.shopify-section-group-footer-group[id$="__rich_text_rCHiC7"]');
      const richText = footer?.querySelector('.rich-text');
      if (!richText) return;
      const measurements = new Map();
      const measure = (element) => {
        if (!measurements.has(element)) {
          measurements.set(element, {
            rect: element.getBoundingClientRect(),
            style: getComputedStyle(element)
          });
        }
        return measurements.get(element);
      };
      const last = [...main.children].reverse().find((element) => visible(element, measure));
      if (!last) return;
      const edge = richText.getBoundingClientRect();
      const elements = [last, ...last.querySelectorAll('*')].filter((element) => visible(element, measure));
      const content = elements.filter((element) => element.matches('p, h1, h2, h3, h4, h5, h6, ul, ol, form, figure, img, table, details, button, input, textarea, select, hr') || [...element.childNodes].some((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()));
      const contentBottom = Math.max(measure(last).rect.top, ...content.map((element) => measure(element).rect.bottom));
      const productTail = elements.some((element) => element.matches('.products, .featured-collection, .collection-tabs, .tec-hub-products, product-recommendations') && element.querySelector('.product-card') && measure(element).rect.bottom >= contentBottom - 2);
      const pageBackground = getComputedStyle(main).backgroundColor;
      const hasBoundary = elements.some((element) => {
        const { rect, style } = measure(element);
        if (rect.width < edge.width * 0.75) return false;
        const distance = edge.top - rect.bottom;
        const border = parseFloat(style.borderBottomWidth) > 0 || (rect.height <= 2 && parseFloat(style.borderTopWidth) > 0);
        if (border && distance >= -2 && distance <= 24) return true;
        if (element.matches('.spacer.has-border-separator') && distance >= -2 && distance + rect.height / 2 <= 24) return true;
        const background = style.backgroundColor;
        const colored = background !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(background) && background !== pageBackground;
        return distance >= -2 && distance <= 2 && (colored || style.backgroundImage !== 'none');
      });
      footer.dataset.tecFooterSeparator = productTail || hasBoundary || edge.top - contentBottom < 24 ? 'hidden' : 'visible';
    };
    const schedule = () => {
      if (frame == null) frame = requestAnimationFrame(update);
    };
    new ResizeObserver(schedule).observe(main);
    new MutationObserver(schedule).observe(main, { childList: true, subtree: true });
    main.addEventListener('toggle', schedule, true);
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', schedule);
    document.addEventListener('shopify:section:unload', schedule);
    document.addEventListener('shopify:section:reorder', schedule);
    document.fonts?.ready.then(schedule);
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();

// Preserve the page width when a native drawer locks viewport scrolling.
(() => {
  const init = () => {
    const root = document.documentElement;
    let scrollbarWidth = window.innerWidth - root.getBoundingClientRect().width;
    const sync = () => {
      if (!document.body.classList.contains('open-cc')) {
        root.style.removeProperty('--tec-scrollbar-compensation');
        scrollbarWidth = window.innerWidth - root.getBoundingClientRect().width;
        return;
      }
      const reservedWidth = window.innerWidth - root.getBoundingClientRect().width;
      root.style.setProperty('--tec-scrollbar-compensation', `${Math.max(0, scrollbarWidth - reservedWidth)}px`);
    };
    document.addEventListener('panel:open', sync);
    document.addEventListener('panel:closed', sync);
    window.addEventListener('resize', sync, { passive: true });
    sync();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

if (!customElements.get('tec-slideshow-navigation')) {
  customElements.define('tec-slideshow-navigation', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      const slideshow = this.closest('.tec-home-slideshow');
      if (!slideshow) return;
      this.listeners = new AbortController();
      this.addEventListener('keyup', (event) => {
        if (event.target.closest('button.flickity-nav')) event.stopPropagation();
      }, { capture: true, signal: this.listeners.signal });
      let attempts = 0;
      const connect = () => {
        if (!this.isConnected) return;
        const carousel = slideshow._flkty;
        if (!carousel) {
          if (++attempts < 300) this.frame = requestAnimationFrame(connect);
          return;
        }
        this.carousel = carousel;
        this.prepareMobileMotion(slideshow, carousel);
        this.prepareMobileBackdrop(slideshow, carousel);
        this.prepareLineReflow(slideshow, carousel);
        const viewport = window.matchMedia('(max-width: 767px)');
        const updateAccents = () => carousel.cells.forEach(({ element }) => this.setSlideAccent(element, viewport.matches));
        viewport.addEventListener('change', updateAccents, { signal: this.listeners.signal });
        updateAccents();
        this.update = () => {
          const index = carousel.selectedIndex;
          const total = carousel.cells.length;
          this.querySelector('[data-slideshow-current]').textContent = String(index + 1).padStart(2, '0');
          this.querySelector('[data-slideshow-total]').textContent = String(total).padStart(2, '0');
          this.transitionLabel((carousel.selectedElement.querySelector('.slideshow__slide-heading')?.textContent || carousel.selectedElement.querySelector('.inline-badge')?.textContent || '').replace(/\s+/g, ' ').trim());
        };
        carousel.on('change', this.update);
        this.update();
        const progress = this.querySelector('[data-slideshow-progress]');
        const duration = Number(carousel.options.autoPlay);
        if (duration > 0 && carousel.cells.length > 1 && carousel.player) {
          const player = carousel.player;
          const originalTick = player.tick;
          const originalClear = player.clear;
          const reset = () => {
            this.progressAnimation?.cancel();
            this.progressAnimation = null;
          };
          const restart = () => {
            reset();
            this.progressAnimation = progress.animate([
              { transform: 'scaleX(0)' },
              { transform: 'scaleX(1)' }
            ], { duration: Number(carousel.options.autoPlay), easing: 'linear', fill: 'forwards' });
          };
          const tick = function (...args) {
            const result = originalTick.apply(this, args);
            if (this.state === 'playing') restart();
            return result;
          };
          const clear = function (...args) {
            const result = originalClear.apply(this, args);
            reset();
            return result;
          };
          player.tick = tick;
          player.clear = clear;
          this.restorePlayer = () => {
            if (player.tick === tick) player.tick = originalTick;
            if (player.clear === clear) player.clear = originalClear;
          };
          if (player.state === 'playing') restart();
        } else {
          progress.style.transform = 'scaleX(1)';
        }
      };
      this.frame = requestAnimationFrame(connect);
    }
    transitionLabel(text) {
      const label = this.querySelector('[data-slideshow-label]');
      if (!label || this.pendingLabel === text) return;
      this.pendingLabel = text;
      const version = this.labelVersion = (this.labelVersion || 0) + 1;
      this.labelAnimation?.cancel();
      if (label.textContent.trim() === text || this.closest('.tec-home-slideshow').dataset.labelAnimation === 'false' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        label.textContent = text;
        return;
      }
      this.labelAnimation = label.animate([
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-4px)' }
      ], { duration: 140, easing: 'ease-in', fill: 'forwards' });
      this.labelAnimation.finished.then(() => {
        if (!this.isConnected || this.labelVersion !== version) return;
        label.textContent = text;
        this.labelAnimation = label.animate([
          { opacity: 0, transform: 'translateY(5px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 260, easing: 'ease-out', fill: 'both' });
      }).catch(() => {});
    }
    prepareMobileMotion(slideshow, carousel) {
      if (!window.CSS?.supports('-webkit-touch-callout', 'none') || carousel.options.fade) return;
      const viewport = window.matchMedia('(max-width: 767px)');
      const original = carousel.setTranslateX;
      // Flickity switches between 3D and 2D transforms when a slide settles.
      // Keep one rendering mode and avoid fractional device pixels on iOS.
      const alignedTranslate = function (position, is3d) {
        if (!viewport.matches) return original.call(this, position, is3d);
        const direction = this.options.rightToLeft ? -1 : 1;
        const ratio = Math.max(1, window.devicePixelRatio || 1);
        const pixels = Math.round((position + this.cursorPosition) * direction * ratio) / ratio;
        this.slider.style.transform = `translate3d(${pixels}px,0,0)`;
      };
      carousel.setTranslateX = alignedTranslate;
      const sync = () => {
        slideshow.classList.toggle('tec-ios-slideshow-motion', viewport.matches);
        carousel.positionSlider();
      };
      viewport.addEventListener('change', sync, { signal: this.listeners.signal });
      this.restoreMobileMotion = () => {
        if (carousel.setTranslateX === alignedTranslate) carousel.setTranslateX = original;
        slideshow.classList.remove('tec-ios-slideshow-motion');
      };
      sync();
    }
    prepareMobileBackdrop(slideshow, carousel) {
      if (!window.CSS?.supports('-webkit-touch-callout', 'none')) return;
      const viewport = window.matchMedia('(max-width: 767px)');
      const signal = this.listeners.signal;
      const layers = new Map();
      let frame;
      const clear = () => {
        layers.forEach(({ panel, layer }) => {
          panel.classList.remove('tec-mobile-frosted');
          layer.remove();
        });
        layers.clear();
      };
      const update = () => {
        if (signal.aborted) return;
        if (!viewport.matches) return clear();
        carousel.cells.forEach(({ element: slide }) => {
          if (slide.querySelector('.slideshow__slide-video-bg')) return;
          const panel = slide.querySelector('.slideshow__slide-content');
          const image = slide.querySelector('.slideshow-image--mobile') || slide.querySelector('.slideshow__slide-bg img');
          if (!panel || !image?.complete || !image.naturalWidth) return;
          const source = image.currentSrc || image.src;
          if (!source || source.startsWith('data:') || image.naturalWidth <= 20) return;
          const imageBox = image.getBoundingClientRect();
          const panelBox = panel.getBoundingClientRect();
          if (!imageBox.width || !imageBox.height || !panelBox.width) return;
          let entry = layers.get(panel);
          if (!entry) {
            const layer = document.createElement('span');
            const copy = document.createElement('img');
            layer.className = 'tec-slideshow-frost';
            layer.setAttribute('aria-hidden', 'true');
            copy.alt = '';
            copy.draggable = false;
            copy.decoding = 'async';
            layer.append(copy);
            panel.prepend(layer);
            entry = { panel, layer, copy, source: '' };
            layers.set(panel, entry);
          }
          const { copy } = entry;
          const style = getComputedStyle(image);
          copy.style.left = `${imageBox.left - panelBox.left}px`;
          copy.style.top = `${imageBox.top - panelBox.top}px`;
          copy.style.width = `${imageBox.width}px`;
          copy.style.height = `${imageBox.height}px`;
          copy.style.objectFit = style.objectFit;
          copy.style.objectPosition = style.objectPosition;
          if (entry.source === source) return;
          entry.source = source;
          panel.classList.remove('tec-mobile-frosted');
          copy.src = source;
          copy.decode().then(() => {
            if (!signal.aborted && viewport.matches && layers.get(panel) === entry && entry.source === source) {
              panel.classList.add('tec-mobile-frosted');
            }
          }).catch(() => {});
        });
      };
      const schedule = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      };
      const observer = new ResizeObserver(schedule);
      observer.observe(slideshow);
      carousel.cells.forEach(({ element }) => {
        const panel = element.querySelector('.slideshow__slide-content');
        if (panel) observer.observe(panel);
      });
      viewport.addEventListener('change', schedule, { signal });
      slideshow.addEventListener('load', (event) => {
        if (event.target.closest?.('.slideshow__slide-bg')) schedule();
      }, { capture: true, signal });
      this.restoreMobileBackdrop = () => {
        observer.disconnect();
        cancelAnimationFrame(frame);
        clear();
      };
      schedule();
    }
    prepareLineReflow(slideshow, carousel) {
      if (!slideshow._animations_enabled) return;
      const signal = this.listeners.signal;
      const rebuild = () => {
        if (signal.aborted || !this.isConnected || slideshow._flkty !== carousel) return;
        slideshow._animations.forEach((timeline) => timeline.kill());
        slideshow._splitTexts?.slice().reverse().forEach((split) => split.revert());
        slideshow._animations.length = 0;
        slideshow.dataset.animationsReady = '';
        slideshow.prepareAnimations(slideshow, slideshow._animations);
        slideshow.animateSlides(carousel.selectedIndex, slideshow, slideshow._animations);
      };
      document.fonts.ready.then(() => {
        if (!signal.aborted) this.lineFrame = requestAnimationFrame(rebuild);
      });
      let previousWidth = slideshow.getBoundingClientRect().width;
      this.lineResizeObserver = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width;
        if (Math.abs(width - previousWidth) < 1) return;
        previousWidth = width;
        clearTimeout(this.lineResizeTimer);
        this.lineResizeTimer = setTimeout(rebuild, 150);
      });
      this.lineResizeObserver.observe(slideshow);
    }
    setSlideAccent(slide, mobile) {
      if (slide.dataset.badgeAuto === 'false') {
        slide.style.removeProperty('--tec-slide-accent');
        slide.style.removeProperty('--tec-slide-accent-text');
        return;
      }
      const images = slide.querySelectorAll('.slideshow__slide-bg img');
      const image = (mobile && slide.querySelector('.slideshow__slide-bg .slideshow-image--mobile')) || images[0];
      const source = image?.getAttribute('data-src') || image?.currentSrc || image?.src;
      if (!source || source.startsWith('data:')) return;
      const url = new URL(source, window.location.href);
      if (!url.pathname.includes('/cdn/shop/') && url.hostname !== 'cdn.shopify.com') return;
      url.searchParams.set('width', '96');
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      const key = `${url.href}|${mobile ? 'mobile' : 'desktop'}`;
      if (slide.dataset.tecAccentSource === key) return;
      slide.dataset.tecAccentSource = key;
      this.accentCache ||= new Map();
      const apply = (color) => {
        if (!this.isConnected || slide.dataset.tecAccentSource !== key) return;
        slide.style.setProperty('--tec-slide-accent', color);
        slide.style.setProperty('--tec-slide-accent-text', '#fff');
      };
      if (this.accentCache.has(key)) {
        apply(this.accentCache.get(key));
        return;
      }
      const sample = new Image();
      sample.crossOrigin = 'anonymous';
      sample.decoding = 'async';
      this.accentSamples ||= new Set();
      this.accentSamples.add(sample);
      sample.onload = () => {
        this.accentSamples.delete(sample);
        if (!this.isConnected) return;
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 64;
          canvas.height = 32;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context) return;
          const imageBox = image.getBoundingClientRect();
          const panelBox = slide.querySelector('.slideshow__slide-content').getBoundingClientRect();
          const scale = Math.max(imageBox.width / sample.naturalWidth, imageBox.height / sample.naturalHeight);
          const position = getComputedStyle(image).objectPosition.split(' ').map((value) => parseFloat(value) / 100);
          const offsetX = (sample.naturalWidth * scale - imageBox.width) * (Number.isFinite(position[0]) ? position[0] : .5);
          const offsetY = (sample.naturalHeight * scale - imageBox.height) * (Number.isFinite(position[1]) ? position[1] : .5);
          const sourceX = Math.max(0, (panelBox.left - imageBox.left + offsetX) / scale);
          const sourceY = Math.max(0, (panelBox.top - imageBox.top + offsetY) / scale);
          const sourceWidth = Math.min(sample.naturalWidth - sourceX, panelBox.width / scale);
          const sourceHeight = Math.min(sample.naturalHeight - sourceY, panelBox.height / scale);
          if (sourceWidth <= 0 || sourceHeight <= 0) return;
          context.drawImage(sample, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, 64, 32);
          const pixels = context.getImageData(0, 0, 64, 32).data;
          const buckets = Array.from({ length: 24 }, () => ({ weight: 0, rgb: [0, 0, 0] }));
          const neutral = { weight: 0, rgb: [0, 0, 0] };
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] < 200) continue;
            const rgb = [pixels[i], pixels[i + 1], pixels[i + 2]];
            const max = Math.max(...rgb), min = Math.min(...rgb), delta = max - min;
            const lightness = (max + min) / 510;
            if (lightness < .12 || lightness > .86) continue;
            neutral.weight++;
            rgb.forEach((channel, c) => neutral.rgb[c] += channel);
            const saturation = delta / (255 - Math.abs(max + min - 255) || 1);
            if (saturation < .16) continue;
            let hue = max === rgb[0] ? (rgb[1] - rgb[2]) / delta : max === rgb[1] ? (rgb[2] - rgb[0]) / delta + 2 : (rgb[0] - rgb[1]) / delta + 4;
            hue = (hue * 60 + 360) % 360;
            const bucket = buckets[Math.floor(hue / 15)];
            const weight = .5 + saturation;
            bucket.weight += weight;
            rgb.forEach((channel, c) => bucket.rgb[c] += channel * weight);
          }
          const dominant = buckets.reduce((best, bucket) => bucket.weight > best.weight ? bucket : best, buckets[0]);
          const result = dominant.weight ? dominant : neutral;
          if (!result.weight) return;
          let rgb = result.rgb.map((channel) => channel / result.weight);
          const luminance = (channels) => channels.map((channel) => {
            const value = channel / 255;
            return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
          }).reduce((total, channel, i) => total + channel * [.2126, .7152, .0722][i], 0);
          while (luminance(rgb) > .18) rgb = rgb.map((channel) => channel * .96);
          const color = '#' + rgb.map((channel) => Math.round(channel).toString(16).padStart(2, '0')).join('');
          this.accentCache.set(key, color);
          apply(color);
        } catch {}
      };
      sample.onerror = () => this.accentSamples.delete(sample);
      sample.src = url.href;
    }
    disconnectedCallback() {
      this.labelVersion = (this.labelVersion || 0) + 1;
      this.labelAnimation?.cancel();
      this.labelAnimation = null;
      this.pendingLabel = null;
      this.lineResizeObserver?.disconnect();
      clearTimeout(this.lineResizeTimer);
      cancelAnimationFrame(this.lineFrame);
      this.accentSamples?.forEach((sample) => { sample.onload = null; sample.onerror = null; });
      this.accentSamples?.clear();
      cancelAnimationFrame(this.frame);
      this.listeners?.abort();
      if (this.carousel && this.update) this.carousel.off('change', this.update);
      this.restoreMobileMotion?.();
      this.restoreMobileMotion = null;
      this.restoreMobileBackdrop?.();
      this.restoreMobileBackdrop = null;
      this.restorePlayer?.();
      this.restorePlayer = null;
      this.progressAnimation?.cancel();
      this.progressAnimation = null;
      this.carousel = null;
    }
  });
}

(() => {
  console.log('[TEC - DEV]');

  // Keep links on the French storefront when a third-party script adds the English prefix.
  const keepFrenchStorefrontLinks = () => {
    if (document.documentElement.lang !== 'fr' || /^\/(?:[a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i.test(window.location.pathname)) return;

    const fixLink = (link) => {
      const href = link.getAttribute('href');
      if (!href || !/^(?:\/en(?:\/|[?#]|$)|https?:\/\/)/i.test(href)) return;

      let url;
      try { url = new URL(href, window.location.href); } catch { return; }
      if (url.origin !== window.location.origin || !/^\/en(?:\/|$)/.test(url.pathname)) return;

      url.pathname = url.pathname.slice(3) || '/';
      link.setAttribute('href', href.startsWith('/') ? `${url.pathname}${url.search}${url.hash}` : url.href);
    };

    const fixLinksIn = (root) => {
      if (root.nodeType !== Node.ELEMENT_NODE) return;
      if (root.matches('a[href]')) fixLink(root);
      root.querySelectorAll('a[href]').forEach(fixLink);
    };

    fixLinksIn(document.body);
    new MutationObserver((changes) => {
      changes.forEach((change) => {
        if (change.type === 'attributes') fixLink(change.target);
        else change.addedNodes.forEach(fixLinksIn);
      });
    }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['href'] });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', keepFrenchStorefrontLinks, { once: true });
  else keepFrenchStorefrontLinks();

  if (window.gsap) {
    window.gsap.defaults({
      ease: window.Power4?.easeOut || 'power4.out'
    });
    window.gsap.config({
      nullTargetWarn: false
    });
    const animationPlugins = [window.ScrollTrigger, window.DrawSVGPlugin].filter(Boolean);
    if (animationPlugins.length) {
      window.gsap.registerPlugin(...animationPlugins);
    }
  }

  if (!customElements.get('tec-cart-featured')) {
    customElements.define('tec-cart-featured', class extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('.cart-drawer-featured__grid');
        this.navigation = this.querySelector('.cart-drawer-featured__navigation');
        this.previous = this.querySelector('[data-cart-featured-previous]');
        this.next = this.querySelector('[data-cart-featured-next]');
        if (!this.grid || !this.navigation || !this.previous || !this.next) return;

        this.listeners = new AbortController();
        const options = { signal: this.listeners.signal };
        this.updateNavigation = () => {
          const maximum = this.grid.scrollWidth - this.grid.clientWidth;
          const position = Math.abs(this.grid.scrollLeft);
          this.navigation.hidden = maximum <= 1;
          this.previous.disabled = position <= 1;
          this.next.disabled = position >= maximum - 1;
        };
        const scrollProducts = (direction) => {
          const card = this.grid.querySelector('.cart-drawer-featured__item');
          if (!card) return;
          const styles = getComputedStyle(this.grid);
          const step = card.getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
          const count = Math.max(1, Math.floor(this.grid.clientWidth / step));
          this.grid.scrollBy({
            left: direction * (styles.direction === 'rtl' ? -1 : 1) * step * count,
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
          });
        };

        this.previous.addEventListener('click', () => scrollProducts(-1), options);
        this.next.addEventListener('click', () => scrollProducts(1), options);
        this.grid.addEventListener('scroll', this.updateNavigation, { ...options, passive: true });
        this.resizeObserver = new ResizeObserver(this.updateNavigation);
        this.resizeObserver.observe(this.grid);
        this.updateNavigation();
      }

      disconnectedCallback() {
        this.listeners?.abort();
        this.resizeObserver?.disconnect();
      }
    });
  }

  const ANIMATED_LINK_UNDERLINE_CLASS = 'tec-link-underline--animated';

  const bindAnimatedLinkUnderlines = (root = document) => {
    const links = [];
    const closestLink = root.closest?.('a[href]');

    if (closestLink) links.push(closestLink);
    if (root.matches?.('a[href]')) links.push(root);
    links.push(...root.querySelectorAll?.('a[href]') || []);

    let candidates = [...new Set(links)];
    const visited = new Set();

    // Read one depth before writing classes; parents still precede inherited styles.
    while (candidates.length) {
      const next = [];
      const underlined = [];

      candidates.forEach((candidate) => {
        if (visited.has(candidate)) return;
        visited.add(candidate);
        next.push(...candidate.children);
        if (!(candidate instanceof HTMLElement)) return;
        if (candidate.classList.contains(ANIMATED_LINK_UNDERLINE_CLASS)) return;
        if (!candidate.textContent?.trim()) return;

        const decorationLines = window.getComputedStyle(candidate).textDecorationLine.split(' ');
        if (decorationLines.includes('underline')) {
          underlined.push(candidate);
        }
      });
      underlined.forEach((candidate) => candidate.classList.add(ANIMATED_LINK_UNDERLINE_CLASS));
      candidates = next;
    }
  };

  const syncRecommendationsState = (recommendations) => {
    const panel = recommendations.closest('[data-recommendations-panel]');
    const drawer = recommendations.closest('cart-drawer');

    if (!panel || !drawer) {
      return;
    }

    const hasRecommendations = recommendations.innerHTML.trim().length > 0;

    panel.classList.toggle('cart-drawer-recommendations-panel--loaded', hasRecommendations);
    drawer.classList.toggle('cart-drawer--has-recommendations', hasRecommendations);
  };

  const bindRecommendationsPanel = (recommendations) => {
    if (recommendations.dataset.tecRecommendationsBound === 'true') {
      syncRecommendationsState(recommendations);
      return;
    }

    recommendations.dataset.tecRecommendationsBound = 'true';

    const observer = new MutationObserver(() => {
      syncRecommendationsState(recommendations);
    });

    observer.observe(recommendations, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    syncRecommendationsState(recommendations);
  };

  const bindAllRecommendationsPanels = (root = document) => {
    root.querySelectorAll('cart-drawer [data-recommendations-panel] product-recommendations').forEach(bindRecommendationsPanel);
  };

  const cleanEventPayload = (payload) => Object.entries(payload).reduce((cleaned, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }

    return cleaned;
  }, {});

  const publishNexusEvent = (eventName, payload) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return false;

    try {
      publish.call(window.Shopify.analytics, eventName, cleanEventPayload(payload));
      return true;
    } catch (err) {
      console.warn('TEC analytics event échoué:', err);
      return false;
    }
  };

  const getNewsletterSignupDedupeKey = (email) => [
    'nexus:newsletter_signup',
    (email || '').toLowerCase(),
    window.location.pathname
  ].join('|');

  const publishNewsletterSignupEvent = (container) => {
    if (!container || container.dataset.newsletterSuccess !== 'true') {
      return;
    }

    const email = (container.dataset.newsletterEmail || '').trim();

    if (!email) {
      return;
    }

    const dedupeKey = getNewsletterSignupDedupeKey(email);

    try {
      if (window.sessionStorage?.getItem(dedupeKey) === 'true') {
        return;
      }
    } catch (err) {
      // sessionStorage can be unavailable in restricted browser contexts.
    }

    if (publishNexusEvent('nexus:newsletter_signup', {
      email,
      locale_code: container.dataset.newsletterLocale || window.Shopify?.locale || window.__SHOP_LOCALE__ || 'fr',
      page_path: window.location.pathname,
      source: 'shopify_customer_form'
    })) {
      try {
        window.sessionStorage?.setItem(dedupeKey, 'true');
      } catch (err) {
        // The event has already been published; storage failure should not affect the page.
      }
    }
  };

  const bindNewsletterSignup = (container) => {
    if (container.dataset.tecNewsletterBound === 'true') {
      return;
    }

    container.dataset.tecNewsletterBound = 'true';
    publishNewsletterSignupEvent(container);
  };

  const bindAllNewsletterSignups = (root = document) => {
    root.querySelectorAll('[data-nexus-newsletter-signup]').forEach(bindNewsletterSignup);
  };

  const getBackInStockModal = (alert) => {
    if (!alert.dataset.modalId) {
      return null;
    }

    return document.getElementById(alert.dataset.modalId);
  };

  const getBackInStockProductForm = (alert) => {
    const sectionId = alert.dataset.sectionId;

    if (!sectionId) {
      return null;
    }

    return document.getElementById(`product-form-${sectionId}`);
  };

  const getBackInStockAddButton = (alert) => {
    const holder = alert.closest('.product--product-form');

    if (!holder) {
      return null;
    }

    return holder.querySelector('product-form:not([data-sticky]) .single-add-to-cart-button');
  };

  const setBackInStockMessage = (modal, type, text) => {
    const message = modal && modal.querySelector('[data-back-in-stock-message]');

    if (!message) {
      return;
    }

    message.textContent = text;
    message.classList.remove('back-in-stock-alert__message--success', 'back-in-stock-alert__message--error');
    message.classList.add(`back-in-stock-alert__message--${type}`);
    message.hidden = false;
  };

  const clearBackInStockMessage = (modal) => {
    const message = modal && modal.querySelector('[data-back-in-stock-message]');

    if (!message) {
      return;
    }

    message.textContent = '';
    message.classList.remove('back-in-stock-alert__message--success', 'back-in-stock-alert__message--error');
    message.hidden = true;
  };

  const resetBackInStockModal = (alert, modal) => {
    const form = modal && modal.querySelector('[data-back-in-stock-form]');
    const text = modal && modal.querySelector('[data-back-in-stock-text]');

    if (form) {
      form.classList.remove('back-in-stock-alert__form--success');
      form.reset();
    }

    if (text) {
      if (!text.dataset.initialText) {
        text.dataset.initialText = text.textContent;
      }

      text.textContent = text.dataset.initialText;
    }

    clearBackInStockMessage(modal);
  };

  const setBackInStockSuccess = (modal, text) => {
    const form = modal && modal.querySelector('[data-back-in-stock-form]');
    const textElement = modal && modal.querySelector('[data-back-in-stock-text]');

    if (textElement) {
      if (!textElement.dataset.initialText) {
        textElement.dataset.initialText = textElement.textContent;
      }

      textElement.textContent = text;
    }

    if (form) {
      form.classList.add('back-in-stock-alert__form--success');
    }

    clearBackInStockMessage(modal);
  };

  const publishBackInStockSignupEvent = (alert, email) => {
    publishNexusEvent('nexus:back_in_stock_signup', {
      shopify_product_id: alert.dataset.productId,
      shopify_variant_id: alert.dataset.variantId,
      locale_code: alert.dataset.locale || window.Shopify?.locale || window.__SHOP_LOCALE__ || 'fr',
      email,
      shopify_customer_id: alert.dataset.customerId || null,
      contact_type: alert.dataset.customerId ? 'customer' : 'email',
      product_title: alert.dataset.productTitle || null,
      product_handle: alert.dataset.productHandle || null,
      variant_title: alert.dataset.variantTitle || null
    });
  };

  const syncBackInStockAlert = (alert) => {
    const productForm = getBackInStockProductForm(alert);
    const variantInput = productForm && productForm.querySelector('input[name="id"]');
    const addButton = getBackInStockAddButton(alert);
    const purchaseHolder = alert.closest('.product--product-form');
    const integratedCart = purchaseHolder && purchaseHolder.querySelector('[data-aeternum-integrated-cart]');
    const dynamicCheckout = purchaseHolder && purchaseHolder.querySelector('.aeternum-dynamic-checkout');
    const buttonText = addButton && addButton.querySelector('.single-add-to-cart-button--text');
    const unavailableText = (alert.dataset.unavailableText || '').trim();
    const currentText = (buttonText ? buttonText.textContent : '').trim();
    const variantId = variantInput && variantInput.value;
    const isUnavailable = unavailableText && currentText === unavailableText;
    const isSoldOut = Boolean(variantId && addButton && addButton.disabled && !isUnavailable);

    if (variantId) {
      alert.dataset.variantId = variantId;
    }

    alert.hidden = !isSoldOut;
    if (integratedCart) {
      integratedCart.hidden = isSoldOut;
    }
    if (dynamicCheckout) {
      dynamicCheckout.hidden = isSoldOut;
    }
  };

  const syncBackInStockAlerts = (sectionId = null) => {
    document.querySelectorAll('[data-back-in-stock-alert]').forEach((alert) => {
      if (sectionId && alert.dataset.sectionId !== sectionId) {
        return;
      }

      syncBackInStockAlert(alert);
    });
  };

  const bindBackInStockAlert = (alert) => {
    if (alert.dataset.tecBackInStockBound === 'true') {
      syncBackInStockAlert(alert);
      return;
    }

    alert.dataset.tecBackInStockBound = 'true';

    const productForm = getBackInStockProductForm(alert);
    const variantInput = productForm && productForm.querySelector('input[name="id"]');
    const modal = getBackInStockModal(alert);
    const form = modal && modal.querySelector('[data-back-in-stock-form]');
    const opener = alert.querySelector('.back-in-stock-alert__trigger');

    if (variantInput) {
      variantInput.addEventListener('change', () => {
        window.setTimeout(() => syncBackInStockAlert(alert), 0);
      });
    }

    if (opener) {
      opener.addEventListener('click', () => {
        resetBackInStockModal(alert, modal);
        syncBackInStockAlert(alert);
      });
    }

    if (form && modal) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (alert.hidden) {
          return;
        }

        const submitButton = form.querySelector('[type="submit"]');
        const emailInput = form.querySelector('input[name="email"]');
        const email = emailInput ? emailInput.value.trim() : (alert.dataset.customerEmail || '').trim();
        const customerId = (alert.dataset.customerId || '').trim();

        if (!customerId && emailInput && !emailInput.checkValidity()) {
          setBackInStockMessage(modal, 'error', alert.dataset.requiredMessage || alert.dataset.errorMessage);
          emailInput.reportValidity();
          return;
        }

        clearBackInStockMessage(modal);

        const payload = {
          shopify_product_id: alert.dataset.productId,
          shopify_variant_id: alert.dataset.variantId,
          locale_code: alert.dataset.locale || window.Shopify?.locale || 'fr'
        };

        if (customerId) {
          payload.shopify_customer_id = customerId;
        } else {
          payload.email = email;
        }

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.classList.add('loading');
          submitButton.setAttribute('aria-busy', 'true');
        }

        try {
          const response = await fetch('/apps/back-in-stock', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
          });
          const responsePayload = await response.json().catch(() => null);

          if (!response.ok || responsePayload?.status !== 'success') {
            throw new Error('back-in-stock-alert-failed');
          }

          setBackInStockSuccess(modal, alert.dataset.successMessage);
          publishBackInStockSignupEvent(alert, email);
          form.reset();
        } catch (error) {
          setBackInStockMessage(modal, 'error', alert.dataset.errorMessage);
        } finally {
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-busy');
          }
        }
      });
    }

    syncBackInStockAlert(alert);
  };

  const bindAllBackInStockAlerts = (root = document) => {
    root.querySelectorAll('[data-back-in-stock-alert]').forEach(bindBackInStockAlert);
  };

  let scheduleChatControlAlignment = () => {};

  const bindFloatingControlsToStickyCart = () => {
    const stickyCart = document.querySelector('.product-add-to-cart-sticky, .tec-pack__mobile-buy');
    const footer = document.getElementById('footer');
    const floatingControlsBoundary = document.querySelector('.shopify-section-group-footer-group.section-newsletter') || footer;
    let floatingControlsBoundaryIsVisible = false;

    if (!stickyCart || stickyCart.dataset.aeternumFloatingControlsBound === 'true') {
      return;
    }

    stickyCart.dataset.aeternumFloatingControlsBound = 'true';

    let syncFrame = null;

    const syncFloatingControls = () => {
      syncFrame = null;
      const stickyCartRect = stickyCart.getBoundingClientRect();
      const stickyCartStyle = window.getComputedStyle(stickyCart);
      const stickyCartIsOnScreen = stickyCartRect.width > 0 &&
        stickyCartRect.height > 0 &&
        stickyCartRect.left < window.innerWidth &&
        stickyCartRect.right > 0 &&
        stickyCartRect.top < window.innerHeight &&
        stickyCartRect.bottom > 0 &&
        stickyCartStyle.display !== 'none' &&
        stickyCartStyle.visibility !== 'hidden' &&
        stickyCartStyle.opacity !== '0';
      const isPackCart = stickyCart.matches('.tec-pack__mobile-buy');
      const isVisible = stickyCartIsOnScreen && (isPackCart || (stickyCart.classList.contains('sticky--visible') && !floatingControlsBoundaryIsVisible));
      document.body.classList.toggle('aeternum-sticky-cart-visible', isVisible);

      if (!isVisible) {
        document.documentElement.style.removeProperty('--aeternum-sticky-cart-height');
        document.documentElement.style.removeProperty('--aeternum-sticky-cart-width');
        document.documentElement.style.removeProperty('--aeternum-floating-controls-bottom');
        scheduleChatControlAlignment();
        return;
      }

      document.documentElement.style.setProperty('--aeternum-sticky-cart-height', `${stickyCartRect.height}px`);
      document.documentElement.style.setProperty('--aeternum-sticky-cart-width', `${stickyCartRect.width}px`);
      document.documentElement.style.setProperty('--aeternum-floating-controls-bottom', `${Math.max(0, window.innerHeight - stickyCartRect.top) + 12}px`);
      scheduleChatControlAlignment();
    };

    const scheduleFloatingControlsSync = () => {
      if (syncFrame !== null) {
        return;
      }

      syncFrame = window.requestAnimationFrame(syncFloatingControls);
    };

    const classObserver = new MutationObserver(syncFloatingControls);
    classObserver.observe(stickyCart, {
      attributes: true,
      attributeFilter: ['class']
    });
    const packResult = stickyCart.closest('[data-pack-result]');
    if (packResult) classObserver.observe(packResult, { attributes: true, attributeFilter: ['hidden'] });

    if ('ResizeObserver' in window) {
      const sizeObserver = new ResizeObserver(syncFloatingControls);
      sizeObserver.observe(stickyCart);
    }

    if (floatingControlsBoundary && 'IntersectionObserver' in window) {
      const boundaryObserver = new IntersectionObserver((entries) => {
        floatingControlsBoundaryIsVisible = entries.some((entry) => entry.isIntersecting);
        syncFloatingControls();
      });
      boundaryObserver.observe(floatingControlsBoundary);
    }

    stickyCart.addEventListener('transitionend', scheduleFloatingControlsSync);
    window.addEventListener('scroll', scheduleFloatingControlsSync, { passive: true });
    window.addEventListener('resize', scheduleFloatingControlsSync, { passive: true });
    window.visualViewport?.addEventListener('resize', scheduleFloatingControlsSync, { passive: true });

    syncFloatingControls();
  };

  const stabilizeShopifyChatActivator = () => {
    if (!window.customElements) {
      return;
    }

    window.customElements.whenDefined('shopify-chat').then(() => {
      const chat = document.querySelector('shopify-chat');
      const chatRoot = chat && chat.shadowRoot;

      if (!chatRoot) {
        return;
      }

      let alignmentFrame = null;
      let observedActivatorRoot = null;
      let activatorObserver = null;

      const getActivatorRoot = () => {
        const runtime = chatRoot.querySelector('shopify-agent-runtime');
        return runtime?.shadowRoot || chatRoot;
      };

      const alignBackToChat = () => {
        alignmentFrame = null;

        const activatorRoot = getActivatorRoot();
        const activator = activatorRoot.querySelector('.activator');
        const activatorNudge = activatorRoot.querySelector('.activator-nudge');

        if (window.innerWidth > 767) {
          activatorNudge?.style.removeProperty('bottom');
          document.documentElement.style.removeProperty('--aeternum-chat-activator-bottom');
          return;
        }

        if (!activator || !activatorNudge) {
          return;
        }

        // Position the actual chat launcher; newer Inbox runtimes ignore the old offset variable.
        const clearance = document.documentElement.style.getPropertyValue('--aeternum-floating-controls-bottom');
        if (clearance) activatorNudge.style.setProperty('bottom', clearance, 'important');
        else activatorNudge.style.removeProperty('bottom');

        const activatorRect = activator.getBoundingClientRect();
        const activatorNudgeBottom = Number.parseFloat(window.getComputedStyle(activatorNudge).bottom);

        if (activatorRect.width <= 0 || activatorRect.height <= 0) {
          return;
        }

        document.documentElement.style.setProperty(
          '--aeternum-chat-activator-bottom',
          `${Math.max(0, Number.isFinite(activatorNudgeBottom) ? activatorNudgeBottom : window.innerHeight - activatorRect.bottom)}px`
        );
      };

      scheduleChatControlAlignment = () => {
        if (alignmentFrame !== null) {
          return;
        }

        alignmentFrame = window.requestAnimationFrame(() => {
          alignmentFrame = window.requestAnimationFrame(alignBackToChat);
        });
      };

      const stabilizeActivator = () => {
        const activatorRoot = getActivatorRoot();

        if (activatorRoot !== observedActivatorRoot) {
          activatorObserver?.disconnect();
          observedActivatorRoot = activatorRoot;
          activatorObserver = new MutationObserver(stabilizeActivator);
          activatorObserver.observe(activatorRoot, {
            childList: true,
            subtree: true,
            characterData: true
          });
        }

        const activator = activatorRoot.querySelector('.activator');
        const activatorNudge = activatorRoot.querySelector('.activator-nudge');
        const activatorLabel = activatorRoot.querySelector('.activator__label');
        const localizedLabel = window.__SHOP_CONTEXT__?.chatLabel?.trim();

        if (localizedLabel && activatorLabel?.textContent.trim() !== localizedLabel) {
          activatorLabel.textContent = localizedLabel;
        }

        if (localizedLabel && activator?.getAttribute('aria-label') !== localizedLabel) {
          activator.setAttribute('aria-label', localizedLabel);
        }

        if (activator && activator.dataset.aeternumChatBound !== 'true') {
          activator.dataset.aeternumChatBound = 'true';
          activator.addEventListener('click', () => window.setTimeout(stabilizeActivator, 500));
        }

        if (activatorNudge) {
          activatorNudge.style.removeProperty('animation');
          activatorNudge.style.removeProperty('translate');
          activatorNudge.classList.add('activator-nudge--active');
        }

        if (activatorLabel?.textContent.trim()) {
          chat.classList.add('aeternum-chat-has-label');
        }

        scheduleChatControlAlignment();
      };

      const chatObserver = new MutationObserver(stabilizeActivator);
      chatObserver.observe(chatRoot, {
        childList: true,
        subtree: true,
        characterData: true
      });

      window.addEventListener('scroll', scheduleChatControlAlignment, { passive: true });
      window.addEventListener('resize', scheduleChatControlAlignment, { passive: true });

      stabilizeActivator();
    });
  };

  document.addEventListener('product:variant-change', (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const variantTitle = event.detail && event.detail.variantTitle;

    if (sectionId && variantTitle) {
      document.querySelectorAll(`[data-back-in-stock-alert][data-section-id="${sectionId}"]`).forEach((alert) => {
        alert.dataset.variantTitle = variantTitle;
        const modal = getBackInStockModal(alert);
        modal?.querySelectorAll('[data-back-in-stock-variant-title]').forEach((variantTitleElement) => {
          variantTitleElement.textContent = variantTitle;
        });
      });
    }

    window.setTimeout(() => syncBackInStockAlerts(sectionId), 0);
  });

  const revealProductInformationTab = (tab) => {
    if (!tab || !window.matchMedia('(max-width: 767px)').matches) {
      return;
    }

    window.requestAnimationFrame(() => {
      tab.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    });
  };

  const bindProductInformationTabScroller = (root = document) => {
    const tabLists = [];
    if (root instanceof HTMLElement && root.matches('.product-information-drawer .side-panel-content--tabs')) {
      tabLists.push(root);
    }
    root.querySelectorAll?.('.product-information-drawer .side-panel-content--tabs').forEach((tabs) => tabLists.push(tabs));

    tabLists.forEach((tabs) => {
      if (tabs.dataset.aeternumScrollerBound === 'true') {
        return;
      }
      tabs.dataset.aeternumScrollerBound = 'true';
      tabs.addEventListener('click', (event) => {
        revealProductInformationTab(event.target.closest('button'));
      });
    });
  };

  const bindPowderUsageShortcuts = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-powder-usage-shortcut]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-powder-usage-shortcut]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.powderUsageBound === 'true') {
        return;
      }
      trigger.dataset.powderUsageBound = 'true';
      trigger.addEventListener('click', () => {
        const tab = document.querySelector('[data-powder-usage-tab]');
        const tabbedContent = tab?.closest('tabbed-content');
        if (!tab || !tabbedContent) {
          return;
        }

        const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
        const tabIndex = buttons.indexOf(tab);
        if (tabIndex < 0) {
          return;
        }

        tabbedContent.selectedIndex = tabIndex;
        revealProductInformationTab(tab);
        window.requestAnimationFrame(() => {
          tabbedContent.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    });
  };

  const bindOilUsageShortcuts = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-oil-usage-shortcut]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-oil-usage-shortcut]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.oilUsageBound === 'true') {
        return;
      }
      trigger.dataset.oilUsageBound = 'true';
      trigger.addEventListener('click', () => {
        const tab = document.querySelector('[data-oil-usage-tab]');
        const tabbedContent = tab?.closest('tabbed-content');
        if (!tab || !tabbedContent) {
          return;
        }

        const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
        const tabIndex = buttons.indexOf(tab);
        if (tabIndex < 0) {
          return;
        }

        tabbedContent.selectedIndex = tabIndex;
        revealProductInformationTab(tab);
        window.requestAnimationFrame(() => {
          tabbedContent.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    });
  };

  const bindCandleUsageShortcuts = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-candle-usage-shortcut]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-candle-usage-shortcut]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.candleUsageBound === 'true') return;
      trigger.dataset.candleUsageBound = 'true';
      trigger.addEventListener('click', () => {
        const tab = document.querySelector('[data-candle-usage-tab]');
        const tabbedContent = tab?.closest('tabbed-content');
        if (!tab || !tabbedContent) return;

        const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
        const tabIndex = buttons.indexOf(tab);
        if (tabIndex < 0) return;

        tabbedContent.selectedIndex = tabIndex;
        revealProductInformationTab(tab);
        window.requestAnimationFrame(() => {
          tabbedContent.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    });
  };

  const bindSaltUsageShortcuts = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-salt-usage-shortcut]')) triggers.push(root);
    root.querySelectorAll?.('[data-salt-usage-shortcut]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.saltUsageBound === 'true') return;
      trigger.dataset.saltUsageBound = 'true';
      trigger.addEventListener('click', () => {
        const tab = document.querySelector('[data-salt-usage-tab]');
        const tabbedContent = tab?.closest('tabbed-content');
        if (!tab || !tabbedContent) return;

        const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
        const tabIndex = buttons.indexOf(tab);
        if (tabIndex < 0) return;

        tabbedContent.selectedIndex = tabIndex;
        revealProductInformationTab(tab);
        window.requestAnimationFrame(() => {
          tabbedContent.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    });
  };

  const bindMineralQualityTriggers = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-mineral-quality-trigger]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-mineral-quality-trigger]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.mineralQualityBound === 'true') {
        return;
      }
      trigger.dataset.mineralQualityBound = 'true';
      trigger.addEventListener('click', () => {
        const drawer = document.getElementById('Product-Information-Drawer');
        const tabs = drawer?.querySelector('side-panel-content-tabs');
        const qualityTab = tabs?.querySelector('[data-mineral-quality-tab]');
        if (!drawer || !tabs || !qualityTab || typeof tabs.toggleActiveClass !== 'function') {
          return;
        }
        const tabButtons = Array.from(tabs.querySelectorAll('button'));
        const qualityIndex = tabButtons.indexOf(qualityTab);
        if (qualityIndex < 0) {
          return;
        }
        tabs.toggleActiveClass(qualityIndex);
        OverlayManager.open(drawer);
        revealProductInformationTab(qualityTab);
      });
    });
  };

  const bindLoyaltyTriggers = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-loyalty-trigger]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-loyalty-trigger]').forEach((trigger) => triggers.push(trigger));

    triggers.forEach((trigger) => {
      if (trigger.dataset.loyaltyBound === 'true') {
        return;
      }
      trigger.dataset.loyaltyBound = 'true';
      trigger.addEventListener('click', () => {
        const drawer = document.getElementById('Product-Information-Drawer');
        const tabs = drawer?.querySelector('side-panel-content-tabs');
        const loyaltyTab = tabs?.querySelector('[data-loyalty-tab]');
        if (!drawer || !tabs || !loyaltyTab || typeof tabs.toggleActiveClass !== 'function') {
          return;
        }
        const tabButtons = Array.from(tabs.querySelectorAll('button'));
        const loyaltyIndex = tabButtons.indexOf(loyaltyTab);
        if (loyaltyIndex < 0) {
          return;
        }
        tabs.toggleActiveClass(loyaltyIndex);
        OverlayManager.open(drawer);
        revealProductInformationTab(loyaltyTab);
      });
    });
  };

  const replaceCercleCurrency = (root, replacements) => {
    if (!root || replacements.length === 0) {
      return;
    }

    const sortedReplacements = [...replacements]
      .filter(({ source, target }) => source && target && source !== target)
      .sort((a, b) => b.source.length - a.source.length);

    if (sortedReplacements.length === 0) {
      return;
    }

    const replaceText = (text) => sortedReplacements.reduce((value, { source, target }) => {
      const escapedSource = source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const expression = new RegExp(`(^|[^\\p{L}\\p{N}])(${escapedSource})(?=$|[^\\p{L}\\p{N}])`, 'giu');

      return value.replace(expression, (match, prefix, matchedTerm) => {
        const replacement = matchedTerm.charAt(0) === matchedTerm.charAt(0).toUpperCase()
          ? target.charAt(0).toUpperCase() + target.slice(1)
          : target;

        return `${prefix}${replacement}`;
      });
    }, text);

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, [data-tec-cercle-controller]')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const textNodes = [];
    let currentNode;

    while ((currentNode = walker.nextNode())) {
      textNodes.push(currentNode);
    }

    textNodes.forEach((node) => {
      const updatedText = replaceText(node.nodeValue || '');
      if (updatedText !== node.nodeValue) {
        node.nodeValue = updatedText;
      }
    });

    root.querySelectorAll('[aria-label], [title]').forEach((element) => {
      ['aria-label', 'title'].forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) return;
        const updatedValue = replaceText(value);
        if (updatedValue !== value) {
          element.setAttribute(attribute, updatedValue);
        }
      });
    });
  };

  const syncCercleBalance = (cardWrapper, controller) => {
    if (!cardWrapper || !controller) {
      return;
    }

    let authenticated = controller.dataset.customerAuthenticated === 'true';
    let points = controller.dataset.customerPoints;

    try {
      const customerStore = window.Alpine?.store?.('customer');
      if (customerStore) {
        const storeAuthenticated = typeof customerStore.authenticated === 'function'
          ? customerStore.authenticated()
          : customerStore.payload !== null;
        authenticated = authenticated || storeAuthenticated;

        const currentPoints = typeof customerStore.points === 'function'
          ? customerStore.points()
          : customerStore.payload?.points;
        if (storeAuthenticated && currentPoints !== null && currentPoints !== undefined && currentPoints !== '') {
          points = currentPoints;
        }
      }
    } catch (error) {
      // Alpine may not have initialized the Essential Loyalty store yet.
    }

    let balance = cardWrapper.querySelector('[data-tec-cercle-balance]');
    if (!authenticated || points === null || points === undefined || points === '') {
      balance?.remove();
      cardWrapper.classList.remove('tec-cercle-hero__access--connected');
      return;
    }

    if (!balance) {
      balance = document.createElement('p');
      balance.dataset.tecCercleBalance = 'true';
      balance.className = 'tec-cercle-balance';
      balance.setAttribute('aria-live', 'polite');
    }

    const numericPoints = Number(points);
    const formattedPoints = Number.isFinite(numericPoints)
      ? new Intl.NumberFormat(document.documentElement.lang || 'fr').format(numericPoints)
      : String(points);
    let label = balance.querySelector('span');
    let value = balance.querySelector('strong');
    if (!label || !value) {
      label = document.createElement('span');
      value = document.createElement('strong');
      balance.replaceChildren(label, value);
    }

    const labelText = controller.dataset.balanceLabel || '';
    if (label.textContent !== labelText) {
      label.textContent = labelText;
    }
    if (value.textContent !== formattedPoints) {
      value.textContent = formattedPoints;
    }

    const buttonGroup = cardWrapper.querySelector('.essential-hero-button-group');
    if (buttonGroup && balance.nextElementSibling !== buttonGroup) {
      buttonGroup.before(balance);
    } else if (!buttonGroup && !balance.isConnected) {
      cardWrapper.append(balance);
    }

    cardWrapper.classList.add('tec-cercle-hero__access--connected');
  };

  const setCercleText = (element, value) => {
    const text = value?.trim();
    if (element && text && element.textContent !== text) {
      element.textContent = text;
    }
  };

  const applyCercleEditorContent = (page, controller) => {
    const access = page.querySelector('.tec-cercle-hero__access');
    const accessButtons = access?.querySelectorAll('.essential-button') || [];

    setCercleText(access?.querySelector('.essential-heading'), controller.dataset.accessHeading);
    setCercleText(access?.querySelector('.essential-text'), controller.dataset.accessText);
    setCercleText(accessButtons[0], controller.dataset.signupLabel);
    setCercleText(accessButtons[1], controller.dataset.loginLabel);

    const howItWorks = page.querySelector('.essential-hiw-explainer');
    const steps = howItWorks?.querySelectorAll('.essential-hiw-step') || [];
    const stepContent = [
      [controller.dataset.stepOneHeading, controller.dataset.stepOneText],
      [controller.dataset.stepTwoHeading, controller.dataset.stepTwoText],
      [controller.dataset.stepThreeHeading, controller.dataset.stepThreeText]
    ];

    setCercleText(howItWorks?.querySelector('.essential-heading'), controller.dataset.howHeading);
    stepContent.forEach(([heading, text], index) => {
      setCercleText(steps[index]?.querySelector('.essential-hiw-step-heading'), heading);
      setCercleText(steps[index]?.querySelector('.essential-hiw-step-text'), text);
    });

    const earn = page.querySelector('.essential-ways-to-earn-wrapper');
    setCercleText(earn?.querySelector('.essential-heading'), controller.dataset.earnHeading);
    setCercleText(earn?.querySelector('.essential-text'), controller.dataset.earnIntro);

    const redeem = page.querySelector('.essential-ways-to-redeem-wrapper');
    setCercleText(redeem?.querySelector('.essential-heading'), controller.dataset.redeemHeading);
    setCercleText(redeem?.querySelector('.essential-text'), controller.dataset.redeemIntro);
  };

  const decodeCercleRewards = (page) => {
    const rewardsRoot = page.querySelector('.essential-wtr-rewards[x-data]');
    const encodedRewards = rewardsRoot?.getAttribute('x-data')?.match(/rewards:\s*'([^']+)'/)?.[1];
    if (!encodedRewards) {
      return [];
    }

    try {
      const binary = window.atob(encodedRewards);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (error) {
      return [];
    }
  };

  const getCercleRewardAmount = (text) => {
    const match = String(text || '').replace(/\u00a0/g, ' ').match(/(\d+(?:[.,]\d+)?)\s*€/);
    return match ? Number(match[1].replace(',', '.')) : null;
  };

  const formatCercleMinimumAmount = (value) => {
    const numericValue = Number(String(value || '').replace(/[^\d.,-]/g, '').replace(',', '.'));
    if (!Number.isFinite(numericValue)) {
      return String(value || '').trim();
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
        maximumFractionDigits: 2
      }).format(numericValue);
    } catch (error) {
      return `${numericValue} €`;
    }
  };

  const syncCercleRewardConditions = (page, controller) => {
    const rewardMinimumLabel = controller.dataset.rewardMinimumLabel?.trim();
    if (!rewardMinimumLabel) {
      return;
    }

    const rewardsByAmount = new Map(
      decodeCercleRewards(page)
        .filter((reward) => reward?.constraint?.value)
        .map((reward) => [Number(reward.value), reward.constraint.value])
    );

    page.querySelectorAll('.essential-card-reward, .essential-hiw-rewards .essential-card').forEach((card) => {
      const content = card.querySelector('.essential-card-content');
      const rewardAmount = getCercleRewardAmount(card.querySelector('.essential-card-title')?.textContent);
      const minimumValue = rewardsByAmount.get(rewardAmount);
      if (!content || !minimumValue) {
        return;
      }

      const conditionText = rewardMinimumLabel.replace('{amount}', formatCercleMinimumAmount(minimumValue));
      let condition = content.querySelector('.tec-cercle-reward-condition');
      if (!condition) {
        condition = document.createElement('span');
        condition.className = 'tec-cercle-reward-condition';
        content.append(condition);
      }
      setCercleText(condition, conditionText);
    });

    page.querySelectorAll('.essential-hiw-rewards .essential-card-variant').forEach((variant) => {
      const isEmptyVariant = /^[\s\u00a0–—-]*$/.test(variant.textContent || '');
      variant.classList.toggle('tec-cercle-empty-variant', isEmptyVariant);
      if (variant.hidden !== isEmptyVariant) {
        variant.hidden = isEmptyVariant;
      }
    });
  };

  const bindCercleModalTriggers = (page) => {
    page.querySelectorAll('.essential-card-action, .essential-card-reward, .essential-hiw-rewards .essential-card').forEach((card) => {
      if (card.dataset.tecCercleModalBound === 'true') {
        return;
      }

      card.dataset.tecCercleModalBound = 'true';
      card.addEventListener('click', () => {
        const isRewardCard = card.classList.contains('essential-card-reward')
          || Boolean(card.closest('.essential-hiw-rewards'));
        if (isRewardCard) {
          const rewards = Array.from(page.querySelectorAll('.essential-card-reward'));
          const rewardAmount = getCercleRewardAmount(card.querySelector('.essential-card-title')?.textContent);
          const rewardTiers = new Map([[5, 1], [20, 2], [30, 3], [50, 4]]);
          page.dataset.tecCercleModalType = 'reward';
          page.dataset.tecCercleModalTier = String(
            rewardTiers.get(rewardAmount) || Math.max(1, rewards.indexOf(card) + 1)
          );
        } else {
          page.dataset.tecCercleModalType = 'mission';
          delete page.dataset.tecCercleModalTier;
        }
      });
    });
  };

  const decorateCercleModal = (page) => {
    const overlay = document.querySelector('.essential-modal-overlay');
    if (!overlay) {
      return;
    }

    overlay.classList.remove(
      'tec-cercle-modal--mission',
      'tec-cercle-modal--reward',
      'tec-cercle-modal--reward-tier-1',
      'tec-cercle-modal--reward-tier-2',
      'tec-cercle-modal--reward-tier-3',
      'tec-cercle-modal--reward-tier-4'
    );
    overlay.querySelectorAll('.tec-cercle-modal-gain').forEach((element) => {
      element.classList.remove('tec-cercle-modal-gain');
    });
    overlay.querySelectorAll('.tec-cercle-hidden-app-description').forEach((element) => {
      element.classList.remove('tec-cercle-hidden-app-description');
    });

    const icon = overlay.querySelector('.essential-modal-icon img');
    const isReward = icon?.getAttribute('alt') === 'reward image'
      || page.dataset.tecCercleModalType === 'reward';
    const modalType = isReward ? 'reward' : 'mission';
    overlay.classList.add(`tec-cercle-modal--${modalType}`);

    if (isReward) {
      const tier = Math.min(4, Math.max(1, Number(page.dataset.tecCercleModalTier) || 1));
      overlay.classList.add(`tec-cercle-modal--reward-tier-${tier}`);
    }

    const modalContent = overlay.querySelector('.essential-modal-content');
    const gain = Array.from(modalContent?.children || [])
      .find((element) => element.classList.contains('essential-modal-text'));
    const isEmptyRewardDescription = isReward
      && /^[\s\u00a0–—-]*$/.test(gain?.textContent || '');
    if (isEmptyRewardDescription) {
      gain?.classList.add('tec-cercle-hidden-app-description');
    } else {
      gain?.classList.add('tec-cercle-modal-gain');
    }

    if (isReward) {
      overlay.querySelectorAll('.essential-card-description, [x-text="discount.description"]')
        .forEach((element) => element.classList.add('tec-cercle-hidden-app-description'));
    }
  };

  const bindCercleAeternum = () => {
    const controller = document.querySelector('[data-tec-cercle-controller]');
    if (!controller) {
      return false;
    }

    const page = controller.closest('#essential-loyalty-landing-page');
    const hero = page?.querySelector('.essential-hero');
    if (!page || !hero) {
      return false;
    }

    page.classList.add('tec-cercle-page');
    document.body.classList.add('tec-cercle-modal-theme');

    if (hero.dataset.tecCercleEnhanced !== 'true') {
      hero.dataset.tecCercleEnhanced = 'true';
      hero.classList.add('tec-cercle-hero');

      const heroContent = document.createElement('div');
      heroContent.className = 'tec-cercle-hero__content';

      const mark = document.createElement('span');
      mark.className = 'tec-cercle-hero__mark';
      mark.setAttribute('aria-hidden', 'true');

      const eyebrow = document.createElement('p');
      eyebrow.className = 'tec-cercle-hero__eyebrow';
      eyebrow.textContent = controller.dataset.eyebrow || '';

      const title = document.createElement('h1');
      title.className = 'tec-cercle-hero__title';
      title.textContent = controller.dataset.title || '';

      const intro = document.createElement('p');
      intro.className = 'tec-cercle-hero__intro';
      intro.textContent = controller.dataset.intro || '';

      heroContent.append(mark, eyebrow, title, intro);

      const cardWrapper = hero.querySelector('.essential-hero-card-wrapper');
      hero.insertBefore(heroContent, cardWrapper || hero.firstChild);

      if (cardWrapper) {
        cardWrapper.classList.add('tec-cercle-hero__access');
      }
    }

    const rewards = page.querySelector('.essential-ways-to-redeem');
    if (rewards && !rewards.id) {
      rewards.id = 'cercle-recompenses';
    }

    const replacements = [
      { source: controller.dataset.pointSourceMany, target: controller.dataset.aeterMany },
      { source: controller.dataset.pointSourceOne, target: controller.dataset.aeterOne }
    ];

    applyCercleEditorContent(page, controller);
    syncCercleRewardConditions(page, controller);
    bindCercleModalTriggers(page);
    decorateCercleModal(page);
    replaceCercleCurrency(document.querySelector('.essential-modal-overlay'), replacements);
    replaceCercleCurrency(page, replacements);
    syncCercleBalance(page.querySelector('.tec-cercle-hero__access'), controller);

    if (page.dataset.tecCercleObserved === 'true') {
      return true;
    }

    page.dataset.tecCercleObserved = 'true';
    let currencyFrame = null;
    const currencyObserver = new MutationObserver(() => {
      if (currencyFrame !== null) {
        return;
      }
      currencyFrame = window.requestAnimationFrame(() => {
        currencyFrame = null;
        applyCercleEditorContent(page, controller);
        syncCercleRewardConditions(page, controller);
        bindCercleModalTriggers(page);
        decorateCercleModal(page);
        replaceCercleCurrency(document.querySelector('.essential-modal-overlay'), replacements);
        replaceCercleCurrency(page, replacements);
        syncCercleBalance(page.querySelector('.tec-cercle-hero__access'), controller);
      });
    });

    currencyObserver.observe(document.body, {
      childList: true,
      characterData: true,
      subtree: true
    });

    return true;
  };

  const bootCercleAeternum = () => {
    if (!document.documentElement.classList.contains('tec-cercle-booting')
      && !document.querySelector('[data-tec-cercle-controller]')) return;

    const maxAttempts = 120;
    let attempts = 0;

    const attemptBinding = () => {
      if (bindCercleAeternum()) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            document.documentElement.classList.remove('tec-cercle-booting');
          });
        });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(attemptBinding, 50);
        return;
      }

      document.documentElement.classList.remove('tec-cercle-booting');
    };

    attemptBinding();
  };

  const bindIntegratedProductCart = (root = document) => {
    const controls = [];

    if (root instanceof Element && root.matches('[data-aeternum-integrated-cart]')) {
      controls.push(root);
    }

    if (root.querySelectorAll) {
      controls.push(...root.querySelectorAll('[data-aeternum-integrated-cart]'));
    }

    controls.forEach((control) => {
      if (control.dataset.aeternumCartBound === 'true') {
        return;
      }

      const productForm = control.closest('product-form');
      const form = control.closest('form');
      const submitButton = control.querySelector('.single-add-to-cart-button');
      const quantityControl = control.querySelector('[data-aeternum-cart-quantity]');
      const label = control.querySelector('[data-aeternum-cart-label]');
      const minusButton = control.querySelector('[data-aeternum-cart-minus]');
      const plusButton = control.querySelector('[data-aeternum-cart-plus]');

      if (!productForm || !form || !submitButton || !quantityControl || !label || !minusButton || !plusButton) {
        return;
      }

      control.dataset.aeternumCartBound = 'true';

      let quantity = 0;
      let lineKey = '';
      let busy = false;

      const getVariantId = () => Number(form.querySelector('[name="id"]')?.value || 0);
      const getSourceInput = () => document.getElementById(`Quantity-${productForm.dataset.section}`);
      const getStep = () => Math.max(1, Number(getSourceInput()?.step || 1));
      const getMaximum = () => {
        const maximum = Number(getSourceInput()?.max || 0);
        return maximum > 0 ? maximum : null;
      };
      const formatLabel = (count) => {
        const template = count === 1 ? control.dataset.addedOne : control.dataset.addedOther;
        return (template || '').replace('__COUNT__', count);
      };

      const render = () => {
        const hasQuantity = quantity > 0;
        const maximum = getMaximum();

        submitButton.hidden = hasQuantity;
        quantityControl.hidden = !hasQuantity;
        label.textContent = hasQuantity ? formatLabel(quantity) : '';
        minusButton.disabled = busy;
        plusButton.disabled = busy || submitButton.disabled || (maximum !== null && quantity + getStep() > maximum);
        quantityControl.setAttribute('aria-busy', busy ? 'true' : 'false');
      };

      const showError = (message) => {
        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message || false);
        }
      };

      const applyCart = (cart) => {
        const variantId = getVariantId();
        const matchingItems = Array.isArray(cart?.items)
          ? cart.items.filter((item) => Number(item.variant_id) === variantId)
          : [];

        quantity = matchingItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
        lineKey = matchingItems[0]?.key || '';
        busy = false;
        render();
      };

      const refresh = () => {
        fetch(control.dataset.cartUrl, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
            return response.json();
          })
          .then(applyCart)
          .catch((error) => {
            busy = false;
            render();
            console.error(error);
          });
      };

      plusButton.addEventListener('click', () => {
        if (busy || submitButton.disabled) return;

        const sourceInput = getSourceInput();
        if (sourceInput) {
          sourceInput.value = getStep();
        }

        busy = true;
        render();

        const formData = new FormData(form);
        formData.set('quantity', getStep());

        fetch(control.dataset.addUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: formData
        })
          .then((response) => response.json().then((item) => ({ response, item })))
          .then(({ response, item }) => {
            if (!response.ok || item.status) {
              throw new Error(item.description || item.message || `Cart request failed (${response.status})`);
            }

            showError(false);
            document.dispatchEvent(new CustomEvent('cart:item-added', {
              detail: { product: item }
            }));
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .catch((error) => {
            busy = false;
            render();
            showError(error.message);
          });
      });

      minusButton.addEventListener('click', () => {
        if (busy || !lineKey || quantity <= 0) return;

        busy = true;
        render();

        fetch(control.dataset.changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            id: lineKey,
            quantity: Math.max(0, quantity - getStep())
          })
        })
          .then((response) => response.json().then((cart) => ({ response, cart })))
          .then(({ response, cart }) => {
            if (!response.ok || cart.status) {
              throw new Error(cart.description || cart.message || `Cart update failed (${response.status})`);
            }

            showError(false);
            applyCart(cart);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            document.dispatchEvent(new CustomEvent('line-item:change:end', {
              detail: { quantity, cart }
            }));
          })
          .catch((error) => {
            busy = false;
            render();
            showError(error.message);
          });
      });

      document.addEventListener('cart:item-added', refresh);
      document.addEventListener('line-item:change:end', (event) => {
        if (event.detail?.cart) {
          applyCart(event.detail.cart);
        } else {
          refresh();
        }
      });
      document.addEventListener('product:variant-error', () => {
        busy = false;
        render();
      });
      document.addEventListener('product:variant-change', (event) => {
        if (String(event.detail?.sectionId) === String(productForm.dataset.section)) {
          window.setTimeout(refresh, 0);
        }
      });

      render();
      refresh();
    });
  };

  const prepareKlarnaBenefitSlide = (slider) => {
    if (!slider.hasAttribute('data-aeternum-integrate-klarna')) return true;
    if (slider.classList.contains('aeternum-product-benefits--with-klarna')) return true;

    const productInformation = slider.closest('.product-information--inner');
    const placeholder = slider.querySelector('[data-aeternum-klarna-slide]');
    const klarnaControl = slider.querySelector('[data-aeternum-klarna-control]');
    const klarnaPlacement = productInformation?.querySelector('klarna-placement');
    const klarnaBlock = klarnaPlacement?.closest('.shopify-app-block');

    if (!placeholder || !klarnaControl || !klarnaPlacement || !klarnaBlock) return false;

    placeholder.append(klarnaBlock);
    placeholder.hidden = false;
    placeholder.setAttribute('data-aeternum-benefit-slide', '');
    klarnaControl.hidden = false;
    klarnaControl.setAttribute('data-aeternum-benefit-control', '0');
    slider.classList.add('aeternum-product-benefits--with-klarna');
    return true;
  };

  const bindProductBenefitsSliders = (root = document) => {
    const sliders = [];

    if (root instanceof Element && root.matches('[data-aeternum-benefits-slider]')) {
      sliders.push(root);
    }

    if (root.querySelectorAll) {
      sliders.push(...root.querySelectorAll('[data-aeternum-benefits-slider]'));
    }

    sliders.forEach((slider) => {
      if (slider.dataset.aeternumBenefitsBound === 'true') return;

      if (!prepareKlarnaBenefitSlide(slider)) {
        const attempt = Number(slider.dataset.aeternumKlarnaAttempt || 0);
        if (attempt < 10) {
          slider.dataset.aeternumKlarnaAttempt = String(attempt + 1);
          window.setTimeout(() => bindProductBenefitsSliders(slider), 300);
          return;
        }
      }

      const slides = Array.from(slider.querySelectorAll('[data-aeternum-benefit-slide]'));
      const controls = Array.from(slider.querySelectorAll('[data-aeternum-benefit-control]'));

      if (slides.length < 2 || controls.length !== slides.length) return;

      slider.dataset.aeternumBenefitsBound = 'true';

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let currentIndex = 0;
      let timer = null;
      let paused = false;

      const schedule = () => {
        window.clearTimeout(timer);
        timer = null;

        if (paused || document.hidden || reducedMotion.matches || !slider.isConnected) return;

        timer = window.setTimeout(() => {
          selectBenefit(currentIndex + 1);
        }, 4500);
      };

      const selectBenefit = (index) => {
        currentIndex = (index + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          slide.hidden = slideIndex !== currentIndex;
        });

        controls.forEach((control, controlIndex) => {
          control.setAttribute('aria-pressed', controlIndex === currentIndex ? 'true' : 'false');
        });

        schedule();
      };

      controls.forEach((control, controlIndex) => {
        control.addEventListener('click', () => selectBenefit(controlIndex));
      });

      slider.addEventListener('pointerenter', () => {
        paused = true;
        schedule();
      });

      slider.addEventListener('pointerleave', () => {
        paused = false;
        schedule();
      });

      slider.addEventListener('focusin', () => {
        paused = true;
        schedule();
      });

      slider.addEventListener('focusout', () => {
        window.setTimeout(() => {
          paused = slider.contains(document.activeElement);
          schedule();
        }, 0);
      });

      document.addEventListener('visibilitychange', schedule);
      reducedMotion.addEventListener?.('change', schedule);
      selectBenefit(0);
    });
  };

  const productCardReviewMobile = window.matchMedia('(max-width: 767px)');

  const syncProductCardReviewRating = (widget) => {
    const productCard = widget.closest('product-card');
    const featuredProduct = widget.closest('.featured-product');
    if (!productCard && !featuredProduct) return;

    const updateRating = () => {
      const average = Number.parseFloat(widget.dataset.reviewAvg);
      const reviewCount = Number.parseInt(widget.dataset.reviewCount, 10);
      const nativeCount = widget.querySelector('.AirReviews-StarRate--Count');
      if (!nativeCount || !Number.isFinite(average) || !Number.isFinite(reviewCount)) return;

      const averageText = Number.isInteger(average) ? average.toFixed(0) : average.toFixed(1);
      const ratingText = `${averageText}/5 (${reviewCount})`;
      nativeCount.classList.add('tec-product-card-review-average');
      if (featuredProduct) {
        nativeCount.style.setProperty('font-size', productCardReviewMobile.matches ? '12px' : '14px', 'important');
      } else if (productCard && productCardReviewMobile.matches) {
        nativeCount.style.setProperty('font-size', '12px', 'important');
      } else {
        nativeCount.style.removeProperty('font-size');
      }
      if (nativeCount.textContent?.trim() !== ratingText) nativeCount.textContent = ratingText;
    };

    updateRating();

    if (!widget.dataset.tecProductCardReviewBound) {
      widget.dataset.tecProductCardReviewBound = 'true';
      new MutationObserver(updateRating).observe(widget, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  };

  const bindProductCardReviewRatings = (root = document) => {
    const widgets = new Set();
    const selector = 'product-card .AirReviews-Widget--Stars, .featured-product .AirReviews-Widget--Stars[data-tec-featured-review]';

    if (root instanceof HTMLElement) {
      if (root.matches(selector)) widgets.add(root);
      const closestWidget = root.closest(selector);
      if (closestWidget) widgets.add(closestWidget);
    }

    root.querySelectorAll?.(selector).forEach((widget) => widgets.add(widget));
    widgets.forEach(syncProductCardReviewRating);
  };

  const syncProductReviewSummary = (title, reviewBlock) => {
    const bindReviewLink = (summary) => {
      const reviewLink = summary?.querySelector('.tec-product-review-summary__link');
      if (!reviewLink || reviewLink.dataset.tecReviewLinkBound) return;
      reviewLink.dataset.tecReviewLinkBound = 'true';
      reviewLink.addEventListener('click', (event) => {
        const target = document.getElementById('AirReviews-BlockWrapper')
          || document.querySelector('.AirReviews-Embed-BlockWrapper');
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
        window.history.replaceState(null, '', '#AirReviews-BlockWrapper');
      });
    };
    const initialSummary = reviewBlock.querySelector('[data-tec-initial-review-summary]');
    bindReviewLink(initialSummary);
    const widget = reviewBlock.querySelector('.AirReviews-Widget--Stars');
    const legacySummary = widget?.querySelector(':scope > .AirReviews-StarRatingWrapper');

    if (!widget || !legacySummary) return;

    let summary = widget.querySelector(':scope > .tec-product-review-summary')
      || initialSummary;
    if (summary?.hasAttribute('data-tec-initial-review-summary')) {
      summary.removeAttribute('data-tec-initial-review-summary');
      widget.prepend(summary);
    }
    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'tec-product-review-summary';

      const stars = document.createElement('span');
      stars.className = 'tec-product-review-summary__stars';

      const average = document.createElement('span');
      average.className = 'tec-product-review-summary__average';

      const divider = document.createElement('span');
      divider.className = 'tec-product-review-summary__divider';
      divider.setAttribute('aria-hidden', 'true');

      const reviewLink = document.createElement('a');
      reviewLink.className = 'tec-product-review-summary__link';
      reviewLink.href = '#AirReviews-BlockWrapper';

      summary.append(stars, average, divider, reviewLink);
      widget.prepend(summary);
    }

    bindReviewLink(summary);

    if (!legacySummary.hidden) legacySummary.hidden = true;
    if (legacySummary.style.getPropertyValue('display') !== 'none'
      || legacySummary.style.getPropertyPriority('display') !== 'important') {
      legacySummary.style.setProperty('display', 'none', 'important');
    }

    const updateSummary = () => {
      const ratingElement = legacySummary.querySelector('[role="img"][aria-label]');
      const ratingLabel = ratingElement?.getAttribute('aria-label') || '';
      const ratingTitle = legacySummary.querySelector('.AirReviews-StarRateProduct__Content.left > div:first-child')?.textContent || '';
      const ratingMatch = (ratingTitle.match(/\d+(?:[.,]\d+)?/) || ratingLabel.match(/\d+(?:[.,]\d+)?/));
      const ratingText = ratingMatch?.[0] || '0';
      const count = legacySummary.querySelector('.AirReviews-StarRate--Count')?.textContent?.trim() || '0';
      const countTemplate = title.dataset.tecReviewCountLabel || '__COUNT__';
      const stars = summary.querySelector('.tec-product-review-summary__stars');
      const nativeStars = legacySummary.querySelector('.AirReviews-StarRate');

      if (nativeStars) {
        const nativeStarsClone = nativeStars.cloneNode(true);
        nativeStarsClone.removeAttribute('tabindex');
        // Unchanged stars must not restart the observer that synchronizes this summary.
        if (stars.childNodes.length !== 1 || !stars.firstElementChild?.isEqualNode(nativeStarsClone)) {
          stars.replaceChildren(nativeStarsClone);
        }
      }
      const average = summary.querySelector('.tec-product-review-summary__average');
      const reviewLink = summary.querySelector('.tec-product-review-summary__link');
      const countText = countTemplate.replace('__COUNT__', count);
      if (average.textContent !== ratingText) average.textContent = ratingText;
      if (reviewLink.textContent !== countText) reviewLink.textContent = countText;
    };

    updateSummary();

    if (!legacySummary.dataset.tecReviewSummaryBound) {
      legacySummary.dataset.tecReviewSummaryBound = 'true';
      new MutationObserver(updateSummary).observe(legacySummary, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  };

  const syncProductSummary = (container) => {
    const title = container.querySelector(':scope > .product-title-container[data-tec-product-summary]');
    const reviewSlot = title?.querySelector(':scope > .tec-product-review-slot');
    const inventory = title?.querySelector(':scope > .tec-product-commerce-card .product-inventory-notice--wrapper')
      || container.querySelector(':scope > .product-inventory-notice--wrapper, :scope > .tec-product-commerce-card .product-inventory-notice--wrapper');

    if (!title) return;

    let card = title.querySelector(':scope > .tec-product-commerce-card')
      || container.querySelector(':scope > .tec-product-commerce-card');
    if (!card) {
      card = document.createElement('div');
      card.className = 'tec-product-commerce-card';
      card.dataset.tecProductCommerceCard = 'true';
      title.after(card);
    }

    let priceColumn = card.querySelector(':scope > .tec-product-commerce-card__price');
    if (!priceColumn) {
      priceColumn = document.createElement('div');
      priceColumn.className = 'tec-product-commerce-card__price';
      card.prepend(priceColumn);
    }

    const price = title.querySelector(':scope > .product-price-container');
    const installment = title.querySelector(':scope > form.installment');

    if (price) priceColumn.append(price);
    if (installment) priceColumn.append(installment);
    if (inventory && inventory.parentElement !== card) card.append(inventory);

    const reviewBlock = reviewSlot?.querySelector(':scope > .shopify-app-block')
      || Array.from(container.children).find((child) => {
        return child.matches('.shopify-app-block') && child.querySelector('.AirReviews-Widget--Stars');
      });

    if (reviewBlock) {
      reviewBlock.classList.add('tec-product-summary__reviews');
      if (reviewSlot && reviewBlock.parentElement !== reviewSlot) {
        reviewSlot.append(reviewBlock);
      } else if (!reviewSlot && reviewBlock.nextElementSibling !== card) {
        card.before(reviewBlock);
      }
      syncProductReviewSummary(title, reviewBlock);
    }

    const stockText = inventory?.querySelector('.product-inventory-notice--text');
    const hasStock = Boolean(stockText);
    card.classList.toggle('tec-product-commerce-card--price-only', !hasStock);

    if (!stockText) return;

    Array.from(stockText.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    });

    let stockCopy = stockText.querySelector(':scope > .tec-product-availability__copy');
    if (!stockCopy) {
      stockCopy = document.createElement('span');
      stockCopy.className = 'tec-product-availability__copy';

      const stockLabel = document.createElement('span');
      stockLabel.className = 'tec-product-availability__stock';
      stockLabel.textContent = title.dataset.tecStockLabel;

      const dispatchLabel = document.createElement('span');
      dispatchLabel.className = 'tec-product-availability__dispatch';
      dispatchLabel.textContent = title.dataset.tecDispatchLabel;

      stockCopy.append(stockLabel, dispatchLabel);
      stockText.append(stockCopy);
    }
  };

  const bindProductSummaries = (root = document) => {
    const containers = new Set();

    if (root instanceof HTMLElement) {
      if (root.matches('.product-information--inner')) containers.add(root);
      const closestContainer = root.closest('.product-information--inner');
      if (closestContainer) containers.add(closestContainer);
    }

    root.querySelectorAll?.('.product-information--inner').forEach((container) => containers.add(container));
    containers.forEach(syncProductSummary);
  };

  const bindProductReviewEmptyState = (root = document) => {
    const title = document.querySelector('.product-title-container[data-tec-product-summary]');
    const messageText = title?.dataset.tecReviewEmptyMessage?.trim();
    if (!messageText) return;

    const reviewWrappers = new Set();
    if (root instanceof HTMLElement) {
      if (root.matches('#AirReviews-BlockWrapper')) reviewWrappers.add(root);
      const closestWrapper = root.closest('#AirReviews-BlockWrapper');
      if (closestWrapper) reviewWrappers.add(closestWrapper);
    }
    root.querySelectorAll?.('#AirReviews-BlockWrapper').forEach((wrapper) => reviewWrappers.add(wrapper));

    reviewWrappers.forEach((wrapper) => {
      const content = wrapper.querySelector('.AirReviews-NoReview__Content');
      if (!content) return;

      let message = content.querySelector(':scope > .tec-product-review-empty-message');
      if (!message) {
        message = document.createElement('p');
        message.className = 'tec-product-review-empty-message';

        const nativeHeading = content.querySelector(':scope > .AirReviews-Text--bodySm');
        if (nativeHeading) {
          nativeHeading.after(message);
        } else {
          content.prepend(message);
        }
      }
      if (message.textContent !== messageText) message.textContent = messageText;
    });
  };

  const relatedArticleStopWords = new Set([
    'a', 'au', 'aux', 'avec', 'd', 'de', 'des', 'du', 'en', 'et', 'l', 'la', 'le', 'les',
    'par', 'pour', 'sans', 'sur', 'un', 'une'
  ]);

  const relatedArticleProductWords = new Set([
    'amulettes', 'arbre', 'baton', 'batons', 'boite', 'bougie', 'bougies', 'bracelet', 'bracelets',
    'brute', 'brutes', 'collier', 'colliers', 'cone', 'cones', 'eau', 'eaux', 'eclat', 'eclats',
    'encens', 'extra', 'face', 'faces', 'fiole', 'fioles', 'galet', 'galets', 'grand', 'grande',
    'huile', 'huiles', 'lot', 'lotion', 'lotions', 'lots', 'magique', 'magiques', 'mm', 'naturel',
    'naturelle', 'naturelles', 'naturels', 'pack', 'packs', 'pendentif', 'pendentifs', 'pendule',
    'pendules', 'petit', 'petite', 'pierre', 'pierres', 'pointe', 'pointes', 'poudre', 'poudres',
    'resine', 'resines', 'roule', 'roulee', 'roulees', 'roules', 'sachet', 'sachets', 'seche',
    'seches', 'sphere', 'spheres', 'veritable', 'veritables', 'vie'
  ]);

  const relatedArticleFamilyWords = new Set([
    'amulettes', 'bougie', 'bougies', 'encens', 'huile', 'huiles', 'pendule', 'pendules',
    'pierre', 'pierres', 'resine', 'resines'
  ]);

  const normalizeRelatedArticleWords = (value) => value
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 1 && !/\d/.test(word) && !relatedArticleStopWords.has(word));

  const getRelatedArticleQueries = (productTitle) => {
    const words = normalizeRelatedArticleWords(productTitle);
    const distinctiveWords = words.filter((word) => !relatedArticleProductWords.has(word));
    const queries = [];
    const addQuery = (query) => {
      const cleanQuery = query.trim();
      if (cleanQuery && !queries.includes(cleanQuery)) queries.push(cleanQuery);
    };

    addQuery((distinctiveWords.length ? distinctiveWords : words).slice(0, 4).join(' '));
    distinctiveWords.slice(0, 4).forEach(addQuery);
    words.filter((word) => relatedArticleFamilyWords.has(word)).forEach(addQuery);

    return queries;
  };

  const getRelatedArticleBlogHandle = (articleUrl) => {
    try {
      const url = new URL(articleUrl, window.location.origin);
      const pathParts = url.pathname.split('/').filter(Boolean);
      const blogIndex = pathParts.indexOf('blogs');
      return blogIndex >= 0 ? pathParts[blogIndex + 1] || '' : '';
    } catch (_error) {
      return '';
    }
  };

  const getRelatedArticleBlogUrl = (articleUrl) => {
    try {
      const url = new URL(articleUrl, window.location.origin);
      const pathParts = url.pathname.split('/').filter(Boolean);
      const blogIndex = pathParts.indexOf('blogs');
      if (blogIndex < 0 || !pathParts[blogIndex + 1]) return '';
      return `/${pathParts.slice(0, blogIndex + 2).join('/')}`;
    } catch (_error) {
      return '';
    }
  };

  const cleanRelatedArticleUrl = (articleUrl) => {
    try {
      const url = new URL(articleUrl, window.location.origin);
      return `${url.pathname}${url.hash}`;
    } catch (_error) {
      return articleUrl.split('?')[0];
    }
  };

  const createRelatedArticleCard = (template, article, blogTitle) => {
    const card = template.content.firstElementChild.cloneNode(true);
    const articleUrl = cleanRelatedArticleUrl(article.url);
    const mediaLink = card.querySelector('[data-related-article-media]');
    const titleLink = card.querySelector('[data-related-article-title]');
    const readLink = card.querySelector('[data-related-article-link]');
    const blogLabel = card.querySelector('[data-related-article-blog]');
    const image = card.querySelector('[data-related-article-image]');
    const placeholder = card.querySelector('[data-related-article-placeholder]');
    const featuredImage = article.featured_image;

    [mediaLink, titleLink, readLink].forEach((link) => {
      link.href = articleUrl;
    });

    mediaLink.setAttribute('aria-label', article.title);
    titleLink.textContent = article.title;
    blogLabel.textContent = blogTitle;
    blogLabel.href = article.relatedBlogUrl;

    if (featuredImage?.url) {
      image.src = featuredImage.url;
      image.alt = featuredImage.alt || article.title;
      if (featuredImage.width) image.width = featuredImage.width;
      if (featuredImage.height) image.height = featuredImage.height;
      image.hidden = false;
      placeholder.hidden = true;
    }

    return card;
  };

  const resizeRelatedArticlesCarousel = (carousel) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const flickity = carousel._flkty || window.Flickity?.data(carousel);
        flickity?.reloadCells();
        flickity?.resize();
        flickity?.reposition();
        flickity?.select(0, false, true);
      });
    });
  };

  const mountRelatedArticlesCarousel = (grid) => {
    const panel = grid.closest('[data-related-articles-panel]');
    const arrowsTemplate = panel?.querySelector('[data-related-articles-carousel-arrows]');
    const progress = panel?.querySelector('[data-related-articles-progress]');
    const tabButton = panel?.closest('tabbed-content')?.querySelector('[data-related-articles-tab]');
    if (!panel || !arrowsTemplate || !progress) return grid;

    const cards = Array.from(grid.querySelectorAll('.tec-related-article-card'));

    const carousel = document.createElement('slide-show');
    Array.from(grid.attributes).forEach((attribute) => carousel.setAttribute(attribute.name, attribute.value));
    carousel.classList.add('tec-related-articles--carousel', 'carousel', 'flickity-resize');
    carousel.dataset.relatedArticlesCarousel = 'true';

    cards.forEach((card) => {
      card.classList.add('carousel__slide');
      carousel.append(card);
    });
    carousel.append(arrowsTemplate.content.cloneNode(true));
    grid.replaceWith(carousel);
    progress.hidden = false;

    if (tabButton) {
      tabButton.addEventListener('click', () => resizeRelatedArticlesCarousel(carousel));
    }
    resizeRelatedArticlesCarousel(carousel);
    return carousel;
  };

  const activateRelatedArticlesCarousel = (grid) => {
    const cards = Array.from(grid.querySelectorAll('.tec-related-article-card'));
    if (cards.length <= 3 || grid.matches('slide-show')) return grid;

    const panel = grid.closest('[data-related-articles-panel]');
    const tabButton = panel?.closest('tabbed-content')?.querySelector('[data-related-articles-tab]');
    if (!panel) return grid;

    if (!panel.classList.contains('active') && tabButton) {
      if (grid.dataset.relatedArticlesCarouselPending === 'true') return grid;
      grid.dataset.relatedArticlesCarouselPending = 'true';
      tabButton.addEventListener('click', () => {
        const mountWhenVisible = (attempt = 0) => {
          if ((!panel.classList.contains('active') || panel.offsetWidth === 0) && attempt < 60) {
            requestAnimationFrame(() => mountWhenVisible(attempt + 1));
            return;
          }
          const pendingGrid = panel.querySelector('[data-related-articles-source]');
          if (pendingGrid && !pendingGrid.matches('slide-show')) {
            mountRelatedArticlesCarousel(pendingGrid);
          }
        };
        requestAnimationFrame(() => mountWhenVisible());
      }, { once: true });
      return grid;
    }

    return mountRelatedArticlesCarousel(grid);
  };

  const revealRelatedArticles = (grid) => {
    const tabbedContent = grid.closest('tabbed-content');
    const tabButton = tabbedContent?.querySelector('[data-related-articles-tab]');
    const panel = grid.closest('[data-related-articles-panel]');

    if (!tabbedContent || !tabButton || !panel) return;

    tabButton.hidden = false;
    panel.hidden = false;

    const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
    const hasVisibleActiveButton = buttons.some((button) => button.classList.contains('active') && !button.hidden);
    if (!hasVisibleActiveButton) {
      tabbedContent.selectedIndex = Math.max(0, buttons.indexOf(tabButton));
    }
  };

  const bindRelatedArticleRecommendations = async (grid) => {
    if (grid.dataset.relatedArticlesBound === 'true') return;

    grid.dataset.relatedArticlesBound = 'true';
    grid.dataset.relatedArticlesState = 'loading';

    const panel = grid.closest('[data-related-articles-panel]');
    const template = panel?.querySelector('[data-related-article-template]');
    const blogTitlesElement = panel?.querySelector('[data-related-article-blog-titles]');
    const queries = getRelatedArticleQueries(grid.dataset.productTitle || '');

    if (!template || !blogTitlesElement || !queries.length) {
      grid.dataset.relatedArticlesState = 'empty';
      return;
    }

    grid.dataset.relatedArticlesQuery = queries[0];
    grid.dataset.relatedArticlesQueries = queries.join('|');

    try {
      const blogTitles = JSON.parse(blogTitlesElement.textContent);
      const shopRoot = window.Shopify?.routes?.root || '/';
      const searchResults = await Promise.all(queries.map(async (query) => {
        try {
          const searchParams = new URLSearchParams({
            q: query,
            'resources[type]': 'article',
            'resources[limit]': '10',
            'resources[options][fields]': 'title'
          });
          const response = await fetch(`${shopRoot}search/suggest.json?${searchParams.toString()}`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });

          if (!response.ok) return [];

          const payload = await response.json();
          return payload.resources?.results?.articles || [];
        } catch (_error) {
          return [];
        }
      }));
      const seenUrls = new Set();
      const articles = searchResults.flat().filter((article) => {
        const blogHandle = getRelatedArticleBlogHandle(article.url);
        const articleUrl = cleanRelatedArticleUrl(article.url);
        if (!blogTitles[blogHandle] || seenUrls.has(articleUrl)) return false;
        seenUrls.add(articleUrl);
        article.relatedBlogTitle = blogTitles[blogHandle];
        article.relatedBlogUrl = getRelatedArticleBlogUrl(article.url);
        return true;
      }).slice(0, 6);

      if (!articles.length) {
        grid.dataset.relatedArticlesState = 'empty';
        return;
      }

      const cards = document.createDocumentFragment();
      articles.forEach((article) => {
        cards.append(createRelatedArticleCard(template, article, article.relatedBlogTitle));
      });
      grid.replaceChildren(cards);
      grid.dataset.relatedArticlesState = 'ready';
      revealRelatedArticles(grid);
      activateRelatedArticlesCarousel(grid);
    } catch (_error) {
      grid.dataset.relatedArticlesState = 'error';
    }
  };

  const bindRelatedArticles = (root = document) => {
    const grids = [];
    if (root instanceof HTMLElement && root.matches('[data-related-articles-source]')) {
      grids.push(root);
    }
    root.querySelectorAll?.('[data-related-articles-source]').forEach((grid) => grids.push(grid));
    grids.forEach((grid) => {
      if (grid.dataset.relatedArticlesSource === 'automatic') {
        bindRelatedArticleRecommendations(grid);
        return;
      }
      if (grid.dataset.relatedArticlesBound === 'true') return;
      grid.dataset.relatedArticlesBound = 'true';
      activateRelatedArticlesCarousel(grid);
    });
  };

  const mineralProductStopWords = new Set([
    'a', 'au', 'aux', 'd', 'de', 'des', 'du', 'en', 'et', 'l', 'la', 'le', 'les',
    'pierre', 'pierres', 'un', 'une'
  ]);

  const normalizeMineralProductWords = (value) => String(value || '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 1 && !mineralProductStopWords.has(word));

  const isMatchingMineralProduct = (product, mineralName) => {
    const mineralWords = normalizeMineralProductWords(mineralName);
    const productWords = new Set(normalizeMineralProductWords(product.title));
    return mineralWords.length > 0 && mineralWords.every((word) => productWords.has(word));
  };

  const getMineralProductPriority = (productTitle, productFamily = 'mineral') => {
    if (productFamily !== 'mineral') return 0;
    const title = normalizeMineralProductWords(productTitle).join(' ');
    if (/\b(roulee|roule|galet)\b/.test(title)) return 0;
    if (/\bbracelet\b/.test(title)) return 1;
    if (/\b(brute|brut|eclat|eclats)\b/.test(title)) return 2;
    if (/\bfiole\b/.test(title)) return 3;
    return 4;
  };

  const cleanMineralProductUrl = (productUrl) => {
    try {
      const url = new URL(productUrl, window.location.origin);
      return url.pathname;
    } catch (_error) {
      return String(productUrl || '').split('?')[0];
    }
  };

  const resizeMineralProductImage = (imageUrl, width) => {
    try {
      const url = new URL(imageUrl, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return imageUrl;
    }
  };

  const formatMineralProductPrice = (price, locale, currency) => {
    const amount = Number.parseFloat(price);
    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} €`;
    }
  };

  const getMineralProductHandle = (productUrl) => {
    try {
      const url = new URL(productUrl, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      return parts[0] === 'products' ? parts[1] || '' : '';
    } catch (_error) {
      return '';
    }
  };

  const normalizeMineralProductDetails = (product, source = 'storefront') => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availableVariant = variants.find((variant) => variant.available);
    if (!availableVariant) return null;

    const imageSource = source === 'collection'
      ? product.images?.[0]?.src
      : product.featured_image;
    const rawPrice = availableVariant.price;
    const price = source === 'storefront' ? Number(rawPrice) / 100 : Number.parseFloat(rawPrice);

    return {
      available: true,
      featured_image: {
        alt: product.featured_image?.alt || product.title,
        url: typeof imageSource === 'string' ? imageSource : imageSource?.url
      },
      image: typeof imageSource === 'string' ? imageSource : imageSource?.url,
      price,
      title: product.title,
      url: product.url || `/products/${product.handle}`,
      variantId: availableVariant.id
    };
  };

  const fetchMineralProductDetails = async (product, shopRoot) => {
    const handle = getMineralProductHandle(product.url);
    if (!handle) return null;

    const response = await fetch(`${shopRoot}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return null;
    return normalizeMineralProductDetails(await response.json());
  };

  const createMineralProductAddButton = (product, sidebar) => {
    if (!product.variantId) return null;

    const button = document.createElement('button');
    const addLabel = sidebar.dataset.productAddLabel || '';
    button.type = 'button';
    button.className = 'product-card-small--add-to-cart-button tec-mineral-product-card__add';
    button.dataset.mineralProductAdd = '';
    button.dataset.variantId = String(product.variantId);
    button.setAttribute('aria-label', [addLabel, product.title].filter(Boolean).join(' — '));
    button.title = addLabel;
    button.innerHTML = '<span class="product-card-small--add-to-cart-icon tec-mineral-product-card__add-icon"><svg width="15" height="12" viewBox="0 0 15 12" fill="none" aria-hidden="true" focusable="false"><path d="M6.963 10.79a1.21 1.21 0 1 1-2.419 0 1.21 1.21 0 0 1 2.419 0ZM11.865 10.79a1.21 1.21 0 1 1-2.419 0 1.21 1.21 0 0 1 2.419 0ZM13.616 1.878H6.088V3.15h6.732l-.764 3.247a.949.949 0 0 1-.923.748h-5.22a.953.953 0 0 1-.939-.748L3.637.493A.637.637 0 0 0 3.016 0H.947v1.273h1.56l1.225 5.411A2.234 2.234 0 0 0 5.913 8.42h5.22a2.23 2.23 0 0 0 2.165-1.735l.939-4.027a.637.637 0 0 0-.621-.78Z" fill="currentColor"/></svg></span><span class="tec-mineral-product-card__add-check" aria-hidden="true">✓</span>';
    const addText = document.createElement('span');
    addText.className = 'product-card-small--add-to-cart-text tec-mineral-product-card__add-text';
    addText.textContent = addLabel;
    button.append(addText);
    return button;
  };

  const createMineralProductCard = (product, index, sidebar) => {
    const card = document.createElement('article');
    const imageLink = document.createElement('a');
    const titleLink = document.createElement('a');
    const media = document.createElement('div');
    const image = document.createElement('img');
    const content = document.createElement('div');
    const purchase = document.createElement('div');
    const price = document.createElement('span');
    const productLabel = sidebar.dataset.productLabel || '';
    const featuredImage = product.featured_image;

    card.className = `product-card-small tec-mineral-product-card${index === 0 ? ' tec-mineral-product-card--primary' : ''}`;
    imageLink.href = cleanMineralProductUrl(product.url);
    imageLink.target = '_blank';
    imageLink.rel = 'noopener noreferrer';
    imageLink.setAttribute('aria-label', [productLabel, product.title].filter(Boolean).join(' : '));

    media.className = 'product-card-small--image tec-mineral-product-card__media';
    image.className = 'tec-mineral-product-card__image';
    image.loading = 'lazy';
    image.alt = featuredImage?.alt || product.title;
    image.src = resizeMineralProductImage(featuredImage?.url || product.image, 240);
    if (featuredImage?.width) image.width = featuredImage.width;
    if (featuredImage?.height) image.height = featuredImage.height;
    imageLink.append(image);
    media.append(imageLink);

    content.className = 'product-card-small--info tec-mineral-product-card__content';
    titleLink.className = 'product-card-small--link tec-mineral-product-card__title';
    titleLink.href = cleanMineralProductUrl(product.url);
    titleLink.target = '_blank';
    titleLink.rel = 'noopener noreferrer';
    titleLink.title = product.title;
    titleLink.textContent = product.title;
    content.append(titleLink);

    purchase.className = 'product-card-small--add-to-cart tec-mineral-product-card__purchase';
    price.className = 'price tec-mineral-product-card__price';
    price.textContent = formatMineralProductPrice(product.price, sidebar.dataset.locale, sidebar.dataset.currency);
    purchase.append(price);

    card.append(media, content);
    const addButton = createMineralProductAddButton(product, sidebar);
    if (addButton) purchase.append(addButton);
    content.append(purchase);
    return card;
  };

  const updateMineralCartSections = (sections) => {
    if (!sections) return;
    [
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
    ].forEach(({ id, section, selector }) => {
      const target = document.getElementById(id);
      const html = sections[section];
      if (!target || !html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(selector);
      const destination = target.querySelector(selector) || target;
      if (source) destination.innerHTML = source.innerHTML;
    });
  };

  const bindMineralProductAddButtons = (sidebar) => {
    sidebar.querySelectorAll('[data-mineral-product-add]').forEach((button) => {
      if (button.dataset.mineralProductAddBound === 'true') return;
      button.dataset.mineralProductAddBound = 'true';
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const variantId = Number(button.dataset.variantId);
        if (!Number.isFinite(variantId)) return;

        const status = sidebar.querySelector('[data-mineral-products-status]');
        button.disabled = true;
        button.classList.remove('is-added');

        try {
          const response = await fetch(sidebar.dataset.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: ['cart-drawer', 'cart-bubble'],
              sections_url: window.location.pathname
            })
          });
          const payload = await response.json();
          if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Cart request failed');

          updateMineralCartSections(payload.sections);
          button.classList.add('is-added');
          if (status) status.textContent = sidebar.dataset.productAddedLabel || '';
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { product: payload.items?.[0] }
          }));
          document.getElementById('Cart-Drawer')?.open?.();
          window.setTimeout(() => button.classList.remove('is-added'), 1800);
        } catch (_error) {
          if (status) status.textContent = sidebar.dataset.productAddError || '';
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  const fetchLatestMineralProducts = async (sidebar, shopRoot, limit = 6) => {
    const collectionHandle = sidebar.dataset.fallbackCollection || 'mineraux-pierres';
    const requestLimit = Math.min(250, Math.max(24, limit));
    const response = await fetch(`${shopRoot}collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${requestLimit}&sort_by=created-descending`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Mineral fallback collection failed');
    const payload = await response.json();
    return (payload.products || [])
      .map((product) => normalizeMineralProductDetails(product, 'collection'))
      .filter(Boolean)
      .slice(0, limit);
  };

  const shuffleMineralProducts = (products) => {
    const shuffledProducts = products.slice();
    for (let index = shuffledProducts.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffledProducts[index], shuffledProducts[randomIndex]] = [shuffledProducts[randomIndex], shuffledProducts[index]];
    }
    return shuffledProducts;
  };

  const renderMineralProducts = (sidebar, list, products, heading) => {
    const cards = document.createDocumentFragment();
    products.forEach((product, index) => cards.append(createMineralProductCard(product, index, sidebar)));
    list.replaceChildren(cards);
    const headingElement = sidebar.querySelector('[data-mineral-products-heading]');
    if (headingElement && heading) headingElement.textContent = heading;
    bindMineralProductAddButtons(sidebar);
    syncMineralArticleLayout(sidebar, true);
  };

  const syncMineralArticleLayout = (sidebar, hasProducts) => {
    const layout = sidebar.closest('.aeternum-article-layout--mineral, .aeternum-article-layout--tarot-products');
    const articleMain = layout?.querySelector(':scope > .aeternum-article-main');
    if (!layout || !articleMain) return;

    sidebar.hidden = !hasProducts;
    layout.style.setProperty(
      'grid-template-columns',
      hasProducts ? 'var(--aet-mineral-sidebar-width) minmax(0, 1fr)' : 'minmax(0, 1fr)',
      'important'
    );
    layout.style.setProperty('gap', hasProducts ? 'var(--aet-mineral-column-gap)' : '0', 'important');
    articleMain.style.setProperty('grid-column', hasProducts ? '2' : '1', 'important');
    articleMain.style.setProperty('justify-self', hasProducts ? 'start' : 'center', 'important');
  };

  const bindMineralArticleProductRecommendations = async (sidebar) => {
    if (sidebar.dataset.mineralProductsBound === 'true') return;
    sidebar.dataset.mineralProductsBound = 'true';

    if (sidebar.dataset.mineralProductsSource === 'manual') {
      syncMineralArticleLayout(sidebar, true);
      bindMineralProductAddButtons(sidebar);
      return;
    }

    const mineralName = sidebar.dataset.mineralQuery || '';
    const productFamily = sidebar.dataset.productFamily || 'mineral';
    const list = sidebar.querySelector('[data-mineral-products-list]');
    if (!mineralName || !list) {
      syncMineralArticleLayout(sidebar, true);
      bindMineralProductAddButtons(sidebar);
      return;
    }

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';

      if (sidebar.dataset.mineralProductsSource === 'random') {
        const randomProducts = shuffleMineralProducts(
          await fetchLatestMineralProducts(sidebar, shopRoot, 250)
        ).slice(0, 6);

        if (randomProducts.length) {
          renderMineralProducts(sidebar, list, randomProducts, sidebar.dataset.productsHeading);
          return;
        }
      }

      if (productFamily === 'metal') {
        const collectionProducts = await fetchLatestMineralProducts(sidebar, shopRoot, 24);
        const matchingMetalProducts = collectionProducts
          .filter((product) => isMatchingMineralProduct(product, mineralName))
          .slice(0, 6);

        if (matchingMetalProducts.length) {
          renderMineralProducts(sidebar, list, matchingMetalProducts, sidebar.dataset.productsHeading);
          return;
        }

        if (collectionProducts.length) {
          renderMineralProducts(sidebar, list, collectionProducts.slice(0, 6), sidebar.dataset.latestProductsHeading);
          return;
        }
      }

      const searchParams = new URLSearchParams({
        q: mineralName,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title'
      });
      const response = await fetch(`${shopRoot}search/suggest.json?${searchParams.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Mineral product search failed');

      const payload = await response.json();
      const matchingProducts = (payload.resources?.results?.products || [])
        .filter((product) => product.available && isMatchingMineralProduct(product, mineralName))
        .map((product, originalIndex) => ({ product, originalIndex }))
        .sort((left, right) => {
          const priorityDifference = getMineralProductPriority(left.product.title, productFamily) - getMineralProductPriority(right.product.title, productFamily);
          return priorityDifference || left.originalIndex - right.originalIndex;
        })
        .slice(0, 6)
        .map(({ product }) => product);

      const products = (await Promise.all(
        matchingProducts.map((product) => fetchMineralProductDetails(product, shopRoot))
      )).filter(Boolean);

      if (products.length) {
        renderMineralProducts(sidebar, list, products, sidebar.dataset.productsHeading);
        return;
      }

      const latestProducts = await fetchLatestMineralProducts(sidebar, shopRoot);
      if (latestProducts.length) {
        renderMineralProducts(sidebar, list, latestProducts, sidebar.dataset.latestProductsHeading);
        return;
      }

      syncMineralArticleLayout(sidebar, true);
      bindMineralProductAddButtons(sidebar);
    } catch (_error) {
      syncMineralArticleLayout(sidebar, true);
      bindMineralProductAddButtons(sidebar);
    }
  };

  const bindMineralArticleProducts = (root = document) => {
    const sidebars = [];
    if (root instanceof HTMLElement && root.matches('[data-mineral-products]')) sidebars.push(root);
    root.querySelectorAll?.('[data-mineral-products]').forEach((sidebar) => sidebars.push(sidebar));
    sidebars.forEach(bindMineralArticleProductRecommendations);
  };

  const syncSameAuthorCardHeights = (carousel) => {
    const cards = Array.from(carousel.querySelectorAll('[data-same-author-product]'));
    if (!cards.length || carousel.offsetWidth === 0) return;

    cards.forEach((card) => card.style.removeProperty('height'));
    const tallestCardHeight = Math.ceil(Math.max(...cards.map((card) => card.getBoundingClientRect().height)));
    if (!tallestCardHeight) return;

    cards.forEach((card) => {
      card.style.height = `${tallestCardHeight}px`;
    });

    const flickity = carousel._flkty || window.Flickity?.data(carousel);
    flickity?.resize();
    flickity?.reposition();
  };

  const scheduleSameAuthorCardHeightSync = (carousel) => {
    cancelAnimationFrame(carousel._sameAuthorHeightFrame);
    carousel._sameAuthorHeightFrame = requestAnimationFrame(() => {
      carousel._sameAuthorHeightFrame = requestAnimationFrame(() => syncSameAuthorCardHeights(carousel));
    });
  };

  const bindSameAuthorCardHeightSync = (carousel) => {
    if (carousel.dataset.sameAuthorHeightBound === 'true') return;
    carousel.dataset.sameAuthorHeightBound = 'true';

    carousel.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => scheduleSameAuthorCardHeightSync(carousel), { once: true });
      }
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleSameAuthorCardHeightSync(carousel));
    }

    const contentObserver = new MutationObserver(() => scheduleSameAuthorCardHeightSync(carousel));
    contentObserver.observe(carousel, { childList: true, subtree: true });
    carousel._sameAuthorHeightObserver = contentObserver;

    window.addEventListener('resize', () => scheduleSameAuthorCardHeightSync(carousel), { passive: true });
    scheduleSameAuthorCardHeightSync(carousel);
  };

  const mountSameAuthorCarousel = (grid) => {
    const panel = grid.closest('[data-same-author-panel]');
    const arrowsTemplate = panel?.querySelector('[data-same-author-carousel-arrows]');
    const progress = panel?.querySelector('[data-same-author-progress]');
    const tabButton = panel?.closest('tabbed-content')?.querySelector('[data-same-author-tab]');
    if (!panel || !arrowsTemplate || !progress) return grid;

    const cards = Array.from(grid.querySelectorAll('[data-same-author-product]'));
    const carousel = document.createElement('slide-show');
    Array.from(grid.attributes).forEach((attribute) => carousel.setAttribute(attribute.name, attribute.value));
    carousel.classList.add('tec-related-articles--carousel', 'carousel', 'flickity-resize');
    carousel.dataset.sameAuthorCarousel = 'true';

    cards.forEach((card) => {
      card.classList.add('carousel__slide');
      carousel.append(card);
    });
    carousel.append(arrowsTemplate.content.cloneNode(true));
    grid.replaceWith(carousel);
    progress.hidden = false;

    if (tabButton) {
      tabButton.addEventListener('click', () => {
        resizeRelatedArticlesCarousel(carousel);
        scheduleSameAuthorCardHeightSync(carousel);
      });
    }
    bindSameAuthorCardHeightSync(carousel);
    resizeRelatedArticlesCarousel(carousel);
    return carousel;
  };

  const activateSameAuthorCarousel = (grid) => {
    const cards = Array.from(grid.querySelectorAll('[data-same-author-product]'));
    if (cards.length <= 3 || grid.matches('slide-show')) return grid;

    const panel = grid.closest('[data-same-author-panel]');
    const tabButton = panel?.closest('tabbed-content')?.querySelector('[data-same-author-tab]');
    if (!panel) return grid;

    if (!panel.classList.contains('active') && tabButton) {
      if (grid.dataset.sameAuthorCarouselPending === 'true') return grid;
      grid.dataset.sameAuthorCarouselPending = 'true';
      tabButton.addEventListener('click', () => {
        const mountWhenVisible = (attempt = 0) => {
          if ((!panel.classList.contains('active') || panel.offsetWidth === 0) && attempt < 60) {
            requestAnimationFrame(() => mountWhenVisible(attempt + 1));
            return;
          }
          const pendingGrid = panel.querySelector('[data-same-author-source]');
          if (pendingGrid && !pendingGrid.matches('slide-show')) {
            mountSameAuthorCarousel(pendingGrid);
          }
        };
        requestAnimationFrame(() => mountWhenVisible());
      }, { once: true });
      return grid;
    }

    return mountSameAuthorCarousel(grid);
  };

  const revealSameAuthorProducts = (grid) => {
    const tabbedContent = grid.closest('tabbed-content');
    const tabButton = tabbedContent?.querySelector('[data-same-author-tab]');
    const panel = grid.closest('[data-same-author-panel]');
    if (!tabbedContent || !tabButton || !panel) return;

    tabButton.hidden = false;
    panel.hidden = false;

    const buttons = Array.from(tabbedContent.querySelectorAll('.tabbed-content--tabs button'));
    const hasVisibleActiveButton = buttons.some((button) => button.classList.contains('active') && !button.hidden);
    if (!hasVisibleActiveButton) {
      tabbedContent.selectedIndex = Math.max(0, buttons.indexOf(tabButton));
    }
  };

  const bindSameAuthorProducts = async (grid) => {
    if (grid.dataset.sameAuthorBound === 'true') return;

    grid.dataset.sameAuthorBound = 'true';
    grid.dataset.sameAuthorState = 'loading';

    const author = grid.dataset.author?.trim();
    const authorFilterKey = grid.dataset.authorFilterKey?.trim();
    const collectionPath = grid.dataset.collectionUrl;
    const currentProductHandle = grid.dataset.currentProductHandle;
    if (!author || !authorFilterKey || !collectionPath || !currentProductHandle) {
      grid.dataset.sameAuthorState = 'empty';
      return;
    }

    try {
      const collectionUrl = new URL(collectionPath, window.location.origin);
      collectionUrl.searchParams.set('view', 'author-products');
      collectionUrl.searchParams.set(authorFilterKey, author);

      const response = await fetch(collectionUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error(`Same-author request failed with ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = Array.from(responseDocument.querySelectorAll('[data-same-author-product]'))
        .filter((card) => card.dataset.productHandle !== currentProductHandle)
        .slice(0, 6)
        .map((card) => document.importNode(card, true));

      if (!cards.length) {
        grid.dataset.sameAuthorState = 'empty';
        return;
      }

      grid.replaceChildren(...cards);
      grid.dataset.sameAuthorState = 'ready';
      revealSameAuthorProducts(grid);
      activateSameAuthorCarousel(grid);
    } catch (_error) {
      grid.dataset.sameAuthorState = 'error';
    }
  };

  const bindAllSameAuthorProducts = (root = document) => {
    const grids = [];
    if (root instanceof HTMLElement && root.matches('[data-same-author-source]')) {
      grids.push(root);
    }
    root.querySelectorAll?.('[data-same-author-source]').forEach((grid) => grids.push(grid));
    grids.forEach(bindSameAuthorProducts);
  };

  const getGrimoireArticleFamily = (articleUrl) => {
    try {
      const pathname = new URL(articleUrl, window.location.origin).pathname;
      if (pathname.includes('/blogs/grimoire-mineral/')) return 'mineral';
      if (pathname.includes('/blogs/grimoire-metaux-magiques/')) return 'metal';
      if (pathname.includes('/blogs/grimoire-botanique/')) return 'botanical';
    } catch (_error) {
      return '';
    }
    return '';
  };

  const renderGrimoireSearchState = (results, message) => {
    results.replaceChildren();
    const state = document.createElement('p');
    state.className = 'tec-grimoire-home__search-state';
    state.textContent = message;
    results.append(state);
    results.hidden = false;
  };

  const renderGrimoireSearchResults = (home, results, articles) => {
    results.replaceChildren();

    articles.forEach((article) => {
      const family = getGrimoireArticleFamily(article.url);
      const link = document.createElement('a');
      const title = document.createElement('strong');
      const label = document.createElement('span');

      link.className = 'tec-grimoire-home__search-result';
      link.href = article.url;
      link.target = '_blank';
      link.rel = 'noopener';
      title.textContent = article.title;
      label.textContent = home.dataset[`${family}Label`] || home.dataset.mineralLabel || '';
      link.append(title, label);
      results.append(link);
    });

    results.hidden = false;
  };

  const bindGrimoireHomeSearch = (home) => {
    const input = home.querySelector('[data-grimoire-search-input]');
    const results = home.querySelector('[data-grimoire-search-results]');
    const clearButton = home.querySelector('[data-grimoire-search-clear]');
    if (!input || !results || !clearButton) return;

    let debounceTimer = null;
    let activeRequest = null;
    const allowedFamilies = new Set(
      (home.dataset.grimoireFamilies || 'mineral,metal')
        .split(',')
        .map((family) => family.trim())
        .filter(Boolean)
    );

    const closeResults = () => {
      results.hidden = true;
      results.replaceChildren();
      input.setAttribute('aria-expanded', 'false');
    };

    const search = async () => {
      const query = input.value.trim();
      clearButton.hidden = query.length === 0;

      if (query.length < 2) {
        activeRequest?.abort();
        closeResults();
        return;
      }

      activeRequest?.abort();
      activeRequest = new AbortController();
      input.setAttribute('aria-expanded', 'true');
      renderGrimoireSearchState(results, home.dataset.searchLoading);

      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const searchParams = new URLSearchParams({
          q: query,
          'resources[type]': 'article',
          'resources[limit]': '10',
          'resources[options][fields]': 'title'
        });
        const response = await fetch(`${shopRoot}search/suggest.json?${searchParams.toString()}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: activeRequest.signal
        });
        if (!response.ok) throw new Error('Grimoire search failed');

        const payload = await response.json();
        const seen = new Set();
        const articles = (payload.resources?.results?.articles || []).filter((article) => {
          const family = getGrimoireArticleFamily(article.url);
          const articleUrl = new URL(article.url, window.location.origin).pathname;
          if (!family || !allowedFamilies.has(family) || seen.has(articleUrl)) return false;
          seen.add(articleUrl);
          return true;
        });

        if (!articles.length) {
          renderGrimoireSearchState(results, home.dataset.searchEmpty);
          return;
        }

        renderGrimoireSearchResults(home, results, articles);
      } catch (error) {
        if (error.name === 'AbortError') return;
        renderGrimoireSearchState(results, home.dataset.searchError);
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(search, 260);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeResults();
        input.blur();
      }
    });

    clearButton.addEventListener('click', () => {
      input.value = '';
      clearButton.hidden = true;
      closeResults();
      input.focus();
    });

    document.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('.tec-grimoire-home__search')) closeResults();
    });
  };

  const bindGrimoireHomeTabs = (home) => {
    const tabs = Array.from(home.querySelectorAll('[data-grimoire-tab]'));
    const panels = Array.from(home.querySelectorAll('[data-grimoire-panel]'));
    if (!tabs.length || !panels.length) return;

    tabs.forEach((tab, tabIndex) => {
      tab.addEventListener('click', () => {
        const selectedFamily = tab.dataset.grimoireTab;
        tabs.forEach((candidate) => candidate.setAttribute('aria-selected', String(candidate === tab)));
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.grimoirePanel !== selectedFamily;
        });
      });

      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextTab = tabs[(tabIndex + direction + tabs.length) % tabs.length];
        nextTab.focus();
        nextTab.click();
      });
    });
  };

  const animateGrimoireGlossaryDialog = async (dialog, origin, opening) => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || typeof dialog.animate !== 'function' || !origin) return;

    const dialogRect = dialog.getBoundingClientRect();
    const originRect = origin.getBoundingClientRect();
    const originCenterX = originRect.left + (originRect.width / 2);
    const originCenterY = originRect.top + (originRect.height / 2);
    const dialogCenterX = dialogRect.left + (dialogRect.width / 2);
    const dialogCenterY = dialogRect.top + (dialogRect.height / 2);
    const translateX = originCenterX - dialogCenterX;
    const translateY = originCenterY - dialogCenterY;
    const scaleX = Math.max(0.18, Math.min(1, originRect.width / dialogRect.width));
    const scaleY = Math.max(0.18, Math.min(1, originRect.height / dialogRect.height));
    const cardFrame = {
      opacity: 0,
      transform: `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`,
      borderRadius: '16px'
    };
    const dialogFrame = {
      opacity: 1,
      transform: 'translate(0, 0) scale(1, 1)',
      borderRadius: '24px'
    };

    try {
      await dialog.animate(
        opening ? [cardFrame, dialogFrame] : [dialogFrame, cardFrame],
        {
          duration: opening ? 480 : 320,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
        }
      ).finished;
    } catch (_error) {
      // The dialog can safely finish in its target state if an animation is interrupted.
    }
  };

  const closeGrimoireGlossaryDialog = async (dialog) => {
    if (!dialog?.open || dialog.dataset.grimoireGlossaryClosing === 'true') return;

    dialog.dataset.grimoireGlossaryClosing = 'true';
    const origin = dialog.grimoireGlossaryOrigin;
    await animateGrimoireGlossaryDialog(dialog, origin, false);
    dialog.close();
    delete dialog.dataset.grimoireGlossaryClosing;
    origin?.focus({ preventScroll: true });
  };

  const bindGrimoireHomeGlossary = (home) => {
    const dialog = home.querySelector('[data-grimoire-glossary-dialog]');
    const closeButton = dialog?.querySelector('[data-grimoire-glossary-close]');
    const dialogLetter = dialog?.querySelector('[data-grimoire-glossary-letter]');
    const dialogTitle = dialog?.querySelector('[data-grimoire-glossary-title]');
    const dialogBody = dialog?.querySelector('[data-grimoire-glossary-body]');
    if (!dialog || !closeButton || !dialogLetter || !dialogTitle || !dialogBody) return;

    home.querySelectorAll('[data-grimoire-glossary-trigger]').forEach((trigger) => {
      if (trigger.dataset.grimoireGlossaryBound === 'true') return;
      trigger.dataset.grimoireGlossaryBound = 'true';

      trigger.addEventListener('click', async () => {
        if (dialog.open || dialog.dataset.grimoireGlossaryClosing === 'true') return;
        dialogLetter.textContent = trigger.dataset.glossaryLetter || '';
        dialogTitle.textContent = trigger.dataset.glossaryTitle || '';
        const content = trigger.querySelector('template[data-grimoire-glossary-content]');
        if (content) dialogBody.replaceChildren(content.content.cloneNode(true));
        else dialogBody.textContent = trigger.dataset.glossaryBody || '';
        dialog.grimoireGlossaryOrigin = trigger;
        dialog.showModal();
        await animateGrimoireGlossaryDialog(dialog, trigger, true);
      });
    });

    if (dialog.dataset.grimoireGlossaryBound === 'true') return;
    dialog.dataset.grimoireGlossaryBound = 'true';
    closeButton.addEventListener('click', () => closeGrimoireGlossaryDialog(dialog));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeGrimoireGlossaryDialog(dialog);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const dialogRect = dialog.getBoundingClientRect();
      const clickedBackdrop = event.clientX < dialogRect.left
        || event.clientX > dialogRect.right
        || event.clientY < dialogRect.top
        || event.clientY > dialogRect.bottom;
      if (clickedBackdrop) closeGrimoireGlossaryDialog(dialog);
    });
  };

  const setGrimoireLinksNewTab = (root) => {
    const links = [];
    if (root instanceof HTMLAnchorElement) links.push(root);
    root.querySelectorAll?.('a[href]').forEach((link) => links.push(link));

    links.forEach((link) => {
      const href = link.getAttribute('href')?.trim();
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      link.target = '_blank';
      const relValues = new Set((link.rel || '').split(/\s+/).filter(Boolean));
      relValues.add('noopener');
      link.rel = Array.from(relValues).join(' ');
    });
  };

  const bindGrimoireHomeLinks = (home) => {
    const main = home.closest('#main-content') || document.querySelector('#main-content');
    if (!main || main.dataset.grimoireLinksNewTabBound === 'true') return;

    main.dataset.grimoireLinksNewTabBound = 'true';
    setGrimoireLinksNewTab(main);

    const linksObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) setGrimoireLinksNewTab(node);
        });
      });
    });
    linksObserver.observe(main, { childList: true, subtree: true });
  };

  document.addEventListener('aeternum:glossary:refresh', (event) => {
    const dictionary = event.target.closest?.('[data-dictionary]');
    if (dictionary) bindGrimoireHomeGlossary(dictionary);
  });

  const bindGrimoireHomes = (root = document) => {
    const homes = [];
    if (root instanceof HTMLElement && root.matches('[data-grimoire-home], [data-dictionary]')) homes.push(root);
    root.querySelectorAll?.('[data-grimoire-home], [data-dictionary]').forEach((home) => homes.push(home));

    homes.forEach((home) => {
      if (home.dataset.grimoireHomeBound === 'true') return;
      home.dataset.grimoireHomeBound = 'true';
      if (home.hasAttribute('data-dictionary')) { bindGrimoireHomeGlossary(home); return; }
      bindGrimoireHomeSearch(home);
      bindGrimoireHomeTabs(home);
      bindGrimoireHomeGlossary(home);
      bindGrimoireHomeLinks(home);
    });
  };

  const normalizeBotanicalIndexValue = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const bindBotanicalIndex = (index) => {
    if (index.dataset.botanicalIndexBound === 'true') return;

    const input = index.querySelector('[data-botanical-search]');
    const clearButton = index.querySelector('[data-botanical-search-clear]');
    const grid = index.querySelector('[data-botanical-grid]');
    const pagination = index.querySelector('[data-botanical-pagination]');
    let cards = Array.from(index.querySelectorAll('[data-botanical-card]'));
    const letterButtons = Array.from(index.querySelectorAll('[data-botanical-letter]'));
    const count = index.querySelector('[data-botanical-results-count]');
    const empty = index.querySelector('[data-botanical-empty]');
    if (!input || !clearButton || !cards.length || !letterButtons.length || !count || !empty) return;

    index.dataset.botanicalIndexBound = 'true';
    let activeLetter = 'all';
    let normalizedCards = [];
    let availableLetters = new Set();
    const countTemplates = {
      one: index.dataset.resultsTemplateOne || '__COUNT__',
      other: index.dataset.resultsTemplateOther || '__COUNT__'
    };

    // Symbol articles use question titles; their index follows the symbol's name.
    const cardSortName = (card) => {
      const title = card.dataset.sortTitle || '';
      if (!index.hasAttribute('data-symbols-index')) return normalizeBotanicalIndexValue(title);
      return normalizeBotanicalIndexValue(title
        .replace(/^(?:que signifie(?:nt)?|quel est|quelle est|quels sont|quelles sont)\s+/i, '')
        .replace(/^(?:le |la |les |l[’'])/i, '')
        .replace(/^symbole (?:alchimique )?(?:du |de la |des |de l[’']|d[’']|de )/i, ''));
    };

    const refreshCardIndex = () => {
      cards = Array.from(index.querySelectorAll('[data-botanical-card]'));
      if (grid && index.dataset.botanicalSort === 'true') {
        cards.sort((firstCard, secondCard) => cardSortName(firstCard).localeCompare(cardSortName(secondCard)));
        cards.forEach((card) => grid.appendChild(card));
      }
      normalizedCards = cards.map((card) => ({
        card,
        letter: (index.hasAttribute('data-symbols-index') ? cardSortName(card) : normalizeBotanicalIndexValue(card.dataset.titleLetter)).slice(0, 1),
        searchIndex: normalizeBotanicalIndexValue(card.dataset.searchIndex)
      }));
      availableLetters = new Set(normalizedCards.map(({ letter }) => letter).filter(Boolean));
    };

    refreshCardIndex();

    const updateLetterButtons = () => {
      letterButtons.forEach((button) => {
        const letter = button.dataset.botanicalLetter;
        const isActive = letter === activeLetter;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
        if (letter !== 'all') button.disabled = !availableLetters.has(letter);
      });
    };

    const updateResults = () => {
      const queryTokens = normalizeBotanicalIndexValue(input.value).split(' ').filter(Boolean);
      let visibleCount = 0;
      normalizedCards.forEach(({ card, letter, searchIndex }) => {
        const visible = queryTokens.every((token) => searchIndex.includes(token)) && (activeLetter === 'all' || letter === activeLetter);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });
      clearButton.hidden = input.value.length === 0;
      const countTemplate = visibleCount === 1 ? countTemplates.one : countTemplates.other;
      count.textContent = countTemplate.replace('__COUNT__', String(visibleCount));
      empty.hidden = visibleCount !== 0;
    };

    const loadAdditionalPages = async () => {
      let nextUrl = pagination?.dataset.botanicalNextUrl || '';
      if (!grid || !pagination || !nextUrl) {
        updateLetterButtons();
        updateResults();
        return;
      }

      const visitedUrls = new Set();
      index.classList.add('is-hydrating');
      input.disabled = true;
      pagination.hidden = true;

      try {
        while (nextUrl) {
          const requestUrl = new URL(nextUrl, window.location.origin);
          if (requestUrl.origin !== window.location.origin || visitedUrls.has(requestUrl.href)) throw new Error();
          visitedUrls.add(requestUrl.href);

          const response = await fetch(requestUrl.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error();

          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const remoteIndex = page.querySelector('[data-botanical-index]');
          const remoteCards = Array.from(remoteIndex?.querySelectorAll('[data-botanical-card]') || []);
          if (!remoteCards.length) break;

          remoteCards.forEach((card) => grid.appendChild(card));
          nextUrl = remoteIndex?.querySelector('[data-botanical-pagination]')?.dataset.botanicalNextUrl || '';
        }

        pagination.remove();
      } catch {
        pagination.hidden = false;
      } finally {
        input.disabled = false;
        index.classList.remove('is-hydrating');
        refreshCardIndex();
        updateLetterButtons();
        updateResults();
      }
    };

    input.addEventListener('input', () => {
      if (activeLetter !== 'all') {
        activeLetter = 'all';
        updateLetterButtons();
      }
      updateResults();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || input.value.length === 0) return;
      input.value = '';
      updateResults();
    });
    clearButton.addEventListener('click', () => {
      input.value = '';
      activeLetter = 'all';
      updateLetterButtons();
      updateResults();
      input.focus();
    });
    letterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        activeLetter = button.dataset.botanicalLetter || 'all';
        updateLetterButtons();
        updateResults();
      });
    });

    if (pagination?.dataset.botanicalNextUrl) {
      loadAdditionalPages();
    } else {
      updateLetterButtons();
      updateResults();
    }
  };

  const bindBotanicalIndexes = (root = document) => {
    const indexes = [];
    if (root instanceof HTMLElement && root.matches('[data-botanical-index]')) indexes.push(root);
    root.querySelectorAll?.('[data-botanical-index]').forEach((index) => indexes.push(index));
    indexes.forEach(bindBotanicalIndex);
  };


  const syncGrimoireProductCardHeights = (carousel) => {
    const allCards = Array.from(carousel.querySelectorAll('[data-collection] product-card'));
    const activeCards = Array.from(carousel.querySelectorAll('.carousel__slide product-card'));
    if (!activeCards.length || carousel.offsetWidth === 0) return;

    allCards.forEach((card) => card.style.removeProperty('height'));
    const tallestCardHeight = Math.ceil(Math.max(...activeCards.map((card) => card.getBoundingClientRect().height)));
    if (!tallestCardHeight) return;

    activeCards.forEach((card) => {
      card.style.height = `${tallestCardHeight}px`;
    });

    const flickity = carousel._flkty || window.Flickity?.data(carousel);
    flickity?.resize();
    flickity?.reposition();
  };

  const scheduleGrimoireProductCardHeightSync = (carousel) => {
    cancelAnimationFrame(carousel._grimoireProductHeightFrame);
    carousel._grimoireProductHeightFrame = requestAnimationFrame(() => {
      carousel._grimoireProductHeightFrame = requestAnimationFrame(() => syncGrimoireProductCardHeights(carousel));
    });
  };

  const bindGrimoireProductCardHeightSync = (carousel) => {
    if (carousel.dataset.grimoireProductHeightBound === 'true') {
      scheduleGrimoireProductCardHeightSync(carousel);
      return;
    }

    carousel.dataset.grimoireProductHeightBound = 'true';
    carousel.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => scheduleGrimoireProductCardHeightSync(carousel), { once: true });
      }
    });

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleGrimoireProductCardHeightSync(carousel));
    }

    const contentObserver = new MutationObserver(() => scheduleGrimoireProductCardHeightSync(carousel));
    contentObserver.observe(carousel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    carousel._grimoireProductHeightObserver = contentObserver;

    window.addEventListener('resize', () => scheduleGrimoireProductCardHeightSync(carousel), { passive: true });
    scheduleGrimoireProductCardHeightSync(carousel);
  };

  const bindGrimoireProductCardHeights = (root = document) => {
    const selector = ':is(.section-grimoire-mineral-home, .section-grimoire-botanique-home) + .section-collection-tabs slide-show.products, .tec-mineral-choice__product-carousel, .tec-hub-product-carousel';
    const carousels = [];
    if (root instanceof HTMLElement && root.matches(selector)) carousels.push(root);
    root.querySelectorAll?.(selector).forEach((carousel) => carousels.push(carousel));
    carousels.forEach(bindGrimoireProductCardHeightSync);
  };

  const HOME_NEW_ARRIVALS_CAROUSEL = '[id$="__16614195534fb74bc3"] slide-show.products';
  const HOME_TRACKPAD_CAROUSELS = [
    HOME_NEW_ARRIVALS_CAROUSEL,
    'body.template-index [id$="__home_universes"] slide-show.collection-grid__carousel'
  ].join(',');
  const HOME_NEW_ARRIVALS_EXTERNAL_LINKS = [
    '[id$="__16614195534fb74bc3"] .section-header--description a[href]',
    '[id$="__16614195534fb74bc3"] product-card a.product-featured-image-link[href]',
    '[id$="__16614195534fb74bc3"] product-card a.product-card-title[href]',
    '[id$="__rich_text_893NFg"] .rich-text .rte a[href]'
  ].join(',');

  const bindHomeNewArrivalsExternalLinks = (root = document) => {
    const links = [];
    if (root.matches?.(HOME_NEW_ARRIVALS_EXTERNAL_LINKS)) links.push(root);
    root.querySelectorAll?.(HOME_NEW_ARRIVALS_EXTERNAL_LINKS).forEach((link) => links.push(link));

    links.forEach((link) => {
      link.target = '_blank';
      link.rel = 'noopener';
    });
  };

  const bindHomeNewArrivalsTrackpad = (carousel) => {
    if (carousel.dataset.tecTrackpadBound === 'true') return;
    carousel.dataset.tecTrackpadBound = 'true';

    let accumulatedDelta = 0;
    let lastMoveAt = 0;
    let resetTimer = null;

    carousel.addEventListener('wheel', (event) => {
      if (carousel.closest('#aeternum-univers') && window.matchMedia('(max-width: 767px)').matches) return;
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 2) return;

      const flickity = carousel._flkty || window.Flickity?.data(carousel);
      if (!flickity) return;

      event.preventDefault();
      accumulatedDelta += event.deltaX;
      window.clearTimeout(resetTimer);
      resetTimer = window.setTimeout(() => {
        accumulatedDelta = 0;
      }, 180);

      const now = Date.now();
      if (Math.abs(accumulatedDelta) < 34 || now - lastMoveAt < 240) return;

      const direction = Math.sign(accumulatedDelta) * (document.dir === 'rtl' ? -1 : 1);
      accumulatedDelta = 0;
      lastMoveAt = now;

      if (direction > 0) {
        flickity.next();
      } else {
        flickity.previous();
      }
    }, { passive: false });
  };

  const bindHomeNewArrivalsTrackpads = (root = document) => {
    const carousels = [];
    if (root.matches?.(HOME_TRACKPAD_CAROUSELS)) carousels.push(root);
    root.querySelectorAll?.(HOME_TRACKPAD_CAROUSELS).forEach((carousel) => carousels.push(carousel));
    carousels.forEach(bindHomeNewArrivalsTrackpad);
  };

  const positionNoAiPhotoDialog = (dialog, trigger) => {
    if (!dialog || dialog.hidden || !trigger) return;

    if (window.matchMedia('(max-width: 767px)').matches) {
      dialog.style.removeProperty('left');
      dialog.style.removeProperty('top');
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const dialogRect = dialog.getBoundingClientRect();
    const viewportInset = 16;
    const gap = 10;
    const left = Math.min(
      window.innerWidth - dialogRect.width - viewportInset,
      Math.max(viewportInset, triggerRect.right - dialogRect.width)
    );
    const spaceAbove = triggerRect.top - dialogRect.height - gap;
    const top = spaceAbove >= viewportInset
      ? spaceAbove
      : Math.min(window.innerHeight - dialogRect.height - viewportInset, triggerRect.bottom + gap);

    dialog.style.left = `${Math.round(left)}px`;
    dialog.style.top = `${Math.round(Math.max(viewportInset, top))}px`;
  };

  const closeNoAiPhotoDialog = (dialog, restoreFocus = false) => {
    if (!dialog || dialog.hidden) return;

    const trigger = dialog._aeternumNoAiTrigger;
    dialog.hidden = true;
    trigger?.setAttribute('aria-expanded', 'false');
    if (restoreFocus) trigger?.focus();
  };

  const bindNoAiPhotoDialog = (dialog) => {
    if (dialog.dataset.aeternumNoAiBound === 'true') return;

    dialog.dataset.aeternumNoAiBound = 'true';
    dialog.querySelector('[data-no-ai-photo-close]')?.addEventListener('click', () => {
      closeNoAiPhotoDialog(dialog, true);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeNoAiPhotoDialog(dialog, true);
      }
    });
  };

  const bindNoAiPhotoTriggers = (root = document) => {
    const triggers = [];
    if (root instanceof HTMLElement && root.matches('[data-no-ai-photo-trigger]')) {
      triggers.push(root);
    }
    root.querySelectorAll?.('[data-no-ai-photo-trigger]').forEach((trigger) => triggers.push(trigger));

    root.querySelectorAll?.('[data-no-ai-photo-dialog]').forEach(bindNoAiPhotoDialog);

    triggers.forEach((trigger) => {
      if (trigger.dataset.aeternumNoAiBound === 'true') return;

      trigger.dataset.aeternumNoAiBound = 'true';
      ['pointerdown', 'mousedown', 'touchstart'].forEach((eventName) => {
        trigger.addEventListener(eventName, (event) => event.stopPropagation(), { passive: true });
      });
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!dialog) return;

        if (!dialog.hidden && dialog._aeternumNoAiTrigger === trigger) {
          closeNoAiPhotoDialog(dialog);
          return;
        }

        closeNoAiPhotoDialog(dialog);
        dialog._aeternumNoAiTrigger = trigger;
        dialog.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        positionNoAiPhotoDialog(dialog, trigger);
        dialog.querySelector('[data-no-ai-photo-close]')?.focus({ preventScroll: true });
      });
    });
  };

  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll('[data-no-ai-photo-dialog]:not([hidden])').forEach((dialog) => {
      if (!dialog.contains(event.target) && event.target !== dialog._aeternumNoAiTrigger) {
        closeNoAiPhotoDialog(dialog);
      }
    });
  });

  window.addEventListener('scroll', () => {
    document.querySelectorAll('[data-no-ai-photo-dialog]:not([hidden])').forEach((dialog) => {
      closeNoAiPhotoDialog(dialog);
    });
  }, { passive: true });

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-no-ai-photo-dialog]:not([hidden])').forEach((dialog) => {
      positionNoAiPhotoDialog(dialog, dialog._aeternumNoAiTrigger);
    });
  });

  const productPairingRequests = new Map();

  const updateProductPairingControls = (pairing) => {
    const track = pairing?.querySelector('[data-aeternum-product-pairing-track]');
    if (!track) return;

    const previous = pairing.querySelector('[data-aeternum-product-pairing-control="previous"]');
    const next = pairing.querySelector('[data-aeternum-product-pairing-control="next"]');
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentScroll = Math.abs(track.scrollLeft);
    const firstItem = track.querySelector('.aeternum-product-pairing__item');
    if (firstItem) {
      track.style.scrollSnapType = maxScroll > 0 && maxScroll < firstItem.getBoundingClientRect().width ? 'none' : '';
    }

    if (previous) previous.disabled = currentScroll <= 2;
    if (next) next.disabled = currentScroll >= maxScroll - 2;
  };

  const initializeProductPairing = (root = document) => {
    const pairings = [];
    if (root.matches?.('[data-aeternum-product-pairing]')) pairings.push(root);
    pairings.push(...root.querySelectorAll?.('[data-aeternum-product-pairing]') || []);

    pairings.forEach((pairing) => {
      const track = pairing.querySelector('[data-aeternum-product-pairing-track]');
      if (!track || track.dataset.aeternumPairingTrackBound === 'true') return;

      track.dataset.aeternumPairingTrackBound = 'true';
      track.addEventListener('scroll', () => updateProductPairingControls(pairing), { passive: true });
      window.requestAnimationFrame(() => updateProductPairingControls(pairing));
    });
  };

  const refreshProductPairing = async (variantInput, force = false) => {
    const productForm = variantInput.closest('product-form');
    const sectionId = productForm?.dataset.section;
    const variantId = variantInput.value;

    if (!sectionId || !variantId) return;

    const pairings = Array.from(document.querySelectorAll(
      `[data-aeternum-product-pairing][data-section-id="${sectionId}"]`
    ));

    await Promise.all(pairings.map(async (pairing) => {
      if (!force && String(pairing.dataset.variantId) === String(variantId)) return;

      const blockId = pairing.dataset.blockId;
      const requestKey = `${sectionId}:${blockId}`;
      const previousRequest = productPairingRequests.get(requestKey);
      previousRequest?.abort();

      const controller = new AbortController();
      productPairingRequests.set(requestKey, controller);
      pairing.setAttribute('aria-busy', 'true');
      pairing.classList.add('aeternum-product-pairing--loading');

      try {
        const productUrl = new URL(pairing.dataset.productUrl || window.location.pathname, window.location.origin);
        productUrl.searchParams.set('variant', variantId);
        productUrl.searchParams.set('section_id', sectionId);

        const response = await fetch(productUrl.toString(), {
          credentials: 'same-origin',
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Product pairing request failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector(
          `[data-aeternum-product-pairing][data-block-id="${blockId}"]`
        );
        if (controller.signal.aborted || productPairingRequests.get(requestKey) !== controller) return;

        if (replacement) {
          pairing.replaceWith(replacement);
          initializeProductPairing(replacement);
        } else {
          pairing.remove();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          pairing.removeAttribute('aria-busy');
          pairing.classList.remove('aeternum-product-pairing--loading');
        }
      } finally {
        if (productPairingRequests.get(requestKey) === controller) {
          productPairingRequests.delete(requestKey);
        }
      }
    }));
  };

  document.addEventListener('change', (event) => {
    const variantInput = event.target.closest?.('form[id^="product-form-"] input[name="id"]');
    if (variantInput) refreshProductPairing(variantInput);
  });

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-aeternum-product-pairing-control]');
    if (!control) return;

    const pairing = control.closest('[data-aeternum-product-pairing]');
    const track = pairing?.querySelector('[data-aeternum-product-pairing-track]');
    const firstItem = track?.querySelector('.aeternum-product-pairing__item');
    if (!track || !firstItem) return;

    const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) || 0;
    const amount = firstItem.getBoundingClientRect().width + gap;
    const direction = control.dataset.aeternumProductPairingControl === 'previous' ? -1 : 1;
    const rtlMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;

    track.scrollBy({
      left: amount * direction * rtlMultiplier,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-aeternum-pairing-add]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (button.getAttribute('aria-disabled') === 'true' || button.classList.contains('is-added')) return;

    const variantId = String(button.dataset.variantId || '');
    const label = button.querySelector('.product-card-small--add-to-cart-text');
    const card = button.closest('.product-card-small');
    const pairing = button.closest('[data-aeternum-product-pairing]');
    const addUrl = button.dataset.cartAddUrl;
    if (!variantId || !label || !addUrl) return;

    const defaultLabel = button.dataset.defaultLabel || label.textContent.trim();
    const defaultAriaLabel = button.getAttribute('aria-label');
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }]
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.status) {
        throw new Error(payload.description || payload.message || `Cart request failed (${response.status})`);
      }

      const addedProduct = payload.items?.[0] || payload;
      label.textContent = button.dataset.addedLabel;
      button.classList.add('is-added');
      card?.classList.add('aeternum-product-pairing__card--added');
      button.setAttribute('aria-label', button.dataset.addedLabel);

      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { product: addedProduct }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      window.setTimeout(() => {
        if (!button.isConnected) return;
        label.textContent = defaultLabel;
        button.classList.remove('is-added');
        card?.classList.remove('aeternum-product-pairing__card--added');
        if (defaultAriaLabel) button.setAttribute('aria-label', defaultAriaLabel);
        const variantInput = document.querySelector(
          `product-form[data-section="${pairing?.dataset.sectionId}"] input[name="id"]`
        );
        if (variantInput) refreshProductPairing(variantInput, true);
      }, 1800);
    } catch (error) {
      console.error('[Aeternum product pairing]', error);
      const variantInput = document.querySelector(
        `product-form[data-section="${pairing?.dataset.sectionId}"] input[name="id"]`
      );
      if (variantInput) refreshProductPairing(variantInput, true);
    } finally {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
    }
  }, true);


  const bindArcanePantheonStacks = (root = document) => {
    const pantheons = [];
    if (root.matches?.('[data-arcane-pantheon]')) pantheons.push(root);
    pantheons.push(...root.querySelectorAll?.('[data-arcane-pantheon]') || []);

    pantheons.forEach((pantheon) => {
      if (pantheon.dataset.arcanePantheonBound === 'true') return;

      const pool = Array.from(pantheon.querySelectorAll('[data-arcane-pool] [data-image]'));
      const cards = Array.from(pantheon.querySelectorAll('[data-arcane-mini-card]'));
      const names = Array.from(pantheon.querySelectorAll('[data-arcane-featured-name]'));
      if (pool.length < 1 || cards.length < 1) return;

      pantheon.dataset.arcanePantheonBound = 'true';
      const shuffled = pool.slice();

      for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
      }

      cards.forEach((card, index) => {
        const source = shuffled[index % shuffled.length];
        const image = card.querySelector('img');
        const title = card.querySelector('[data-arcane-mini-title]');
        if (!source) return;

        if (image) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = source.dataset.image;
        }
        if (title) title.textContent = source.dataset.title;
        if (names[index]) names[index].textContent = source.dataset.title;
      });
    });
  };

  const HOME_REVIEW_SELECTOR = 'body.template-index .AirReviews-CarouselWrapper';
  const HOME_REVIEW_CONTINUOUS_SPEED = 22;
  const HOME_REVIEW_MANUAL_DURATION = 650;

  const createHomeReviewChevron = (direction) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    svg.classList.add('tec-home-review-chevron');
    svg.setAttribute('width', '6');
    svg.setAttribute('height', '10');
    svg.setAttribute('viewBox', '0 0 6 10');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');

    path.setAttribute('d', direction === 'previous' ? 'M5 1L1 5L5 9' : 'M1 1L5 5L1 9');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);

    return svg;
  };

  const createHomeReviewCtaChevron = () => {
    const icon = document.createElement('span');
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    icon.className = 'tec-home-review-cta-icon';
    icon.setAttribute('aria-hidden', 'true');
    svg.classList.add('tec-home-review-cta-chevron');
    svg.setAttribute('width', '6');
    svg.setAttribute('height', '10');
    svg.setAttribute('viewBox', '0 0 6 10');
    svg.setAttribute('fill', 'none');
    path.setAttribute('d', 'M1 1L5 5L1 9');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.2');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    icon.append(svg);

    return icon;
  };

  const syncHomeReviewCard = (card) => {
    const starRate = card.querySelector('.AirReviews-Carousel__Rate .AirReviews-StarRate');
    const rate = starRate?.closest('.AirReviews-Carousel__Rate');
    const ratingLabel = starRate?.getAttribute('aria-label') || '';
    const ratingMatch = ratingLabel.match(/\d+(?:[.,]\d+)?/);
    if (!starRate || !rate || !ratingMatch) return;

    const rating = Number.parseFloat(ratingMatch[0].replace(',', '.'));
    if (!Number.isFinite(rating)) return;

    let score = rate.querySelector(':scope > .tec-home-review-score');
    if (!score) {
      score = document.createElement('span');
      score.className = 'tec-home-review-score';
      starRate.after(score);
    }
    if (score.textContent !== `${rating}/5`) score.textContent = `${rating}/5`;
  };

  const syncHomeReviewModal = (details) => {
    const wrapper = details.closest('.AirReviews-Modal__Wrapper');
    if (!wrapper) return;

    wrapper.classList.add('tec-home-review-modal');

    const close = wrapper.querySelector('.AirReviews-Modal__Close');
    close?.classList.add('tec-home-review-modal-close');

    wrapper.querySelectorAll('.AirReviews-ImageActions, .AirReviews-RotateBtn').forEach((control) => {
      control.setAttribute('aria-hidden', 'true');
      control.setAttribute('tabindex', '-1');
    });

    const rate = details.querySelector('.AirReviews-Stack__Rate');
    const starRate = rate?.querySelector('.AirReviews-StarRate');
    const ratingLabel = starRate?.getAttribute('aria-label') || '';
    const ratingMatch = ratingLabel.match(/\d+(?:[.,]\d+)?/);

    if (rate && starRate && ratingMatch) {
      const rating = Number.parseFloat(ratingMatch[0].replace(',', '.'));
      if (Number.isFinite(rating)) {
        let score = rate.querySelector(':scope > .tec-home-review-modal-score');
        if (!score) {
          score = document.createElement('span');
          score.className = 'tec-home-review-modal-score';
          starRate.after(score);
        }
        if (score.textContent !== `${rating}/5`) score.textContent = `${rating}/5`;
      }
    }

    const buyButton = details.querySelector('.AirReviews-Modal__ReviewDetails--ShopButton');
    if (buyButton) {
      buyButton.classList.add('button', 'text-button', 'text-button--has-icon', 'tec-home-review-modal-buy');
      if (!buyButton.querySelector(':scope > .tec-home-review-modal-buy-icon')) {
        const icon = createHomeReviewCtaChevron();
        icon.className = 'tec-home-review-modal-buy-icon';
        icon.querySelector('svg')?.classList.replace('tec-home-review-cta-chevron', 'tec-home-review-modal-buy-chevron');
        buyButton.append(icon);
      }
    }
  };

  const bindHomeReviewModals = (root = document) => {
    const selector = 'body.template-index .AirReviews-Modal__ReviewDetails';
    const details = [];
    if (root.matches?.(selector)) details.push(root);
    const parentDetails = root.closest?.(selector);
    if (parentDetails) details.push(parentDetails);
    details.push(...root.querySelectorAll?.(selector) || []);
    [...new Set(details)].forEach(syncHomeReviewModal);
  };

  const syncHomeReviewControls = (wrapper) => {
    [
      ['.AirReviews-Carousel__NavPrev', 'previous'],
      ['.AirReviews-Carousel__NavNext', 'next']
    ].forEach(([selector, direction]) => {
      const control = wrapper.querySelector(selector);
      if (!control || control.querySelector('.tec-home-review-chevron')) return;
      control.append(createHomeReviewChevron(direction));
    });
  };

  const syncHomeReviewHeading = (wrapper) => {
    const appSection = wrapper.closest('.shopify-section');
    const heading = wrapper.querySelector('.AirReviews-Carousel__Heading');
    const title = heading?.querySelector('.AirReviews-Carousel__Header');
    if (!appSection || !heading || !title) return;

    heading.dataset.tecHomeReviewHeading = 'true';
    heading.classList.add('section-header', 'tec-home-review-heading');
    title.setAttribute('role', 'heading');
    title.setAttribute('aria-level', '2');
    title.classList.add('section-header--title', 'h3');
    const reviewAnchor = document.getElementById('aeternum-avis');
    if (reviewAnchor && reviewAnchor !== heading) reviewAnchor.removeAttribute('id');
    heading.id = 'aeternum-avis';
    const titleSuffix = appSection.querySelector('[data-home-title-suffix]')?.dataset.homeTitleSuffix;
    if (titleSuffix && !title.textContent.trim().endsWith(titleSuffix)) title.append(document.createTextNode(titleSuffix));

    let headingContent = heading.querySelector(':scope > .tec-home-review-heading-content');
    if (!headingContent) {
      headingContent = document.createElement('div');
      headingContent.className = 'section-header--content tec-home-review-heading-content';
      heading.prepend(headingContent);
    }
    if (title.parentElement !== headingContent) headingContent.append(title);

    const eyebrowSourceSection = appSection.previousElementSibling;
    const eyebrowSource = eyebrowSourceSection?.querySelector('h1, h2, h3, h4, h5, h6');
    let eyebrow = heading.querySelector('.tec-home-review-eyebrow');
    if (!eyebrow && eyebrowSource) {
      const eyebrow = document.createElement('p');
      eyebrow.className = 'subheading tec-home-review-eyebrow';
      eyebrow.textContent = eyebrowSource.textContent
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^—\s*/, '')
        .replace(/[.\s]+$/, '');
      headingContent.prepend(eyebrow);
      eyebrowSourceSection.classList.add('tec-home-review-source-section');
    } else if (eyebrow) {
      eyebrow.classList.add('subheading');
      if (eyebrow.parentElement !== headingContent) headingContent.prepend(eyebrow);
    }

    const ctaSourceSection = appSection.nextElementSibling;
    const ctaSource = ctaSourceSection?.querySelector('a.button[href*="/pages/tous-les-avis-clients"]');
    let cta = heading.querySelector('.tec-home-review-cta');
    if (!cta && ctaSource) {
      cta = ctaSource.cloneNode(true);
      cta.classList.add('text-button', 'text-button--has-icon', 'tec-home-review-cta');

      const label = cta.textContent.replace(/\s*[⟫»›]+\s*$/, '').trim();
      cta.replaceChildren(document.createTextNode(label), createHomeReviewCtaChevron());

      const ctaWrapper = document.createElement('div');
      ctaWrapper.className = 'section-header--button tec-home-review-heading-button';
      ctaWrapper.append(cta);
      heading.append(ctaWrapper);
      ctaSourceSection.classList.add('tec-home-review-source-section');
    } else if (cta) {
      cta.classList.add('text-button', 'text-button--has-icon');
      let ctaWrapper = cta.closest('.tec-home-review-heading-button');
      if (!ctaWrapper) {
        ctaWrapper = document.createElement('div');
        ctaWrapper.className = 'section-header--button tec-home-review-heading-button';
        cta.before(ctaWrapper);
        ctaWrapper.append(cta);
      }
    }
  };

  const bindHomeReviewContinuousMotion = (wrapper) => {
    if (wrapper.dataset.tecHomeReviewContinuousBound === 'true') {
      wrapper._tecHomeReviewBindContinuousControls?.();
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const viewport = wrapper.querySelector('.swiper');
    const track = viewport?.querySelector('.swiper-wrapper');
    const originals = Array.from(track?.querySelectorAll(':scope > .swiper-slide:not([data-tec-home-review-clone])') || []);
    if (!viewport || !track || originals.length < 2) return;

    wrapper.dataset.tecHomeReviewContinuousBound = 'true';
    wrapper.classList.add('tec-home-review-continuous');

    const clones = originals.map((original, index) => {
      const clone = original.cloneNode(true);
      clone.dataset.tecHomeReviewClone = 'true';
      clone.dataset.tecHomeReviewOriginalIndex = String(index);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
      clone.addEventListener('click', (event) => {
        if (wrapper._tecHomeReviewWasDragged) return;
        event.stopPropagation();
        original.querySelector('.AirReviews-Carousel__Image')?.click();
      });
      return clone;
    });
    track.append(...clones);

    const state = {
      cycleWidth: 0,
      dragging: false,
      dragStartOffset: 0,
      dragStartX: 0,
      lastTime: 0,
      manual: null,
      offset: 0,
      step: 0
    };

    const normalizeOffset = () => {
      if (!state.cycleWidth) return;
      state.offset = ((state.offset % state.cycleWidth) + state.cycleWidth) % state.cycleWidth;
    };

    const measure = () => {
      const firstOriginal = originals[0];
      const firstClone = clones[0];
      const previousCycleWidth = state.cycleWidth;
      const progress = previousCycleWidth ? state.offset / previousCycleWidth : 0;
      state.cycleWidth = firstClone.offsetLeft - firstOriginal.offsetLeft;
      state.step = originals[1].offsetLeft - firstOriginal.offsetLeft;
      if (previousCycleWidth && state.cycleWidth) state.offset = progress * state.cycleWidth;
      normalizeOffset();
    };

    const easeInOutCubic = (progress) => progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;

    const frame = () => {
      const time = Date.now();
      if (!state.lastTime) state.lastTime = time;
      const elapsedSeconds = Math.min((time - state.lastTime) / 1000, 0.05);
      state.lastTime = time;

      if (!document.hidden && !state.dragging) {
        state.offset += HOME_REVIEW_CONTINUOUS_SPEED * elapsedSeconds;
      }

      if (state.manual) {
        const progress = Math.min((time - state.manual.startedAt) / HOME_REVIEW_MANUAL_DURATION, 1);
        const position = state.manual.distance * easeInOutCubic(progress);
        state.offset += position - state.manual.previousPosition;
        state.manual.previousPosition = position;
        if (progress === 1) state.manual = null;
      }

      normalizeOffset();
      track.style.transform = `translate3d(${-state.offset}px, 0, 0)`;
    };

    const shiftByOneReview = (direction) => {
      state.manual = {
        distance: state.step * direction,
        previousPosition: 0,
        startedAt: Date.now()
      };
    };
    wrapper._tecHomeReviewShiftByOne = shiftByOneReview;

    const continuousControls = new WeakSet();
    const bindContinuousControls = () => {
      [
        ['.AirReviews-Carousel__NavPrev', -1],
        ['.AirReviews-Carousel__NavNext', 1]
      ].forEach(([selector, direction]) => {
        let control = wrapper.querySelector(selector);
        if (!control || continuousControls.has(control)) return;
        const cleanControl = control.cloneNode(true);
        cleanControl.dataset.tecHomeReviewContinuousControl = 'true';
        control.replaceWith(cleanControl);
        control = cleanControl;
        continuousControls.add(control);
        control.disabled = false;
        control.classList.remove('disabled');
        control.setAttribute('aria-disabled', 'false');
        control.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          wrapper._tecHomeReviewLastControlPointer = Date.now();
          shiftByOneReview(direction);
        }, true);
        control.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopImmediatePropagation();
          shiftByOneReview(direction);
        }, true);
      });
    };
    wrapper._tecHomeReviewBindContinuousControls = bindContinuousControls;
    bindContinuousControls();

    const finishDrag = () => {
      if (!state.dragging) return;
      state.dragging = false;
      window.setTimeout(() => { wrapper._tecHomeReviewWasDragged = false; }, 0);
    };

    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      state.dragging = true;
      state.dragStartX = event.clientX;
      state.dragStartOffset = state.offset;
      wrapper._tecHomeReviewWasDragged = false;
      event.stopImmediatePropagation();
    }, true);
    window.addEventListener('pointermove', (event) => {
      if (!state.dragging) return;
      const distance = event.clientX - state.dragStartX;
      if (Math.abs(distance) > 5) wrapper._tecHomeReviewWasDragged = true;
      state.offset = state.dragStartOffset - distance;
      normalizeOffset();
      if (wrapper._tecHomeReviewWasDragged) event.preventDefault();
    }, { passive: false });
    window.addEventListener('pointerup', finishDrag, { passive: true });
    window.addEventListener('pointercancel', finishDrag, { passive: true });
    viewport.addEventListener('click', (event) => {
      if (!wrapper._tecHomeReviewWasDragged) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    measure();
    window.setTimeout(measure, 100);
    window.setTimeout(measure, 500);
    window.addEventListener('resize', measure, { passive: true });
    wrapper._tecHomeReviewContinuousTimer = window.setInterval(frame, 16);
  };

  const bindHomeReviewContinuousControlClicks = () => {
    if (document.documentElement.dataset.tecHomeReviewControlClicksBound === 'true') return;
    document.documentElement.dataset.tecHomeReviewControlClicksBound = 'true';
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('.AirReviews-Carousel__NavPrev, .AirReviews-Carousel__NavNext');
      const wrapper = control?.closest(HOME_REVIEW_SELECTOR);
      if (!control || !wrapper || typeof wrapper._tecHomeReviewShiftByOne !== 'function') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const direction = control.matches('.AirReviews-Carousel__NavPrev') ? -1 : 1;
      if (Date.now() - (wrapper._tecHomeReviewLastControlPointer || 0) > 500) {
        wrapper._tecHomeReviewShiftByOne(direction);
      }
    }, true);
  };

  const bindHomeReviewCarousels = (root = document) => {
    bindHomeReviewContinuousControlClicks();
    const wrappers = [];
    if (root.matches?.(HOME_REVIEW_SELECTOR)) wrappers.push(root);
    const parentWrapper = root.closest?.(HOME_REVIEW_SELECTOR);
    if (parentWrapper) wrappers.push(parentWrapper);
    wrappers.push(...root.querySelectorAll?.(HOME_REVIEW_SELECTOR) || []);

    [...new Set(wrappers)].forEach((wrapper) => {
      syncHomeReviewHeading(wrapper);
      wrapper.querySelectorAll('.AirReviews-Carousel__Card').forEach(syncHomeReviewCard);
      syncHomeReviewControls(wrapper);
      bindHomeReviewContinuousMotion(wrapper);
    });
  };

  const bindArticleLikes = (root = document) => {
    const buttons = [];

    if (root instanceof Element && root.matches('[data-aeternum-article-like]')) {
      buttons.push(root);
    }

    root.querySelectorAll?.('[data-aeternum-article-like]').forEach((button) => {
      buttons.push(button);
    });

    buttons.forEach((button) => {
      if (button.dataset.aeternumArticleLikeBound === 'true') return;

      const countElement = button.querySelector('[data-aeternum-article-like-count]');
      const endpoint = button.dataset.aeternumArticleLikeEndpoint;
      const likePath = button.dataset.aeternumArticleLikePath;
      const articleId = button.dataset.aeternumArticleLikeId;
      const addLabel = button.dataset.aeternumArticleLikeAddLabel || '';
      const addedLabel = button.dataset.aeternumArticleLikeAddedLabel || addLabel;
      const errorLabel = button.dataset.aeternumArticleLikeErrorLabel || addLabel;
      const storageKey = `aeternum_article_like_${articleId}`;
      const locale = document.documentElement.lang || 'fr';
      const formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
      let likes = 0;
      let liked = false;
      let pending = false;

      if (!countElement || !endpoint || !likePath || !articleId) return;

      try {
        liked = localStorage.getItem(storageKey) === '1';
      } catch (_) {}

      const render = ({ animate = false } = {}) => {
        button.classList.toggle('is-liked', liked);
        button.setAttribute('aria-pressed', String(liked));
        button.setAttribute('aria-label', liked ? addedLabel : addLabel);
        countElement.textContent = formatter.format(Math.max(0, likes));

        if (animate) {
          button.classList.remove('is-animating');
          window.requestAnimationFrame(() => {
            button.classList.add('is-animating');
            window.setTimeout(() => button.classList.remove('is-animating'), 560);
          });
        }
      };

      const requestLikes = async (method = 'GET') => {
        const response = await fetch(
          `${endpoint}?article=${encodeURIComponent(likePath)}`,
          { method, mode: 'cors', credentials: 'omit', cache: 'no-store' }
        );

        if (!response.ok) throw new Error(`Article like request failed (${response.status})`);

        const payload = await response.json();
        const value = Number(payload.views);
        if (!Number.isFinite(value)) throw new Error('Article like count is invalid');
        return Math.max(0, value);
      };

      button.addEventListener('click', async () => {
        if (liked || pending) {
          if (liked) render({ animate: true });
          return;
        }

        const previousLikes = likes;
        pending = true;
        liked = true;
        likes += 1;
        render({ animate: true });

        try {
          likes = await requestLikes('POST');
          try {
            localStorage.setItem(storageKey, '1');
          } catch (_) {}
          render();
        } catch (_) {
          liked = false;
          likes = previousLikes;
          render();
          button.setAttribute('aria-label', errorLabel);
          window.setTimeout(() => button.setAttribute('aria-label', addLabel), 2000);
        } finally {
          pending = false;
        }
      });

      button.dataset.aeternumArticleLikeBound = 'true';
      render();

      requestLikes()
        .then((value) => {
          likes = value;
          render();
        })
        .catch(() => {});
    });
  };

  const bindAeternumAllReviews = (root = document) => {
    const reviewPages = [];

    if (root instanceof Element && root.matches('[data-aeternum-all-reviews]')) {
      reviewPages.push(root);
    }

    root.querySelectorAll?.('[data-aeternum-all-reviews]').forEach((page) => reviewPages.push(page));

    reviewPages.forEach((page) => {
      if (page.dataset.aeternumAllReviewsBound === 'true') return;

      const endpoint = page.dataset.endpoint;
      const shopId = page.dataset.shopId;
      const pageSize = Number.parseInt(page.dataset.pageSize, 10) || 12;
      const isPreview = page.hasAttribute('data-reviews-preview');
      const locale = page.dataset.locale || document.documentElement.lang || 'fr';
      const list = page.querySelector('[data-reviews-list]');
      const status = page.querySelector('[data-reviews-status]');
      const statusText = page.querySelector('[data-reviews-status-text]');
      const spinner = page.querySelector('.aeternum-all-reviews__spinner');
      const loadMore = page.querySelector('[data-reviews-load-more]');
      const sortSelect = page.querySelector('[data-reviews-sort]');
      const ratingButtons = [...page.querySelectorAll('[data-rating]')];
      const counter = page.querySelector('[data-reviews-counter]');

      if (!endpoint || !shopId || !list || !status || !statusText || !loadMore || (!isPreview && !sortSelect)) return;

      const labels = {
        loading: page.dataset.loadingLabel || '',
        loadMore: page.dataset.loadMoreLabel || '',
        retry: page.dataset.retryLabel || '',
        error: page.dataset.errorLabel || '',
        empty: page.dataset.emptyLabel || '',
        verified: page.dataset.verifiedLabel || '',
        rating: page.dataset.ratingLabel || '',
        readMore: page.dataset.readMoreLabel || '',
        readLess: page.dataset.readLessLabel || '',
        loadedCount: page.dataset.loadedCountLabel || '__COUNT__'
      };

      const numberFormatter = new Intl.NumberFormat(locale);
      const dateFormatter = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });

      const reviewTotal = Number.parseInt(page.dataset.reviewTotal, 10);
      if (Number.isFinite(reviewTotal)) {
        page.querySelectorAll('[data-review-total-value]').forEach((value) => {
          value.textContent = numberFormatter.format(reviewTotal);
        });
      }

      let reviews = [];
      let cursor = '';
      let selectedRating = '';
      let hasNext = true;
      let loading = false;
      let requestController = null;
      const seenReviewIds = new Set();

      const safeUrl = (value) => {
        if (!value) return '';

        try {
          const url = new URL(value, window.location.origin);
          return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
        } catch (_error) {
          return '';
        }
      };

      const setStatus = (message = '', { isLoading = false, isError = false } = {}) => {
        status.hidden = !message;
        status.classList.toggle('is-error', isError);
        spinner.hidden = !isLoading;
        statusText.textContent = message;
      };

      const updateControls = ({ retry = false } = {}) => {
        loadMore.disabled = loading;
        loadMore.hidden = isPreview ? !retry : (reviews.length === 0 ? !retry : (!hasNext && !retry));
        loadMore.textContent = loading ? labels.loading : (retry ? labels.retry : labels.loadMore);
        page.setAttribute('aria-busy', String(loading));

        if (counter) counter.textContent = reviews.length
          ? labels.loadedCount.replace('__COUNT__', numberFormatter.format(reviews.length))
          : '';
      };

      const squareStarPath = 'M7.25762 3.44868C7.4895 2.87125 7.60544 2.58253 7.77108 2.49723C7.91473 2.42325 8.08527 2.42325 8.22892 2.49723C8.39456 2.58253 8.5105 2.87125 8.74238 3.44868L9.46804 5.25573C9.53592 5.42477 9.56986 5.50929 9.62573 5.57321C9.67513 5.62974 9.73661 5.67441 9.80563 5.70392C9.88369 5.7373 9.97456 5.74346 10.1563 5.75578L12.0992 5.88752C12.72 5.92961 13.0304 5.95066 13.1627 6.08183C13.2775 6.19559 13.3302 6.35779 13.3042 6.51727C13.2742 6.70116 13.0355 6.90064 12.558 7.29961L11.0636 8.54816C10.9238 8.66496 10.8539 8.72336 10.8104 8.79624C10.7719 8.86068 10.7484 8.93297 10.7417 9.00773C10.734 9.09228 10.7563 9.18061 10.8007 9.35726L11.2758 11.2457C11.4276 11.8492 11.5035 12.1509 11.4196 12.3173C11.3469 12.4616 11.2089 12.5618 11.0492 12.5864C10.8651 12.6147 10.6016 12.4493 10.0746 12.1185L8.42537 11.083C8.27109 10.9862 8.19396 10.9378 8.11119 10.9189C8.038 10.9022 7.962 10.9022 7.88881 10.9189C7.80605 10.9378 7.72891 10.9862 7.57463 11.083L5.9254 12.1185C5.3984 12.4493 5.1349 12.6147 4.95076 12.5864C4.79106 12.5618 4.65308 12.4616 4.58035 12.3173C4.49649 12.1509 4.57239 11.8492 4.72421 11.2457L5.19929 9.35726C5.24373 9.18061 5.26595 9.09228 5.25833 9.00773C5.25159 8.93297 5.2281 8.86068 5.18961 8.79624C5.14608 8.72336 5.07619 8.66496 4.9364 8.54817L3.44203 7.29961C2.96452 6.90064 2.72576 6.70116 2.69582 6.51727C2.66985 6.35779 2.72255 6.19559 2.8373 6.08183C2.96961 5.95066 3.28002 5.92961 3.90084 5.88752L5.8437 5.75578C6.02544 5.74346 6.11631 5.7373 6.19437 5.70392C6.26339 5.67441 6.32488 5.62974 6.37427 5.57321C6.43014 5.50929 6.46408 5.42477 6.53196 5.25573L7.25762 3.44868Z';

      const createSquareStar = (filled) => {
        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        svg.setAttribute('viewBox', '0 0 16 16');
        svg.setAttribute('aria-hidden', 'true');
        svg.classList.add('aeternum-review-star');
        if (filled) svg.classList.add('is-filled');

        const outer = document.createElementNS(namespace, 'rect');
        outer.setAttribute('width', '16');
        outer.setAttribute('height', '16');
        outer.setAttribute('rx', '3');
        outer.classList.add('aeternum-review-star__outer');

        const inner = document.createElementNS(namespace, 'path');
        inner.setAttribute('d', squareStarPath);
        inner.classList.add('aeternum-review-star__inner');
        svg.append(outer, inner);
        return svg;
      };

      const createVerifiedIcon = () => {
        const namespace = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(namespace, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.classList.add('aeternum-review-card__verified-icon');

        const seal = document.createElementNS(namespace, 'path');
        seal.setAttribute('d', 'M23 12 20.56 9.21l.34-3.69-3.61-.82L15.4 1.5 12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.81 1.89 3.2 3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12Z');

        const check = document.createElementNS(namespace, 'path');
        check.setAttribute('d', 'm7.35 12.15 2.75 2.75 6.55-6.55');
        check.setAttribute('fill', 'none');
        check.setAttribute('stroke', 'currentColor');
        check.setAttribute('stroke-linecap', 'round');
        check.setAttribute('stroke-linejoin', 'round');
        check.setAttribute('stroke-width', '2');
        svg.append(seal, check);
        return svg;
      };

      const createStars = (ratingValue) => {
        const rating = Math.max(0, Math.min(5, Number(ratingValue) || 0));
        const stars = document.createElement('div');
        stars.className = 'aeternum-review-card__stars';
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${labels.rating} ${rating}/5`);

        for (let index = 1; index <= 5; index += 1) {
          stars.append(createSquareStar(index <= Math.round(rating)));
        }

        return stars;
      };

      const createReviewCard = (review) => {
        const card = document.createElement('article');
        card.className = 'aeternum-review-card';

        const top = document.createElement('div');
        top.className = 'aeternum-review-card__top';
        top.append(createStars(review.rate));

        const createdAt = review.createdAt ? new Date(review.createdAt) : null;
        if (createdAt && !Number.isNaN(createdAt.getTime())) {
          const date = document.createElement('time');
          date.className = 'aeternum-review-card__date';
          date.dateTime = createdAt.toISOString();
          date.textContent = dateFormatter.format(createdAt);
          top.append(date);
        }
        const author = document.createElement('div');
        author.className = 'aeternum-review-card__author';

        const authorName = String(review.firstName || review.first_name || '').trim();
        const avatar = document.createElement('span');
        avatar.className = 'aeternum-review-card__avatar';
        avatar.textContent = authorName
          .split(/\s+/)
          .map((part) => part.charAt(0))
          .join('')
          .slice(0, 2)
          .toUpperCase() || 'A';
        avatar.setAttribute('aria-hidden', 'true');

        const authorCopy = document.createElement('div');
        const name = document.createElement('strong');
        name.textContent = authorName || '—';
        authorCopy.append(name);

        if (review.verified || review.verifyBuyer) {
          const verified = document.createElement('span');
          verified.className = 'aeternum-review-card__verified';
          verified.append(createVerifiedIcon(), document.createTextNode(labels.verified));
          authorCopy.append(verified);
        }

        author.append(avatar, authorCopy);
        card.append(author);
        card.append(top);

        const content = String(review.content || '').trim();
        if (content) {
          const contentWrap = document.createElement('div');
          contentWrap.className = 'aeternum-review-card__content';
          const paragraph = document.createElement('p');
          const collapsedContent = content.length > 240 ? `${content.slice(0, 237).trim()}…` : content;
          paragraph.textContent = collapsedContent;
          contentWrap.append(paragraph);

          if (content.length > 240) {
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'aeternum-review-card__toggle';
            toggle.textContent = labels.readMore;
            toggle.setAttribute('aria-expanded', 'false');
            toggle.addEventListener('click', () => {
              const expanded = toggle.getAttribute('aria-expanded') === 'true';
              toggle.setAttribute('aria-expanded', String(!expanded));
              toggle.textContent = expanded ? labels.readMore : labels.readLess;
              paragraph.textContent = expanded ? collapsedContent : content;
            });
            contentWrap.append(toggle);
          }

          card.append(contentWrap);
        }

        const reviewImage = Array.isArray(review.images) ? review.images[0] : '';
        const imageSource = typeof reviewImage === 'string' ? reviewImage : reviewImage?.url;
        const safeReviewImage = safeUrl(imageSource);
        if (safeReviewImage) {
          const media = document.createElement('img');
          media.className = 'aeternum-review-card__media';
          media.src = safeReviewImage;
          media.alt = '';
          media.loading = 'lazy';
          media.decoding = 'async';
          card.append(media);
        }

        const product = review.product || review.products?.[0];
        if (product?.title) {
          const productLink = document.createElement('a');
          productLink.className = 'aeternum-review-card__product';
          let safeProductUrl = safeUrl(product.productLink || (product.handle ? `/products/${product.handle}` : ''));
          if (isPreview && safeProductUrl) {
            const productUrl = new URL(safeProductUrl);
            const productPath = productUrl.pathname.match(/\/products\/[^/]+$/);
            if (productUrl.origin === window.location.origin && productPath) {
              productUrl.pathname = `${window.Shopify?.routes?.root || '/'}${productPath[0].slice(1)}`;
              safeProductUrl = productUrl.href;
            }
          }
          if (safeProductUrl) productLink.href = safeProductUrl;

          const safeProductImage = safeUrl(product.image);
          if (safeProductImage) {
            const productImage = document.createElement('img');
            productImage.src = safeProductImage;
            productImage.alt = '';
            productImage.loading = 'lazy';
            productImage.decoding = 'async';
            productLink.append(productImage);
          }

          const productTitle = document.createElement('span');
          productTitle.textContent = product.title;
          productLink.append(productTitle);
          card.append(productLink);
        }

        return card;
      };

      const renderReviews = (newReviews, { append = false } = {}) => {
        if (!append) list.replaceChildren();
        const fragment = document.createDocumentFragment();
        newReviews.forEach((review) => fragment.append(createReviewCard(review)));
        list.append(fragment);
      };

      const loadReviews = async ({ append = false } = {}) => {
        if (loading || (append && !hasNext)) return;

        loading = true;
        requestController?.abort();
        requestController = new AbortController();
        setStatus(append ? '' : labels.loading, { isLoading: !append });
        updateControls();

        const params = new URLSearchParams({
          shopId,
          from: 'all_reviews_block_widget',
          limitPerPage: String(pageSize),
          sort: sortSelect?.value || 'createdAt_desc',
          minimumRating: 'all-reviews'
        });

        if (append && cursor) params.set('after', cursor);
        if (selectedRating) params.set('rateRangeFilter', selectedRating);

        try {
          const response = await fetch(`${endpoint}?${params.toString()}`, {
            signal: requestController.signal,
            headers: { Accept: 'application/json' }
          });

          if (!response.ok) throw new Error(`Air Reviews request failed: ${response.status}`);

          const payload = await response.json();
          const receivedReviews = Array.isArray(payload?.data?.reviews) ? payload.data.reviews : [];
          const uniqueReviews = receivedReviews.filter((review) => {
            if (!review?.id || seenReviewIds.has(review.id)) return false;
            seenReviewIds.add(review.id);
            return true;
          });

          reviews = append ? [...reviews, ...uniqueReviews] : uniqueReviews;
          cursor = receivedReviews.at(-1)?.id || cursor;
          hasNext = !isPreview && payload?.data?.hasNext === true && receivedReviews.length > 0;
          renderReviews(uniqueReviews, { append });

          if (reviews.length === 0) setStatus(labels.empty);
          else setStatus();
          updateControls();
        } catch (error) {
          if (error.name === 'AbortError') return;
          setStatus(labels.error, { isError: true });
          updateControls({ retry: true });
        } finally {
          loading = false;
          updateControls({ retry: status.classList.contains('is-error') });
        }
      };

      const resetAndLoad = () => {
        requestController?.abort();
        loading = false;
        reviews = [];
        cursor = '';
        hasNext = true;
        seenReviewIds.clear();
        list.replaceChildren();
        loadReviews();
      };

      sortSelect?.addEventListener('change', resetAndLoad);
      ratingButtons.forEach((button) => {
        button.addEventListener('click', () => {
          selectedRating = button.dataset.rating || '';
          ratingButtons.forEach((candidate) => {
            const active = candidate === button;
            candidate.classList.toggle('is-active', active);
            candidate.setAttribute('aria-pressed', String(active));
          });
          resetAndLoad();
        });
      });
      loadMore.addEventListener('click', () => loadReviews({ append: !isPreview && reviews.length > 0 }));

      page.dataset.aeternumAllReviewsBound = 'true';
      loadReviews();
    });
  };

  const bindTarotGuides = (root = document) => {
    const guides = [];

    if (root instanceof HTMLElement && root.matches('[data-tarot-guide]')) {
      guides.push(root);
    }

    root.querySelectorAll?.('[data-tarot-guide]').forEach((guide) => guides.push(guide));

    guides.forEach((guide) => {
      if (guide.dataset.aeternumTarotBound === 'true') return;

      const search = guide.querySelector('[data-tarot-search]');
      const clear = guide.querySelector('[data-tarot-clear]');
      const count = guide.querySelector('[data-tarot-count]');
      const empty = guide.querySelector('[data-tarot-empty]');
      const cards = Array.from(guide.querySelectorAll('[data-tarot-card]'));
      const filterLinks = Array.from(guide.querySelectorAll('[data-tarot-filter-link]'));
      const currentTag = guide.dataset.currentTag || '';
      let selectedFilter = currentTag || 'all';

      const randomizeSpread = () => {
        const spreadCards = Array.from(guide.querySelectorAll('[data-tarot-spread-card]'));
        const candidates = cards.reduce((items, card) => {
          const image = card.querySelector('.tec-tarot-card__media img');
          const title = card.querySelector('h3');

          if (!image || !title) return items;

          items.push({
            alt: image.getAttribute('alt') || title.textContent.trim(),
            sizes: image.getAttribute('sizes') || '',
            src: image.getAttribute('src') || '',
            srcset: image.getAttribute('srcset') || '',
            title: title.textContent.trim()
          });
          return items;
        }, []);

        for (let index = candidates.length - 1; index > 0; index -= 1) {
          const randomIndex = Math.floor(Math.random() * (index + 1));
          [candidates[index], candidates[randomIndex]] = [candidates[randomIndex], candidates[index]];
        }

        spreadCards.forEach((spreadCard, index) => {
          const candidate = candidates[index];
          if (!candidate) return;

          let image = spreadCard.querySelector('img');
          const placeholder = spreadCard.querySelector('.tec-tarot-guide__spread-placeholder');
          const title = spreadCard.querySelector('.tec-tarot-guide__spread-title');

          if (!image) {
            image = document.createElement('img');
            image.loading = 'eager';
            placeholder?.replaceWith(image);
          }

          image.alt = candidate.alt;
          image.src = candidate.src;
          image.srcset = candidate.srcset;
          image.sizes = candidate.sizes;
          if (title) title.textContent = candidate.title;
          spreadCard.dataset.tarotSpreadSelection = candidate.title;
        });
      };

      randomizeSpread();

      if (!search || !clear || !count || cards.length === 0) return;

      const normalize = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(document.documentElement.lang || undefined)
        .trim();

      const updateActiveFilters = () => {
        filterLinks.forEach((link) => {
          const active = link.dataset.tarotFilterLink === selectedFilter;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
      };

      const updateResults = () => {
        const query = normalize(search.value);
        let visibleCount = 0;

        cards.forEach((card) => {
          const tags = card.dataset.tags || '';
          const searchable = normalize(card.dataset.search);
          const matchesFilter = selectedFilter === 'all' || tags.includes(`|${selectedFilter}|`);
          const matchesSearch = query === '' || searchable.includes(query);
          const visible = matchesFilter && matchesSearch;

          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });

        count.textContent = String(visibleCount);
        clear.hidden = query === '';
        if (empty) empty.hidden = visibleCount !== 0;
      };

      search.addEventListener('input', updateResults);
      search.addEventListener('search', updateResults);
      clear.addEventListener('click', () => {
        search.value = '';
        search.focus();
        updateResults();
      });

      filterLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (currentTag !== '') return;

          event.preventDefault();
          selectedFilter = link.dataset.tarotFilterLink || 'all';
          updateActiveFilters();
          updateResults();
        });
      });

      guide.dataset.aeternumTarotBound = 'true';
      updateActiveFilters();
      updateResults();
    });
  };

  const bindTarotSpreadTools = (root = document) => {
    const tools = [];

    if (root instanceof HTMLElement && root.matches('[data-tarot-spread-tool]')) {
      tools.push(root);
    }

    root.querySelectorAll?.('[data-tarot-spread-tool]').forEach((tool) => tools.push(tool));

    tools.forEach((tool) => {
      if (tool.dataset.aeternumTarotSpreadBound === 'true') return;

      const dataNode = tool.querySelector('[data-tarot-spread-data]');
      const stage = tool.querySelector('[data-spread-stage]');
      const tabs = Array.from(tool.querySelectorAll('[data-spread-select]'));
      const cards = Array.from(tool.querySelectorAll('[data-spread-card]'));
      const title = tool.querySelector('[data-spread-title]');
      const kicker = tool.querySelector('[data-spread-kicker]');
      const description = tool.querySelector('[data-spread-description]');
      const dealButton = tool.querySelector('[data-spread-deal]');
      const dealLabel = tool.querySelector('[data-spread-deal-label]');
      const positionList = tool.querySelector('[data-spread-position-list]');
      const status = tool.querySelector('[data-spread-status]');

      if (!dataNode || !stage || !dealButton || !positionList) return;

      let data;
      try {
        data = JSON.parse(dataNode.textContent);
      } catch (error) {
        return;
      }

      let currentKey = 'one';
      let dealTimer = null;
      let dealRun = 0;

      const renderPositionList = (spread) => {
        positionList.replaceChildren();
        positionList.dataset.count = String(spread.positions.length);

        spread.positions.forEach((position, index) => {
          const item = document.createElement('li');
          const marker = document.createElement('span');
          const number = document.createElement('b');
          const copy = document.createElement('span');
          const itemTitle = document.createElement('strong');
          const itemText = document.createElement('span');

          marker.className = 'tec-tarot-home__position-marker';
          copy.className = 'tec-tarot-home__position-copy';
          number.textContent = String(index + 1);
          itemTitle.textContent = position.title;
          itemText.textContent = position.text;
          marker.append(number);
          copy.append(itemTitle, itemText);
          item.append(marker, copy);
          positionList.append(item);
        });
      };

      const prepareCards = (spread) => {
        cards.forEach((card, index) => {
          const position = spread.positions[index];
          const positionNode = card.querySelector('[data-card-position]');

          card.classList.remove('is-revealed');
          card.setAttribute('aria-pressed', 'false');
          card.style.setProperty('--deal-order', String(index));
          card.hidden = !position;

          if (!position) return;

          if (positionNode) positionNode.textContent = position.title;
          card.setAttribute('aria-label', `${data.positionLabel} ${index + 1}: ${position.title}`);
        });
      };

      const deal = () => {
        const spread = data.spreads[currentKey];
        if (!spread) return;

        if (dealTimer !== null) window.clearTimeout(dealTimer);
        const currentDealRun = ++dealRun;
        stage.classList.add('is-resetting');
        stage.classList.remove('is-dealt');
        cards.forEach((card) => {
          card.classList.remove('is-revealed');
          card.setAttribute('aria-pressed', 'false');
        });

        void stage.offsetWidth;
        dealTimer = window.setTimeout(() => {
          if (currentDealRun !== dealRun) return;

          stage.classList.remove('is-resetting');
          void stage.offsetWidth;
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              if (currentDealRun !== dealRun) return;
              stage.classList.add('is-dealt');
              dealTimer = null;
            });
          });
        }, 420);

        if (dealLabel) dealLabel.textContent = data.redeal;
        if (status) {
          status.textContent = String(data.dealtStatus || '')
            .replace('__COUNT__', String(spread.positions.length))
            .replace('__SPREAD__', spread.title);
        }
      };

      const selectSpread = (key) => {
        const spread = data.spreads[key];
        if (!spread) return;

        currentKey = key;
        stage.dataset.layout = key;
        if (title) title.textContent = spread.title;
        if (kicker) kicker.textContent = spread.meta;
        if (description) description.textContent = spread.description;
        if (dealLabel) dealLabel.textContent = data.deal;

        tabs.forEach((tab) => {
          const active = tab.dataset.spreadSelect === key;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        prepareCards(spread);
        renderPositionList(spread);
        deal();
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => selectSpread(tab.dataset.spreadSelect || 'one'));
      });

      cards.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.hidden) return;
          const revealed = card.classList.toggle('is-revealed');
          card.setAttribute('aria-pressed', revealed ? 'true' : 'false');
        });
      });

      dealButton.addEventListener('click', deal);
      tool.dataset.aeternumTarotSpreadBound = 'true';
      selectSpread('one');
    });
  };

  const bindWaxReaders = (scope = document) => {
    const readers = [];

    if (scope instanceof Element && scope.matches('[data-wax-reader]')) {
      readers.push(scope);
    }
    scope.querySelectorAll?.('[data-wax-reader]').forEach((reader) => readers.push(reader));

    readers.forEach((reader) => {
      if (reader.dataset.waxReaderBound === 'true') return;

      const dataNode = reader.querySelector('[data-wax-reader-data]');
      if (!dataNode) return;

      reader.querySelector('[data-wax-start]')?.addEventListener('click', (event) => {
        const target = reader.querySelector('.tec-wax-reader__workspace');
        if (!target) return;
        event.preventDefault();
        target.focus({ preventScroll: true });
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });

      let data;
      try {
        data = JSON.parse(dataNode.textContent || '{}');
      } catch (error) {
        console.error('Aeternum wax reader data could not be parsed.', error);
        return;
      }

      const formButtons = Array.from(reader.querySelectorAll('[data-wax-form]'));
      const selectedContainer = reader.querySelector('[data-wax-selected]');
      const dominantControl = reader.querySelector('[data-wax-dominant-control]');
      const dominantSelect = reader.querySelector('[data-wax-dominant]');
      const counter = reader.querySelector('[data-wax-counter]');
      const resetButton = reader.querySelector('[data-wax-reset]');
      const errorMessage = reader.querySelector('[data-wax-error]');
      const readingTitle = reader.querySelector('[data-wax-reading-title]');
      const readingSummary = reader.querySelector('[data-wax-reading-summary]');
      const readingParts = reader.querySelector('[data-wax-reading-parts]');
      const parameterReading = reader.querySelector('[data-wax-parameter-reading]');
      const flameReading = reader.querySelector('[data-wax-flame-reading]');
      const takeaway = reader.querySelector('[data-wax-takeaway]');

      if (!selectedContainer || !dominantControl || !dominantSelect || !counter || !resetButton || !readingTitle || !readingSummary || !readingParts || !parameterReading || !flameReading || !takeaway) {
        return;
      }

      const state = {
        selected: String(reader.dataset.defaultSelection || '')
          .split(',')
          .map((item) => item.trim())
          .filter((item) => Boolean(data.forms?.[item]))
          .slice(0, 3),
        relation: 'linked',
        aspect: 'clear',
        flame: 'completed'
      };

      const replaceToken = (value, token, replacement) => String(value || '').replace(token, replacement);
      const pairKey = (ids) => ids.slice(0, 2).sort().join('|');

      const cloneIcon = (id, className) => {
        const source = reader.querySelector(`[data-wax-form="${CSS.escape(id)}"] .aeternum-wax-icon`);
        if (!source) return null;
        const wrapper = document.createElement('span');
        if (className) wrapper.className = className;
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.append(source.cloneNode(true));
        return wrapper;
      };

      const renderSelected = () => {
        selectedContainer.replaceChildren();
        dominantSelect.replaceChildren();

        if (state.selected.length === 0) {
          const empty = document.createElement('span');
          empty.className = 'tec-wax-reader__selected-empty';
          empty.textContent = data.strings.emptySelection;
          selectedContainer.append(empty);
        } else {
          state.selected.forEach((id, index) => {
            const form = data.forms[id];
            const item = document.createElement('span');
            item.className = 'tec-wax-reader__selected-item';

            const order = document.createElement('span');
            order.className = 'tec-wax-reader__selected-order';
            order.textContent = String(index + 1);
            order.setAttribute('aria-hidden', 'true');
            item.append(order);

            const icon = cloneIcon(id, 'tec-wax-reader__selected-icon');
            if (icon) item.append(icon);

            const label = document.createElement('span');
            const role = index === 0
              ? data.strings.dominant
              : replaceToken(data.strings.nuance, '__NUMBER__', String(index));
            label.textContent = `${form.label} · ${role}`;
            item.append(label);
            selectedContainer.append(item);
          });
        }

        if (state.selected.length > 1) {
          state.selected.forEach((id) => {
            const option = document.createElement('option');
            option.value = id;
            option.textContent = data.forms[id].label;
            dominantSelect.append(option);
          });
          dominantSelect.value = state.selected[0];
          dominantControl.hidden = false;
        } else {
          dominantControl.hidden = true;
        }

        counter.textContent = replaceToken(data.strings.counter, '__COUNT__', String(state.selected.length));
      };

      const createReadingPart = (title, copy, id) => {
        const section = document.createElement('section');
        section.className = 'tec-wax-reader__reading-part';

        const heading = document.createElement('p');
        heading.className = 'tec-wax-reader__reading-part-title';
        if (id) {
          const icon = cloneIcon(id);
          if (icon) heading.append(icon);
        }
        heading.append(document.createTextNode(title));

        const paragraph = document.createElement('p');
        paragraph.textContent = copy;
        section.append(heading, paragraph);
        return section;
      };

      const buildReading = () => {
        const ids = state.selected;
        if (ids.length === 0) {
          return {
            title: data.strings.emptyTitle,
            summary: data.strings.emptySummary,
            parts: [],
            mix: data.strings.emptyOverview,
            takeaway: data.strings.emptyOverview
          };
        }

        if (ids[0] === 'unknown') {
          const form = data.forms.unknown;
          return {
            title: data.strings.unknownTitle,
            summary: form.meaning,
            parts: [{ id: 'unknown', title: `${form.label} · ${data.strings.dominant}`, copy: form.meaning }],
            mix: form.action,
            takeaway: form.action
          };
        }

        const forms = ids.map((id) => data.forms[id]).filter(Boolean);
        const pair = ids.length > 1 ? data.pairs?.[pairKey(ids)] : null;
        const parts = ids.map((id, index) => ({
          id,
          title: `${data.forms[id].label} · ${index === 0 ? data.strings.dominant : replaceToken(data.strings.nuance, '__NUMBER__', String(index))}`,
          copy: data.forms[id].meaning
        }));

        if (pair) {
          return {
            title: pair.title,
            summary: pair.summary,
            parts,
            mix: pair.mix,
            takeaway: forms.map((form) => form.action).join(' ')
          };
        }

        if (forms.length === 1) {
          return {
            title: forms[0].label,
            summary: forms[0].meaning,
            parts,
            mix: forms[0].action,
            takeaway: forms[0].action
          };
        }

        return {
          title: forms.map((form) => form.label).join(' · '),
          summary: data.strings.combinedSummary,
          parts,
          mix: `${data.strings.mixIntro} ${forms.map((form) => form.action).join(' ')}`,
          takeaway: forms.map((form) => form.action).join(' ')
        };
      };

      const renderReading = () => {
        const reading = buildReading();
        readingTitle.textContent = reading.title;
        readingSummary.textContent = reading.summary;
        readingParts.replaceChildren();
        reading.parts.forEach((part) => {
          readingParts.append(createReadingPart(part.title, part.copy, part.id));
        });
        readingParts.append(createReadingPart(data.strings.readingOverview, reading.mix));
        parameterReading.textContent = `${data.relationReadings[state.relation]} ${data.aspectReadings[state.aspect]}`;
        flameReading.textContent = data.flameReadings[state.flame];
        takeaway.textContent = reading.takeaway;
      };

      const renderButtons = () => {
        formButtons.forEach((button) => {
          const id = button.dataset.waxForm;
          const selectedIndex = state.selected.indexOf(id);
          const selected = selectedIndex !== -1;
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          button.disabled = !selected && state.selected.length >= 3;
          const order = button.querySelector('[data-wax-order]');
          if (order) order.textContent = selected ? String(selectedIndex + 1) : '';
        });
      };

      const renderChoiceButtons = () => {
        reader.querySelectorAll('[data-wax-choice-group]').forEach((group) => {
          const selectedValue = state[group.dataset.waxChoiceGroup];
          group.querySelectorAll('button[data-value]').forEach((button) => {
            button.setAttribute('aria-pressed', button.dataset.value === selectedValue ? 'true' : 'false');
          });
        });
      };

      const render = () => {
        renderButtons();
        renderSelected();
        renderChoiceButtons();
        renderReading();
        resetButton.hidden = state.selected.length === 0
          && state.relation === 'linked'
          && state.aspect === 'clear'
          && state.flame === 'completed';
      };

      formButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.dataset.waxForm;
          const currentIndex = state.selected.indexOf(id);
          if (errorMessage) errorMessage.textContent = '';

          if (currentIndex !== -1) {
            state.selected.splice(currentIndex, 1);
          } else if (state.selected.length >= 3) {
            if (errorMessage) errorMessage.textContent = data.strings.maxError;
            return;
          } else if (id === 'unknown') {
            state.selected = ['unknown'];
          } else {
            state.selected = state.selected.filter((selectedId) => selectedId !== 'unknown');
            state.selected.push(id);
          }

          render();
        });
      });

      reader.querySelectorAll('[data-wax-choice-group]').forEach((group) => {
        const buttons = Array.from(group.querySelectorAll('button[data-value]'));
        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            state[group.dataset.waxChoiceGroup] = button.dataset.value;
            render();
          });
        });
      });

      dominantSelect.addEventListener('change', () => {
        const selectedIndex = state.selected.indexOf(dominantSelect.value);
        if (selectedIndex <= 0) return;
        const [dominantId] = state.selected.splice(selectedIndex, 1);
        state.selected.unshift(dominantId);
        render();
      });

      resetButton.addEventListener('click', () => {
        state.selected = [];
        state.relation = 'linked';
        state.aspect = 'clear';
        state.flame = 'completed';
        if (errorMessage) errorMessage.textContent = '';
        render();
      });

      reader.dataset.waxReaderBound = 'true';
      render();
    });
  };

  const bindRunesHomes = (scope = document) => {
    const homes = [];
    if (scope instanceof Element && scope.matches('[data-runes-home]')) {
      homes.push(scope);
    }
    homes.push(...scope.querySelectorAll('[data-runes-home]'));

    homes.forEach((home) => {
      if (home.dataset.runesHomeBound === 'true') return;

      const cards = Array.from(home.querySelectorAll('[data-rune-card]'));
      const groups = Array.from(home.querySelectorAll('[data-rune-group]'));
      const filters = Array.from(home.querySelectorAll('[data-rune-filter]'));
      const search = home.querySelector('[data-rune-search]');
      const searchClear = home.querySelector('[data-rune-search-clear]');
      const empty = home.querySelector('[data-rune-empty]');
      const drawButton = home.querySelector('[data-rune-draw]');
      const drawPlaceholder = home.querySelector('[data-rune-draw-placeholder]');
      const drawResult = home.querySelector('[data-rune-draw-result]');
      const drawSymbol = home.querySelector('[data-rune-draw-symbol]');
      const drawName = home.querySelector('[data-rune-draw-name]');
      const drawLink = home.querySelector('[data-rune-draw-link]');
      let activeAett = 'all';

      const normalize = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

      const updateDirectory = () => {
        const query = normalize(search?.value);
        let visibleCount = 0;

        cards.forEach((card) => {
          const matchesAett = activeAett === 'all' || card.dataset.runeAett === activeAett;
          const matchesSearch = query === '' || normalize(card.dataset.runeSearchText).includes(query);
          const visible = matchesAett && matchesSearch;
          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });

        groups.forEach((group) => {
          group.hidden = !Array.from(group.querySelectorAll('[data-rune-card]')).some((card) => !card.hidden);
        });

        if (searchClear) searchClear.hidden = query === '';
        if (empty) empty.hidden = visibleCount !== 0;
      };

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          activeAett = filter.dataset.runeFilter || 'all';
          filters.forEach((button) => {
            const isActive = button === filter;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
          });
          updateDirectory();
        });
      });

      search?.addEventListener('input', updateDirectory);
      searchClear?.addEventListener('click', () => {
        search.value = '';
        search.focus();
        updateDirectory();
      });

      drawButton?.addEventListener('click', () => {
        const visibleCards = cards.filter((card) => !card.hidden);
        const pool = visibleCards.length > 0 ? visibleCards : cards;
        if (pool.length === 0 || !drawResult) return;

        const card = pool[Math.floor(Math.random() * pool.length)];
        if (drawSymbol) drawSymbol.textContent = card.dataset.runeSymbol || '';
        if (drawName) drawName.textContent = card.dataset.runeName || '';
        if (drawLink) drawLink.href = card.href;
        if (drawPlaceholder) drawPlaceholder.hidden = true;

        drawResult.hidden = false;
        drawResult.classList.remove('is-revealed');
        window.requestAnimationFrame(() => drawResult.classList.add('is-revealed'));
      });

      home.dataset.runesHomeBound = 'true';
      updateDirectory();
    });
  };

  const bindLunarCalendars = (scope = document) => {
    const calendars = [];
    if (scope instanceof Element && scope.matches('[data-lunar-calendar]')) calendars.push(scope);
    calendars.push(...scope.querySelectorAll('[data-lunar-calendar]'));

    calendars.forEach((calendar) => {
      if (calendar.dataset.lunarCalendarBound === 'true') return;

      const configNode = calendar.querySelector('[data-lunar-config]');
      const form = calendar.querySelector('[data-lunar-form]');
      const yearInput = calendar.querySelector('[data-lunar-year]');
      const monthSelect = calendar.querySelector('[data-lunar-month-select]');
      const status = calendar.querySelector('[data-lunar-status]');
      const nextPanel = calendar.querySelector('[data-lunar-next]');
      const grid = calendar.querySelector('[data-lunar-grid]');
      if (!configNode || !form || !yearInput || !monthSelect || !status || !nextPanel || !grid) return;

      const phaseTabs = Array.from(calendar.querySelectorAll('[data-lunar-phase-tab]'));
      const phasePanels = Array.from(calendar.querySelectorAll('[data-lunar-phase-panel]'));

      let config;
      try {
        config = JSON.parse(configNode.textContent);
      } catch (error) {
        return;
      }

      const API_BASE = 'https://aa.usno.navy.mil/api/moon/phases/year?ID=Aeternum&year=';
      const MIN_YEAR = 1700;
      const MAX_YEAR = 2100;
      const currentYear = new Date().getFullYear();
      const locale = config.locale || document.documentElement.lang || 'fr';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const labels = config.labels;
      const phaseDefinitions = config.phases;
      let activeYear = currentYear;
      let activeMonthIndex = new Date().getMonth();

      const escapeHtml = (value) => String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');

      const replaceTokens = (value, tokens) => Object.entries(tokens).reduce(
        (text, [key, replacement]) => text.replaceAll(`__${key}__`, String(replacement)),
        String(value || '')
      );

      const phaseDate = (item) => {
        const timeParts = String(item.time || '00:00').split(':');
        return new Date(Date.UTC(Number(item.year), Number(item.month) - 1, Number(item.day), Number(timeParts[0]) || 0, Number(timeParts[1]) || 0, Number(timeParts[2]) || 0));
      };

      const formatMoment = (date) => ({
        localDate: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone }).format(date),
        localClock: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(date),
        utcClock: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(date)
      });

      const monthName = (index) => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, index, 1)));
      const monthId = (year, index) => `lunar-${calendar.dataset.sectionId}-${year}-${index + 1}`;
      const populateMonthSelect = (year) => {
        const currentMonthIndex = year === currentYear ? new Date().getMonth() : -1;
        monthSelect.innerHTML = Array.from({ length: 12 }, (_, index) => {
          const suffix = index === currentMonthIndex ? ` · ${labels.currentMonth}` : '';
          return `<option value="${index}">${escapeHtml(monthName(index))}${escapeHtml(suffix)}</option>`;
        }).join('');
      };

      const createNextPanel = (year, phaseData) => {
        const now = new Date();
        const sorted = phaseData.filter((item) => phaseDefinitions[item.phase]).sort((first, second) => phaseDate(first) - phaseDate(second));
        let next = null;
        if (year > currentYear) next = sorted[0] || null;
        if (year === currentYear) next = sorted.find((item) => phaseDate(item) >= now) || null;
        if (!next) return `<article class="tec-lunar__next-card tec-lunar__next-card--empty"><div class="tec-lunar__next-date-block"><strong>${escapeHtml(labels.nextDateLabel)}</strong><p>${escapeHtml(labels.nextNoneBody)}</p></div><div class="tec-lunar__next-phase-block"><strong>${escapeHtml(labels.nextPhaseLabel)}</strong><div class="tec-lunar__next-phase"><span class="tec-lunar__disc tec-lunar__next-disc tec-lunar__disc--new_moon" aria-hidden="true"></span><h2>${escapeHtml(labels.nextNone)}</h2></div></div></article>`;

        const definition = phaseDefinitions[next.phase];
        const date = phaseDate(next);
        const moment = formatMoment(date);
        return `<article class="tec-lunar__next-card">
          <div class="tec-lunar__next-date-block"><strong>${escapeHtml(labels.nextDateLabel)}</strong><time datetime="${date.toISOString()}">${escapeHtml(moment.localDate)} · ${escapeHtml(moment.localClock)}</time><small>${escapeHtml(timeZone)}</small></div>
          <div class="tec-lunar__next-phase-block"><strong>${escapeHtml(labels.nextPhaseLabel)}</strong><div class="tec-lunar__next-phase"><span class="tec-lunar__disc tec-lunar__next-disc tec-lunar__disc--${escapeHtml(definition.key)}" aria-hidden="true"></span><h2>${escapeHtml(definition.name)}</h2></div></div>
          <div class="tec-lunar__next-reading"><strong>${escapeHtml(labels.symbolicReading)}</strong><p>${escapeHtml(definition.card)}</p></div>
        </article>`;
      };

      const createPhaseCard = (item) => {
        const definition = phaseDefinitions[item.phase];
        if (!definition) return '';
        const date = phaseDate(item);
        const moment = formatMoment(date);
        return `<article class="tec-lunar__card" data-lunar-phase="${escapeHtml(item.phase)}">
          <span class="tec-lunar__disc tec-lunar__disc--${escapeHtml(definition.key)}" aria-hidden="true"></span>
          <div class="tec-lunar__card-phase"><h4>${escapeHtml(definition.name)}</h4><span class="tec-lunar__card-keyword">${escapeHtml(definition.keyword)}</span></div>
          <p class="tec-lunar__card-date"><strong>${escapeHtml(labels.exactInstant)}</strong><time datetime="${date.toISOString()}">${escapeHtml(moment.localDate)} · ${escapeHtml(moment.localClock)}</time><small>${escapeHtml(labels.localTime)} · ${escapeHtml(timeZone)} · ${escapeHtml(labels.utcTime)} ${escapeHtml(moment.utcClock)}</small></p>
          <p class="tec-lunar__card-reading"><strong>${escapeHtml(labels.symbolicReading)}</strong><span>${escapeHtml(definition.card)}</span></p>
        </article>`;
      };

      const showMonth = (monthIndex) => {
        activeMonthIndex = Math.min(11, Math.max(0, Number(monthIndex) || 0));
        monthSelect.value = String(activeMonthIndex);
        calendar.querySelectorAll('[data-lunar-month]').forEach((month) => {
          month.hidden = Number(month.dataset.lunarMonth) !== activeMonthIndex;
        });
      };

      const renderCalendar = (year, phaseData) => {
        const currentMonthIndex = year === currentYear ? new Date().getMonth() : -1;
        const grouped = phaseData.reduce((map, item) => {
          if (!phaseDefinitions[item.phase]) return map;
          const month = Number(item.month);
          if (!map.has(month)) map.set(month, []);
          map.get(month).push(item);
          return map;
        }, new Map());

        populateMonthSelect(year);

        grid.innerHTML = Array.from({ length: 12 }, (_, index) => {
          const cards = (grouped.get(index + 1) || []).map(createPhaseCard).join('');
          const badge = index === currentMonthIndex ? `<span>${escapeHtml(labels.currentMonth)}</span>` : '';
          return `<section class="tec-lunar__month${index === currentMonthIndex ? ' is-current' : ''}" id="${monthId(year, index)}" data-lunar-month="${index}"${index === activeMonthIndex ? '' : ' hidden'}><div class="tec-lunar__month-heading"><h3>${escapeHtml(monthName(index))}</h3>${badge}</div><div class="tec-lunar__cards">${cards || `<p class="tec-lunar__empty">${escapeHtml(labels.noPhase)}</p>`}</div></section>`;
        }).join('');
        nextPanel.innerHTML = createNextPanel(year, phaseData);
        showMonth(activeMonthIndex);
      };

      const skeleton = () => Array.from({ length: 4 }, () => '<div class="tec-lunar__card tec-lunar__card--skeleton" aria-hidden="true"><span></span><div><span></span><span></span><span></span></div></div>').join('');
      const loadYear = async (year) => {
        if (!Number.isInteger(year) || year < MIN_YEAR || year > MAX_YEAR) {
          status.textContent = replaceTokens(labels.invalidYear, { MIN: MIN_YEAR, MAX: MAX_YEAR });
          return;
        }
        activeYear = year;
        yearInput.value = String(year);
        populateMonthSelect(year);
        const hasStaticFallback = grid.dataset.lunarStaticYear === String(year) && grid.querySelector('[data-lunar-month]');
        if (!hasStaticFallback) {
          grid.innerHTML = skeleton();
          grid.setAttribute('aria-busy', 'true');
        }
        status.textContent = replaceTokens(labels.loading, { YEAR: year });
        try {
          const response = await fetch(API_BASE + encodeURIComponent(year), { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          if (data.error || !Array.isArray(data.phasedata)) throw new Error(data.error || 'Invalid response');
          renderCalendar(year, data.phasedata);
          delete grid.dataset.lunarStaticYear;
          status.textContent = replaceTokens(labels.loaded, { YEAR: year, ZONE: timeZone });
        } catch (error) {
          nextPanel.innerHTML = '';
          if (hasStaticFallback) {
            showMonth(activeMonthIndex);
            status.textContent = labels.staticFallback;
          } else {
            grid.innerHTML = `<div class="tec-lunar__error" role="alert"><h3>${escapeHtml(labels.errorTitle)}</h3><p>${escapeHtml(labels.errorBody)}</p><button class="button tec-lunar__button" type="button" data-lunar-retry>${escapeHtml(labels.retry)}</button></div>`;
            status.textContent = '';
          }
        } finally {
          grid.setAttribute('aria-busy', 'false');
        }
      };

      form.addEventListener('submit', (event) => { event.preventDefault(); loadYear(Number(yearInput.value)); });
      calendar.querySelectorAll('[data-lunar-year-step]').forEach((button) => button.addEventListener('click', () => loadYear(activeYear + Number(button.dataset.lunarYearStep))));
      calendar.querySelector('[data-lunar-current-year]')?.addEventListener('click', () => {
        activeMonthIndex = new Date().getMonth();
        loadYear(currentYear);
      });
      calendar.addEventListener('click', (event) => {
        if (event.target.closest('[data-lunar-retry]')) loadYear(activeYear);
      });
      monthSelect.addEventListener('change', () => showMonth(monthSelect.value));

      const selectPhase = (selectedTab, moveFocus = false) => {
        const phaseKey = selectedTab.dataset.lunarPhaseTab;
        phaseTabs.forEach((tab) => {
          const selected = tab === selectedTab;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        phasePanels.forEach((panel) => {
          panel.hidden = panel.dataset.lunarPhasePanel !== phaseKey;
        });
        selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        if (moveFocus) selectedTab.focus();
      };

      phaseTabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectPhase(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = phaseTabs.length - 1;
          else if (event.key === 'ArrowRight') nextIndex = (index + 1) % phaseTabs.length;
          else nextIndex = (index - 1 + phaseTabs.length) % phaseTabs.length;
          selectPhase(phaseTabs[nextIndex], true);
        });
      });

      calendar.dataset.lunarCalendarBound = 'true';
      loadYear(currentYear);
    });
  };

  const bindYearWheels = (scope = document) => {
    const wheels = [];
    if (scope instanceof Element && scope.matches('[data-year-wheel]')) {
      wheels.push(scope);
    }
    wheels.push(...scope.querySelectorAll('[data-year-wheel]'));

    wheels.forEach((wheel) => {
      if (wheel.dataset.yearWheelBound === 'true') return;

      const tabs = Array.from(wheel.querySelectorAll('[data-year-wheel-tab]'));
      const panels = Array.from(wheel.querySelectorAll('[data-year-wheel-panel]'));
      const nextSabbat = wheel.querySelector('[data-year-wheel-next]');

      if (nextSabbat) {
        const annualDates = [
          { key: 'imbolc', month: 1, day: 1 },
          { key: 'ostara', month: 2, day: 20 },
          { key: 'beltane', month: 4, day: 1 },
          { key: 'litha', month: 5, day: 21 },
          { key: 'lughnasadh', month: 7, day: 1 },
          { key: 'mabon', month: 8, day: 22 },
          { key: 'samhain', month: 9, day: 31 },
          { key: 'yule', month: 11, day: 21 }
        ];
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const candidates = [now.getFullYear(), now.getFullYear() + 1]
          .flatMap((year) => annualDates.map((sabbat) => ({
            ...sabbat,
            date: new Date(year, sabbat.month, sabbat.day)
          })));
        const upcoming = candidates.find((sabbat) => sabbat.date >= today);
        const matchingTab = upcoming && tabs.find((tab) => tab.dataset.yearWheelTab === upcoming.key);
        const nameTarget = nextSabbat.querySelector('[data-year-wheel-next-name]');
        const dateTarget = nextSabbat.querySelector('[data-year-wheel-next-date]');

        if (upcoming && matchingTab && nameTarget && dateTarget) {
          const language = document.documentElement.lang || 'fr';
          const sabbatName = matchingTab.querySelector('.tec-year-wheel__dial-name')?.textContent.trim();
          const dateFormatter = new Intl.DateTimeFormat(language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          });
          nameTarget.textContent = sabbatName;
          dateTarget.textContent = dateFormatter.format(upcoming.date);
          dateTarget.dateTime = [
            upcoming.date.getFullYear(),
            String(upcoming.date.getMonth() + 1).padStart(2, '0'),
            String(upcoming.date.getDate()).padStart(2, '0')
          ].join('-');
          nextSabbat.hidden = false;
        }
      }

      const selectTab = (selectedTab, { moveFocus = false } = {}) => {
        const selectedKey = selectedTab.dataset.yearWheelTab;

        tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          tab.classList.toggle('is-active', isSelected);
          tab.setAttribute('aria-selected', String(isSelected));
          tab.tabIndex = isSelected ? 0 : -1;
        });

        panels.forEach((panel) => {
          panel.hidden = panel.dataset.yearWheelPanel !== selectedKey;
        });

        if (moveFocus) selectedTab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => selectTab(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();

          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
          else nextIndex = (index - 1 + tabs.length) % tabs.length;

          selectTab(tabs[nextIndex], { moveFocus: true });
        });
      });

      wheel.dataset.yearWheelBound = 'true';
    });
  };

  // Editorial themes derived from the guide; local symbolic rules, never predictions.
  const tarotReadingThemes = {
    "607407997276": "action",
    "607420088668": "action",
    "607420711260": "action",
    "607420645724": "action",
    "607420612956": "action",
    "607420547420": "action",
    "607420416348": "action",
    "607420318044": "action",
    "607420219740": "action",
    "607421202780": "action",
    "607406096732": "action",
    "607407374684": "action",
    "607405113692": "action",
    "607404622172": "action",
    "607420121436": "reflection",
    "607421038940": "reflection",
    "607420809564": "reflection",
    "607421530460": "reflection",
    "607420940636": "reflection",
    "607416779100": "reflection",
    "607416680796": "reflection",
    "607406162268": "reflection",
    "607407407452": "reflection",
    "607407800668": "reflection",
    "607404949852": "reflection",
    "607420449116": "structure",
    "607420481884": "structure",
    "607420383580": "structure",
    "607421858140": "structure",
    "607421825372": "structure",
    "607421563228": "structure",
    "607421628764": "structure",
    "607421595996": "structure",
    "607421661532": "structure",
    "607421399388": "structure",
    "607421366620": "structure",
    "607421301084": "structure",
    "607421890908": "structure",
    "607407964508": "structure",
    "607405244764": "structure",
    "607405441372": "structure",
    "607421137244": "change",
    "607421006172": "change",
    "607421333852": "change",
    "607417205084": "change",
    "607407472988": "change",
    "607407341916": "change",
    "607407735132": "change",
    "607407931740": "change",
    "607406063964": "bond",
    "607420252508": "bond",
    "607417336156": "bond",
    "607419400540": "bond",
    "607417270620": "bond",
    "607416746332": "bond",
    "607416615260": "bond",
    "607416582492": "bond",
    "607416484188": "bond",
    "607419498844": "bond",
    "607419433308": "bond",
    "607419466076": "bond",
    "607407538524": "bond",
    "607421071708": "clarity",
    "607420285276": "clarity",
    "607421235548": "clarity",
    "607421268316": "clarity",
    "607421170012": "clarity",
    "607421104476": "clarity",
    "607420776796": "clarity",
    "607420973404": "clarity",
    "607406129500": "clarity",
    "607407571292": "clarity",
    "607407833436": "clarity",
    "607421464924": "care",
    "607421432156": "care",
    "607420842332": "care",
    "607416713564": "care",
    "607407767900": "care"
  };

  const buildTarotSynthesis = (entries, positions, topic, labels, profiles) => {
    const format = (key, values = {}) => String(labels[key] || '').replace(/__([A-Z_]+)__/g, (match, token) => values[token.toLowerCase()] ?? match);
    const themeName = (theme) => labels[`synthesis_theme_${theme}`];
    const advice = (theme) => labels[`synthesis_advice_${theme}`];
    if (entries.length < 2) return [];
    const cards = entries.map((entry, index) => ({...entry, theme: profiles[entry.card.id], position: positions[index], index}));
    if (cards.some((card) => !card.theme || !card.position)) return [format('synthesis_unavailable')];
    const weight = (card) => ['present', 'situation', 'obstacle', 'advice', 'self'].includes(card.position.key) ? 2 : 1;
    const groups = new Map();
    cards.forEach((card) => {
      if (!groups.has(card.theme)) groups.set(card.theme, {theme: card.theme, cards: [], score: 0});
      const group = groups.get(card.theme);
      group.cards.push(card);
      group.score += weight(card);
    });
    const ranked = [...groups.values()].sort((a, b) => b.score - a.score || b.cards.length - a.cards.length || a.cards[0].index - b.cards[0].index);
    const focus = ranked[0];
    const names = (items) => items.map((item) => item.card.title).join(' · ');
    const paragraphs = [];
    const available = (theme) => cards.filter((card) => card.theme === theme && !card.reversed && !['obstacle', 'hopes'].includes(card.position.key));
    const contrasts = [['action', 'reflection', 'pace'], ['structure', 'change', 'change'], ['bond', 'clarity', 'dialogue'], ['action', 'care', 'care']];
    const pairs = contrasts.map(([left, right, key]) => ({left: available(left), right: available(right), key})).filter((pair) => pair.left.length && pair.right.length);
    pairs.sort((a, b) => b.left.concat(b.right).reduce((score, card) => score + weight(card), 0) - a.left.concat(a.right).reduce((score, card) => score + weight(card), 0));
    if (pairs.length) {
      const pair = pairs[0];
      // Name the most relevant card on each side; all cards still contribute to pair selection.
      const representative = (items) => names([...items].sort((a, b) => weight(b) - weight(a) || a.index - b.index).slice(0, 1));
      paragraphs.push(format(`synthesis_pair_${pair.key}`, {first: representative(pair.left), second: representative(pair.right)}));
    } else if (focus.cards.length > 1) {
      paragraphs.push(format('synthesis_shared', {cards: names(focus.cards), theme: themeName(focus.theme), advice: advice(focus.theme)}));
    } else {
      const other = ranked[1];
      paragraphs.push(format('synthesis_mixed', {first: names(focus.cards), second: names(other.cards), theme: themeName(focus.theme), other: themeName(other.theme)}));
    }
    const obstacle = cards.find((card) => card.position.key === 'obstacle');
    const from = obstacle || cards.find((card) => ['present', 'situation'].includes(card.position.key)) || cards[0];
    const to = cards.find((card) => card.position.key === 'advice') || (obstacle && cards.find((card) => card.position.key === 'self')) || cards.find((card) => ['future', 'outcome'].includes(card.position.key)) || cards[cards.length - 1];
    if (from.index !== to.index) {
      paragraphs.push(format(to.reversed ? 'synthesis_link_reversed' : 'synthesis_link', {
        first: from.card.title, second: to.card.title, position: from.position.title, destination: to.position.title,
        theme: themeName(from.theme), other: themeName(to.theme), advice: advice(to.theme)
      }));
    }
    const reversed = cards.filter((card) => card.reversed);
    if (reversed.length) paragraphs.push(format('synthesis_reversed', {cards: names(reversed)}));
    const actionTheme = to.reversed ? focus.theme : to.theme;
    paragraphs.push(format(`synthesis_context_${['general', 'relations', 'projects'].includes(topic) ? topic : 'general'}`, {advice: advice(actionTheme)}));
    return paragraphs;
  };


  const bindTarotReadings = (root = document) => {
    const nodes = [...(root.querySelectorAll?.('[data-tarot-reading]') || [])];
    if (root.matches?.('[data-tarot-reading]')) nodes.unshift(root);
    nodes.forEach((tool) => {
      if (tool.dataset.bound === 'true') return;
      let data;
      try { data = JSON.parse(tool.querySelector('[data-reading-data]').textContent); } catch { return; }
      const q = (name) => tool.querySelector(`[data-reading-${name}]`);
      const form = q('form');
      const slots = q('slots');
      const method = q('method');
      const context = q('context');
      const reversals = q('reversals');
      const submit = q('submit');
      const results = q('results');
      const status = q('status');
      const labels = data.labels;
      const cache = new Map();
      const cards = new Map(data.cards.map((card) => [card.id, card]));
      let selection = [];
      let revision = 0;
      let requestController;
      const normalize = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const format = (value, vars = {}) => String(value || '').replace(/__([A-Z_]+)__/g, (match, key) => vars[key.toLowerCase()] ?? match);
      const element = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text !== undefined) node.textContent = text;
        return node;
      };
      const currentSpread = () => data.spreads[method.value];
      const invalidate = () => {
        revision += 1;
        requestController?.abort();
        if (!results.hidden) status.textContent = labels.stale;
        results.hidden = true;
        q('copy-status').textContent = '';
        submit.disabled = !data.cards.length;
        submit.textContent = labels.interpret;
        form.removeAttribute('aria-busy');
      };
      const updateChoices = () => {
        const chosen = selection.map((item) => item.id).filter(Boolean);
        slots.querySelectorAll('select').forEach((select) => {
          [...select.options].forEach((option) => { option.disabled = Boolean(option.value && option.value !== select.value && chosen.includes(option.value)); });
        });
        q('progress').textContent = format(labels.progress, {count: chosen.length, total: selection.length});
      };
      const updateImage = (slot, card, reversed) => {
        const media = slot.querySelector('.tec-reading__card-art');
        media.replaceChildren();
        media.classList.toggle('is-reversed', Boolean(reversed));
        if (card?.image) {
          const img = element('img');
          img.src = card.image;
          img.alt = '';
          img.width = 120;
          img.height = 200;
          img.loading = 'lazy';
          media.append(img);
        } else {
          media.append(element('span', '', String(Number(slot.dataset.index) + 1).padStart(2, '0')));
        }
      };
      const renderSlots = () => {
        const spread = currentSpread();
        selection = spread.positions.map((_, index) => selection[index] || {id: '', reversed: false});
        q('method-description').textContent = spread.description;
        slots.replaceChildren();
        spread.positions.forEach((position, index) => {
          const slot = element('div', 'tec-reading__slot');
          slot.dataset.index = String(index);
          const art = element('div', 'tec-reading__card-art');
          const copy = element('div', 'tec-reading__slot-copy');
          const id = `${tool.closest('[id]')?.id || 'tarot'}-card-${index}`;
          const label = element('label', '', `${index + 1}. ${position.title}`);
          label.htmlFor = id;
          const hint = element('p', 'tec-reading__hint', position.text);
          hint.id = `${id}-hint`;
          const select = element('select');
          select.id = id;
          select.setAttribute('aria-describedby', hint.id);
          const empty = element('option', '', labels.select_card);
          empty.value = '';
          select.append(empty);
          Object.entries(data.families).forEach(([key, title]) => {
            const group = element('optgroup');
            group.label = title;
            data.cards.filter((card) => card.family === key).sort((a, b) => a.title.localeCompare(b.title)).forEach((card) => {
              const option = element('option', '', card.title);
              option.value = card.id;
              group.append(option);
            });
            select.append(group);
          });
          select.value = selection[index].id;
          const orientation = element('label', 'tec-reading__check tec-reading__orientation');
          orientation.hidden = !reversals.checked;
          const checkbox = element('input');
          checkbox.type = 'checkbox';
          checkbox.checked = selection[index].reversed;
          orientation.append(checkbox, element('span', '', labels.reversed));
          select.addEventListener('change', () => {
            selection[index].id = select.value;
            status.textContent = '';
            invalidate();
            updateImage(slot, cards.get(select.value), reversals.checked && selection[index].reversed);
            updateChoices();
          });
          checkbox.addEventListener('change', () => {
            selection[index].reversed = checkbox.checked;
            invalidate();
            updateImage(slot, cards.get(select.value), checkbox.checked);
          });
          const selectWrap = element('div', 'tec-reading__select');
          selectWrap.append(select, q('chevron').content.cloneNode(true));
          copy.append(label, hint, selectWrap, orientation);
          slot.append(art, copy);
          slots.append(slot);
          updateImage(slot, cards.get(select.value), reversals.checked && selection[index].reversed);
        });
        updateChoices();
      };
      const sourceText = (html, mode, topic) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('script,style,nav,aside').forEach((node) => node.remove());
        const headings = [...doc.querySelectorAll('h2,h3,h4')];
        const paragraphsAfter = (heading) => {
          const found = [];
          let node = heading?.nextElementSibling;
          while (node && !/^H[1-4]$/.test(node.tagName)) {
            if (node.matches('p,ul,ol') && node.textContent.trim()) found.push(node.textContent.trim());
            node = node.nextElementSibling;
          }
          return found;
        };
        const stress = /defavorable|desfavorable|sfavorevole|unfavou?rable|ungunstig|negati|tension|tensao|renvers|reversed|invertid|invertit|umgekehrt|obstacle|shadow|逆位|逆位置|不利|توتر|مقلوب|उल्ट|तनाव/;
        const supportive = /favou?rable|endroit|upright|droit|aufrecht|favorevole|favoravel|正位|正位置/;
        const specific = headings.find((h) => {
          const title = normalize(h.textContent);
          return mode === 'stress' ? stress.test(title) : supportive.test(title) && !stress.test(title);
        });
        const general = headings.find((h) => /signification.*tirage|meaning.*reading|significado|bedeutung/.test(normalize(h.textContent)));
        let paragraphs = paragraphsAfter(specific);
        let fallback = !paragraphs.length;
        if (!paragraphs.length) paragraphs = paragraphsAfter(general);
        if (!paragraphs.length) { paragraphs = [...doc.querySelectorAll('p')].map((p) => p.textContent.trim()).filter(Boolean).slice(0,2); fallback = true; }
        const topicPattern = topic === 'relations' ? /^(en )?amour|^relations|^love|^liebe|^amor|^amore/ : /^(au |en )?travail|^projet|^work|^career|^trabajo|^arbeit|^lavoro/;
        const topicText = topic === 'general' ? '' : headings.filter((h) => topicPattern.test(normalize(h.textContent)))
          .map((h) => paragraphsAfter(h).slice(0, 2).join('\n\n')).find(Boolean)
          || [...doc.querySelectorAll('p')].map((p) => p.textContent.trim()).find((p) => topicPattern.test(normalize(p))) || '';
        const clip = (text) => text.length > 1100 ? `${text.slice(0,1097).replace(/\s+\S*$/, '')}…` : text;
        return {meaning: clip(paragraphs.slice(0,2).join('\n\n')), topic: clip(topicText), fallback};
      };
      const fetchSource = async (card, signal) => {
        if (cache.has(card.id)) return cache.get(card.id);
        const url = new URL(card.url, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid source');
        // Ajax requests must retain the explicit preview theme, not rely on its cookie alone.
        url.searchParams.set('section_id', 'tarot-reading-data');
        if (/^\d+$/.test(data.previewTheme || '')) url.searchParams.set('preview_theme_id', data.previewTheme);
        const controller = new AbortController();
        const abort = () => controller.abort();
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
        signal.addEventListener('abort', abort, {once: true});
        const timeout = setTimeout(abort, 15000);
        try {
          const response = await fetch(url, {signal: controller.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Source unavailable');
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-tarot-reading-source]');
          if (!content) throw new Error('Missing source');
          const source = JSON.parse(content.textContent);
          if (source.id !== card.id || !source.body) throw new Error('Invalid source');
          cache.set(card.id, source.body);
          return source.body;
        } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
      };
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        invalidate();
        const missing = selection.findIndex((item) => !cards.has(item.id));
        if (missing !== -1) {
          status.textContent = labels.missing;
          slots.querySelectorAll('select')[missing].focus();
          return;
        }
        if (new Set(selection.map((item) => item.id)).size !== selection.length) { status.textContent = labels.duplicate; return; }
        const currentRevision = revision;
        const snapshot = selection.map((item) => ({...item, reversed: reversals.checked && item.reversed, card: cards.get(item.id)}));
        const spread = currentSpread();
        const topic = context.value;
        requestController = new AbortController();
        const signal = requestController.signal;
        submit.disabled = true;
        submit.textContent = labels.loading;
        status.textContent = labels.loading;
        form.setAttribute('aria-busy', 'true');
        try {
          const sourceBodies = new Array(snapshot.length);
          let cursor = 0;
          await Promise.all(Array.from({length: Math.min(3, snapshot.length)}, async () => {
            while (cursor < snapshot.length) { const index = cursor++; sourceBodies[index] = await fetchSource(snapshot[index].card, signal); }
          }));
          if (revision !== currentRevision || signal.aborted) return;
          const summary = q('summary');
          const details = q('details');
          const combined = snapshot.length > 1;
          summary.replaceChildren(element('h3', '', combined ? labels.synthesis_title : labels.summary));
          if (!combined) summary.append(element('p', 'tec-reading__summary-intro', labels.summary_intro));
          if (combined) buildTarotSynthesis(snapshot, spread.positions, topic, labels, tarotReadingThemes).forEach((paragraph) => summary.append(element('p', 'tec-reading__synthesis-paragraph', paragraph)));
          details.replaceChildren();
          const recap = element('ol', 'tec-reading__recap');
          if (!combined) summary.append(recap);
          snapshot.forEach((item, index) => {
            const position = spread.positions[index];
            const tension = item.reversed || position.key === 'obstacle' || position.key === 'hopes';
            const source = sourceText(sourceBodies[index], tension ? 'stress' : 'support', topic);
            const recapItem = element('li');
            const recapTitle = snapshot.length === 1 ? item.card.title : `${position.title} — ${item.card.title}`;
            recapItem.append(element('h4', '', recapTitle));
            if (item.reversed) recapItem.append(element('span', 'tec-reading__recap-orientation', labels.reversed));
            const excerpt = (tension ? source.meaning : source.topic || source.meaning).split(/\n\s*\n/)[0].trim();
            let brief = excerpt;
            if (typeof Intl.Segmenter === 'function') {
              const sentences = [...new Intl.Segmenter(document.documentElement.lang || undefined, {granularity: 'sentence'}).segment(excerpt)];
              brief = sentences.slice(0, sentences[0]?.segment.trim().length < 60 ? 2 : 1).map((sentence) => sentence.segment).join('').trim();
            }
            recapItem.append(element('p', '', brief));
            recap.append(recapItem);
            const article = element('article', 'tec-reading__interpretation');
            const title = element('h3', '', `${index + 1}. ${position.title} — ${item.card.title}`);
            article.append(title, element('p', 'tec-reading__position-note', format(labels.position_note, {position: position.text})));
            if (item.reversed) article.append(element('p', 'tec-reading__lens', labels.reverse_note));
            else if (tension) article.append(element('p', 'tec-reading__lens', labels.tension_note));
            const body = element('p', 'tec-reading__source-text', source.meaning);
            article.append(body);
            if (source.topic) article.append(element('p', '', source.topic));
            if (source.fallback) article.append(element('p', 'tec-reading__hint', labels.source_fallback));
            const link = element('a', 'text-button text-button--has-icon tec-reading__source-link');
            link.append(element('u', '', labels.source));
            const sourceIcon = q('source-icon')?.content.firstElementChild;
            if (sourceIcon) link.append(sourceIcon.cloneNode(true));
            link.href = item.card.url;
            link.target = '_blank';
            link.rel = 'noopener';
            article.append(link);
            details.append(article);
          });
          results.hidden = false;
          status.textContent = '';
          results.querySelector('h2').focus({preventScroll: true});
          results.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
        } catch (error) {
          if (revision === currentRevision) status.textContent = labels.load_error;
        } finally {
          if (revision === currentRevision) { submit.disabled = false; submit.textContent = labels.interpret; form.removeAttribute('aria-busy'); }
        }
      });
      method.addEventListener('change', () => { invalidate(); status.textContent = ''; renderSlots(); });
      q('start')?.addEventListener('click', (event) => {
        event.preventDefault();
        method.focus({preventScroll: true});
        form.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start'});
      });
      reversals.addEventListener('change', () => { invalidate(); renderSlots(); });
      context.addEventListener('change', invalidate);
      form.addEventListener('reset', () => { invalidate(); setTimeout(() => { selection = []; status.textContent = ''; renderSlots(); }, 0); });
      q('copy').addEventListener('click', async () => {
        try {
          const text = [currentSpread().title, labels.method_note, q('summary').innerText, ...[...q('details').children].map((node) => `${node.innerText}\n${node.querySelector('a').href}`), labels.disclaimer].join('\n\n');
          await navigator.clipboard.writeText(text);
          q('copy-status').textContent = labels.copy_ok;
        } catch { q('copy-status').textContent = labels.copy_error; }
      });
      if (!data.cards.length) { status.textContent = labels.load_error; submit.disabled = true; }
      tool.dataset.bound = 'true';
      renderSlots();
      form.hidden = false;
    });
  };

  const bindAstrologyHomes = (scope = document) => {
    const homes = [];
    if (scope instanceof HTMLElement && scope.matches('[data-astrology-home]')) homes.push(scope);
    scope.querySelectorAll?.('[data-astrology-home]').forEach((home) => homes.push(home));

    homes.forEach((home) => {
      if (home.dataset.astrologyBound === 'true') return;
      home.dataset.astrologyBound = 'true';

      const primer = home.querySelector('[data-astrology-primer]');
      if (primer) {
        const tabs = Array.from(primer.querySelectorAll('[data-primer-layer]'));
        const panels = Array.from(primer.querySelectorAll('[data-primer-panel]'));
        const chartLabel = primer.querySelector('[data-primer-chart-label]');
        const activateLayer = (layer, moveFocus = false) => {
          primer.dataset.activeLayer = layer;
          tabs.forEach((tab) => {
            const active = tab.dataset.primerLayer === layer;
            tab.setAttribute('aria-selected', active ? 'true' : 'false');
            tab.tabIndex = active ? 0 : -1;
            if (active && chartLabel) chartLabel.textContent = tab.dataset.primerLabel || tab.textContent.trim();
            if (active && moveFocus) tab.focus({ preventScroll: true });
          });
          panels.forEach((panel) => { panel.hidden = panel.dataset.primerPanel !== layer; });
        };

        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => activateLayer(tab.dataset.primerLayer));
          tab.addEventListener('keydown', (event) => {
            let nextIndex = null;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % tabs.length;
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = tabs.length - 1;
            if (nextIndex === null) return;
            event.preventDefault();
            activateLayer(tabs[nextIndex].dataset.primerLayer, true);
          });
        });
      }

      const filters = Array.from(home.querySelectorAll('[data-astrology-filter]'));
      const cards = Array.from(home.querySelectorAll('[data-astrology-card]'));
      const libraryTitle = home.querySelector('[data-astrology-library-title]');
      const emptyState = home.querySelector('[data-astrology-filter-empty]');
      if (!filters.length || !cards.length) return;

      const applyFilter = (filter, activeLink) => {
        let visibleCount = 0;
        cards.forEach((card) => {
          const visible = filter === 'all' || (card.dataset.astrologyTags || '').includes(`|${filter}|`);
          card.hidden = !visible;
          if (visible) visibleCount += 1;
        });
        filters.forEach((link) => {
          const active = link.dataset.astrologyFilter === filter;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'page');
          else link.removeAttribute('aria-current');
        });
        if (libraryTitle && activeLink?.dataset.filterTitle) libraryTitle.textContent = activeLink.dataset.filterTitle;
        if (emptyState) emptyState.hidden = visibleCount > 0;
      };

      const updateUrl = (link) => {
        const target = new URL(link.href, window.location.href);
        const current = new URL(window.location.href);
        ['preview_theme_id', 'pb'].forEach((parameter) => {
          if (current.searchParams.has(parameter)) target.searchParams.set(parameter, current.searchParams.get(parameter));
        });
        window.history.pushState({ astrologyFilter: link.dataset.astrologyFilter }, '', target);
      };

      filters.forEach((link) => {
        link.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          applyFilter(link.dataset.astrologyFilter, link);
          updateUrl(link);
        });
      });

      window.addEventListener('popstate', () => {
        const activeLink = filters.find((link) => new URL(link.href, window.location.href).pathname === window.location.pathname) || filters[0];
        applyFilter(activeLink.dataset.astrologyFilter, activeLink);
      });
    });
  };

  const trioAffinityModel = (points) => {
    const relation = (a,b) => {
      const gap=Math.min((a-b+12)%12,(b-a+12)%12);
      return gap===0?'same':gap===4?'trine':gap===2?'sextile':gap===6?'opposition':gap===3?'square':'other';
    };
    const weights={same:3,trine:3,sextile:2,opposition:0,square:-2,other:-1};
    return Array.from({length:12},(_,index)=>{
      const relations=points.map(point=>point.index===null?null:relation(point.index,index));
      return {index,relations,score:relations.reduce((sum,item)=>sum+(item===null?0:weights[item]),0)};
    });
  };
  const renderAstroTrio = (root,data,labels,signs) => {
    const q=selector=>root.querySelector(selector);
    const t=JSON.parse(q('[data-trio-labels]').textContent);
    const escape=value=>String(value).replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
    const fill=(text,values)=>Object.entries(values).reduce((s,[key,value])=>s.replaceAll('__'+key+'__',value),text);
    const points=[{key:'sun',label:labels.solar,value:data.planets.sun},{key:'moon',label:labels.lunar,value:data.planets.moon},{key:'asc',label:labels.ascendant,value:data.ascendant}].map(point=>{
      const possible=data.trioPossibilities?.[point.key];
      const uncertain=possible?.length>1;
      return {...point,possible,uncertain,index:point.value===null||uncertain?null:Math.floor(point.value/30)};
    });
    const name=point=>point.uncertain?point.possible.map(i=>labels.signs[i]).join(' / '):point.index===null?t.unknown:labels.signs[point.index];
    const notice=[];
    if(data.unknownTime)notice.push(t.time_notice);
    if(data.trioPolar)notice.push(t.polar_notice);
    if(points.some(p=>p.value!==null&&(p.value%30<.3||p.value%30>29.7)))notice.push(t.cusp_notice);
    const noticeNode=q('[data-trio-notice]');noticeNode.hidden=!notice.length;noticeNode.textContent=notice.join(' ');
    q('[data-trio-panels]').innerHTML=points.map(p=>`<article class="tec-trio__panel" data-trio-panel="${p.key}"><p class="tec-trio__eyebrow">${escape(p.label)}</p><h3>${escape(name(p))}</h3><h4>${escape(t[p.key+'_role'])}</h4><p>${escape(t[p.key+'_copy'])}</p>${p.index!==null?`<p class="tec-trio__tone">${escape(labels.signStyles[p.index])}</p>`:`<p class="tec-trio__tone">${escape(p.uncertain?t.boundary:t.unavailable)}</p>`}</article>`).join('');
    const selectPoint=key=>{
      root.querySelectorAll('[data-trio-point]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.trioPoint===key)));
      root.querySelectorAll('[data-trio-panel]').forEach(p=>p.hidden=p.dataset.trioPanel!==key);
    };
    points.forEach(p=>{
      const b=q('[data-trio-point="'+p.key+'"]');b.querySelector('span').textContent=p.index===null?'—':signs[p.index].glyph;b.querySelector('small').textContent=p.label;b.querySelector('strong').textContent=name(p);b.onclick=()=>selectPoint(p.key);
    });
    selectPoint('sun');
    const rankings=trioAffinityModel(points);const available=points.filter(p=>p.index!==null).length;
    q('[data-trio-basis]').textContent=available?fill(t.basis,{COUNT:available}):t.empty;
    q('[data-trio-signs]').innerHTML=signs.map((s,i)=>`<button type="button" data-trio-candidate="${i}" aria-pressed="false"><span aria-hidden="true">${s.glyph}</span><span>${escape(labels.signs[i])}</span></button>`).join('');
    const compare=index=>{
      root.querySelectorAll('[data-trio-candidate]').forEach(b=>b.setAttribute('aria-pressed',String(Number(b.dataset.trioCandidate)===index)));
      const entry=rankings[index];
      q('[data-trio-comparison]').innerHTML=`<h3>${signs[index].glyph} ${escape(labels.signs[index])}</h3><div class="tec-trio__comparison-grid">${points.map((p,i)=>{const r=entry.relations[i];return `<article><h4>${escape(t[['general','emotion','contact'][i]])}</h4>${r?`<p class="tec-trio__comparison-line">${escape(fill(t.compare_line,{POINT:p.label,SIGN:labels.signs[p.index],OTHER:labels.signs[index]}))}</p><strong class="tec-trio__relation tec-trio__relation--${r}">${escape(t[r+'_'+p.key])}</strong><p>${escape(t[r+'_'+p.key+'_text'])}</p>`:`<p>${escape(t.unavailable)}</p>`}</article>`}).join('')}</div>`;
    };
    q('[data-trio-signs]').onclick=event=>{const b=event.target.closest('[data-trio-candidate]');if(b)compare(Number(b.dataset.trioCandidate));};
    const mode=name=>{
      root.querySelectorAll('[data-trio-mode]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.trioMode===name)));
      const sorted=[...rankings].sort((a,b)=>(name==='more'?b.score-a.score:a.score-b.score)||a.index-b.index);
      const cutoff=sorted[2].score;const group=available?sorted.filter(r=>name==='more'?r.score>=cutoff:r.score<=cutoff):[];
      q('[data-trio-matches]').innerHTML=group.map(r=>`<button type="button" data-trio-candidate="${r.index}" aria-pressed="false"><span aria-hidden="true">${signs[r.index].glyph}</span><strong>${escape(labels.signs[r.index])}</strong><small>${escape(t.detail)} <span aria-hidden="true">›</span></small></button>`).join('');
      if(group.length)compare(group[0].index);else q('[data-trio-comparison]').textContent=t.empty;
    };
    q('[data-trio-matches]').onclick=event=>{const b=event.target.closest('[data-trio-candidate]');if(b)compare(Number(b.dataset.trioCandidate));};
    root.querySelectorAll('[data-trio-mode]').forEach(b=>b.onclick=()=>mode(b.dataset.trioMode));mode('more');
    root.dataset.trioReady='true';
  };

  const bindAstrologyChartGenerators = (scope = document) => {
    const generators = [];
    if (scope instanceof HTMLElement && scope.matches('[data-astro-chart]')) generators.push(scope);
    scope.querySelectorAll?.('[data-astro-chart]').forEach((generator) => generators.push(generator));

    generators.forEach((generator) => {
      if (generator.dataset.astroChartBound === 'true') return;
      generator.dataset.astroChartBound = 'true';

      const $ = (selector) => generator.querySelector(selector);
      const form = $('[data-astro-chart-form]');
      const dateInput = $('[data-astro-date]');
      const timeInput = $('[data-astro-time]');
      const unknownInput = $('[data-astro-time-unknown]');
      const timeField = $('[data-astro-time-field]');
      const placeInput = $('[data-astro-place]');
      const placeButton = $('[data-astro-place-search]');
      const placeResults = $('[data-astro-place-results]');
      const selectedPlace = $('[data-astro-selected-place]');
      const offsetInput = $('[data-astro-offset]');
      const offsetHelp = $('[data-astro-offset-help]');
      const errorBox = $('[data-astro-error]');
      const output = $('[data-astro-output]');
      const wheel = $('[data-astro-wheel]');
      const wheelLegend = $('[data-astro-wheel-legend]');
      const bigThree = $('[data-astro-big-three]');
      const summary = $('[data-astro-summary]');
      const panel = $('[data-astro-panel]');
      const tabHelp = $('[data-astro-tab-help]');
      const timeNotice = $('[data-astro-time-notice]');
      const resultContext = $('[data-astro-result-context]');
      const labelsNode = $('[data-astro-labels]');
      const formulaChevron = $('[data-astro-summary-chevron]')?.innerHTML || '';
      if (!form || !labelsNode) return;

      let labels;
      try { labels = JSON.parse(labelsNode.textContent); } catch (_error) { return; }

      const signs = [
        { glyph: '♈︎', element: 'fire' }, { glyph: '♉︎', element: 'earth' }, { glyph: '♊︎', element: 'air' },
        { glyph: '♋︎', element: 'water' }, { glyph: '♌︎', element: 'fire' }, { glyph: '♍︎', element: 'earth' },
        { glyph: '♎︎', element: 'air' }, { glyph: '♏︎', element: 'water' }, { glyph: '♐︎', element: 'fire' },
        { glyph: '♑︎', element: 'earth' }, { glyph: '♒︎', element: 'air' }, { glyph: '♓︎', element: 'water' }
      ];
      const planetMeta = {
        sun: { glyph: '☉', role: labels.planetRoleSun }, moon: { glyph: '☽', role: labels.planetRoleMoon },
        mercury: { glyph: '☿', role: labels.planetRoleMercury }, venus: { glyph: '♀', role: labels.planetRoleVenus },
        mars: { glyph: '♂', role: labels.planetRoleMars }, jupiter: { glyph: '♃', role: labels.planetRoleJupiter },
        saturn: { glyph: '♄', role: labels.planetRoleSaturn }, uranus: { glyph: '♅', role: labels.planetRoleUranus },
        neptune: { glyph: '♆', role: labels.planetRoleNeptune }, node: { glyph: '☊', role: labels.planetRoleNode }
      };
      const planetActions = {
        sun: labels.planetActionSun, moon: labels.planetActionMoon, mercury: labels.planetActionMercury,
        venus: labels.planetActionVenus, mars: labels.planetActionMars, jupiter: labels.planetActionJupiter,
        saturn: labels.planetActionSaturn, uranus: labels.planetActionUranus, neptune: labels.planetActionNeptune,
        node: labels.planetActionNode
      };
      const countryZones = {
        fr: 'Europe/Paris', be: 'Europe/Brussels', ch: 'Europe/Zurich', lu: 'Europe/Luxembourg', mc: 'Europe/Monaco',
        de: 'Europe/Berlin', nl: 'Europe/Amsterdam', it: 'Europe/Rome', es: 'Europe/Madrid', pt: 'Europe/Lisbon',
        gb: 'Europe/London', ie: 'Europe/Dublin', at: 'Europe/Vienna', dk: 'Europe/Copenhagen', no: 'Europe/Oslo',
        se: 'Europe/Stockholm', fi: 'Europe/Helsinki', pl: 'Europe/Warsaw', cz: 'Europe/Prague', gr: 'Europe/Athens',
        tr: 'Europe/Istanbul', ma: 'Africa/Casablanca', dz: 'Africa/Algiers', tn: 'Africa/Tunis', jp: 'Asia/Tokyo',
        in: 'Asia/Kolkata', cn: 'Asia/Shanghai', nz: 'Pacific/Auckland'
      };
      let place = null;
      let lastSearch = 0;
      let predictiveTimer = null;
      let predictiveRequest = 0;
      let chart = null;

      const norm = (angle) => ((angle % 360) + 360) % 360;
      const rad = (degrees) => degrees * Math.PI / 180;
      const deg = (radians) => radians * 180 / Math.PI;
      const angularDistance = (a, b) => Math.abs(((a - b + 540) % 360) - 180);
      const signFor = (longitude) => Math.floor(norm(longitude) / 30);
      const formatPosition = (longitude) => {
        const normalized = norm(longitude);
        const totalMinutes = Math.round((normalized % 30) * 60);
        const degrees = Math.floor(totalMinutes / 60) % 30;
        const minutes = totalMinutes % 60;
        return `${degrees}°${String(minutes).padStart(2, '0')}′`;
      };
      const escapeHtml = (value) => String(value).replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
      const fillTokens = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template);
      const trimSentence = (value) => String(value).replace(/[.!?]\s*$/, '');
      const showError = (message) => { errorBox.textContent = message; errorBox.hidden = false; };
      const clearError = () => { errorBox.hidden = true; errorBox.textContent = ''; };

      const zoneOffset = (zone, dateValue, timeValue) => {
        try {
          const [year, month, day] = dateValue.split('-').map(Number);
          const [hour, minute] = timeValue.split(':').map(Number);
          let utcGuess = Date.UTC(year, month - 1, day, hour, minute);
          for (let iteration = 0; iteration < 2; iteration += 1) {
            const parts = new Intl.DateTimeFormat('en-CA', {
              timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
            }).formatToParts(new Date(utcGuess)).reduce((result, item) => { result[item.type] = item.value; return result; }, {});
            const represented = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
            const measuredOffset = (represented - utcGuess) / 3600000;
            utcGuess = Date.UTC(year, month - 1, day, hour, minute) - measuredOffset * 3600000;
          }
          return (Date.UTC(year, month - 1, day, hour, minute) - utcGuess) / 3600000;
        } catch (_error) { return null; }
      };

      const refreshOffset = () => {
        if (!place) return;
        const zone = countryZones[place.countryCode];
        const offset = zone && dateInput.value ? zoneOffset(zone, dateInput.value, timeInput.value || '12:00') : null;
        if (offset !== null && Number.isFinite(offset)) {
          offsetInput.value = String(Math.round(offset * 4) / 4);
          offsetHelp.textContent = labels.offsetAuto;
        } else {
          offsetInput.value = String(Math.max(-12, Math.min(14, Math.round(place.lon / 15 * 4) / 4)));
          offsetHelp.textContent = labels.offsetCheck;
        }
      };

      const renderPlaces = (results) => {
        placeResults.innerHTML = '';
        if (!results.length) {
          placeResults.innerHTML = `<p>${escapeHtml(labels.noResults)}</p>`;
          placeResults.hidden = false;
          return;
        }
        results.slice(0, 5).forEach((result) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = result.name;
          button.setAttribute('aria-label', `${labels.choose} ${result.name}`);
          button.addEventListener('click', () => {
            predictiveRequest += 1;
            clearTimeout(predictiveTimer);
            place = {
              name: result.name, lat: Number(result.lat), lon: Number(result.lon), countryCode: result.countryCode || ''
            };
            selectedPlace.textContent = `${labels.selected} ${place.name}`;
            selectedPlace.hidden = false;
            placeInput.value = result.name;
            placeResults.hidden = true;
            refreshOffset();
            clearError();
          });
          placeResults.append(button);
        });
        placeResults.hidden = false;
      };

      const searchPlace = async (options = {}) => {
        const predictive = options.predictive === true;
        const query = placeInput.value.trim();
        if (query.length < (predictive ? 3 : 2)) { if (!predictive) showError(labels.errorPlace); return; }
        const requestId = ++predictiveRequest;
        if (!predictive) {
          const now = Date.now();
          if (now - lastSearch < 1000) return;
          lastSearch = now;
          placeButton.disabled = true;
          placeButton.textContent = labels.searching;
          clearError();
        }
        try {
          const language = (document.documentElement.lang || 'fr').split('-')[0];
          const url = predictive
            ? `https://data.geopf.fr/geocodage/completion/?text=${encodeURIComponent(query)}&terr=METROPOLE,DOMTOM&poiType=administratif&maximumResponses=5`
            : `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&accept-language=${encodeURIComponent(language)}&q=${encodeURIComponent(query)}`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error('geocoding');
          const payload = await response.json();
          if (requestId !== predictiveRequest) return;
          let results;
          if (predictive) {
            const seenPlaces = new Set();
            results = (payload.results || [])
              .filter((item) => ['administratif','municipality'].includes(item.kind) && Number.isFinite(Number(item.x)) && Number.isFinite(Number(item.y)))
              .map((item) => {
                const placeName = item.city || item.names?.[0] || String(item.fulltext || '').split(',')[0];
                const postcode = item.zipcode ? `, ${item.zipcode}` : '';
                return { name: `${placeName}${postcode}, France`, lat: item.y, lon: item.x, countryCode: 'fr' };
              })
              .filter((item) => {
                const key = item.name.toLocaleLowerCase('fr');
                if (seenPlaces.has(key)) return false;
                seenPlaces.add(key);
                return true;
              });
          } else {
            results = payload.map((item) => ({ name: item.display_name, lat: item.lat, lon: item.lon, countryCode: item.address?.country_code || '' }));
          }
          renderPlaces(results);
        } catch (_error) { if (!predictive) showError(labels.errorSearch); }
        finally { if (!predictive) { placeButton.disabled = false; placeButton.textContent = labels.search; } }
      };

      const eccentricAnomaly = (meanAnomaly, eccentricity) => {
        const mean = rad(norm(meanAnomaly));
        let anomaly = mean + eccentricity * Math.sin(mean) * (1 + eccentricity * Math.cos(mean));
        for (let iteration = 0; iteration < 8; iteration += 1) anomaly -= (anomaly - eccentricity * Math.sin(anomaly) - mean) / (1 - eccentricity * Math.cos(anomaly));
        return anomaly;
      };

      const orbitalPosition = (elements) => {
        const anomaly = eccentricAnomaly(elements.M, elements.e);
        const xv = elements.a * (Math.cos(anomaly) - elements.e);
        const yv = elements.a * Math.sqrt(1 - elements.e * elements.e) * Math.sin(anomaly);
        const trueAnomaly = Math.atan2(yv, xv);
        const distance = Math.sqrt(xv * xv + yv * yv);
        const node = rad(elements.N), inclination = rad(elements.i), perihelion = rad(elements.w);
        return {
          x: distance * (Math.cos(node) * Math.cos(trueAnomaly + perihelion) - Math.sin(node) * Math.sin(trueAnomaly + perihelion) * Math.cos(inclination)),
          y: distance * (Math.sin(node) * Math.cos(trueAnomaly + perihelion) + Math.cos(node) * Math.sin(trueAnomaly + perihelion) * Math.cos(inclination)),
          z: distance * Math.sin(trueAnomaly + perihelion) * Math.sin(inclination),
          v: deg(trueAnomaly), r: distance
        };
      };

      const calculateLongitudes = (julianDate) => {
        const d = julianDate - 2451543.5;
        const sunElements = { N: 0, i: 0, w: 282.9404 + 4.70935e-5*d, a: 1, e: .016709 - 1.151e-9*d, M: 356.047 + .9856002585*d };
        const sunOrbit = orbitalPosition(sunElements);
        const sunLongitude = norm(sunOrbit.v + sunElements.w);
        const sunX = sunOrbit.r * Math.cos(rad(sunLongitude));
        const sunY = sunOrbit.r * Math.sin(rad(sunLongitude));
        const elements = {
          mercury: { N:48.3313+3.24587e-5*d,i:7.0047+5e-8*d,w:29.1241+1.01444e-5*d,a:.387098,e:.205635+5.59e-10*d,M:168.6562+4.0923344368*d },
          venus: { N:76.6799+2.4659e-5*d,i:3.3946+2.75e-8*d,w:54.891+1.38374e-5*d,a:.72333,e:.006773-1.302e-9*d,M:48.0052+1.6021302244*d },
          mars: { N:49.5574+2.11081e-5*d,i:1.8497-1.78e-8*d,w:286.5016+2.92961e-5*d,a:1.523688,e:.093405+2.516e-9*d,M:18.6021+.5240207766*d },
          jupiter: { N:100.4542+2.76854e-5*d,i:1.303-1.557e-7*d,w:273.8777+1.64505e-5*d,a:5.20256,e:.048498+4.469e-9*d,M:19.895+.0830853001*d },
          saturn: { N:113.6634+2.3898e-5*d,i:2.4886-1.081e-7*d,w:339.3939+2.97661e-5*d,a:9.55475,e:.055546-9.499e-9*d,M:316.967+.0334442282*d },
          uranus: { N:74.0005+1.3978e-5*d,i:.7733+1.9e-8*d,w:96.6612+3.0565e-5*d,a:19.18171-1.55e-8*d,e:.047318+7.45e-9*d,M:142.5905+.011725806*d },
          neptune: { N:131.7806+3.0173e-5*d,i:1.77-2.55e-7*d,w:272.8461-6.027e-6*d,a:30.05826+3.313e-8*d,e:.008606+2.15e-9*d,M:260.2471+.005995147*d }
        };
        const positions = { sun: sunLongitude };
        Object.entries(elements).forEach(([name, planetElements]) => {
          const orbit = orbitalPosition(planetElements);
          positions[name] = norm(deg(Math.atan2(orbit.y + sunY, orbit.x + sunX)));
        });

        const moonElements = { N:125.1228-.0529538083*d,i:5.1454,w:318.0634+.1643573223*d,a:60.2666,e:.0549,M:115.3654+13.0649929509*d };
        const moonOrbit = orbitalPosition(moonElements);
        let moonLongitude = norm(deg(Math.atan2(moonOrbit.y, moonOrbit.x)));
        const moonMeanLongitude = norm(moonElements.M + moonElements.w + moonElements.N);
        const sunMeanLongitude = norm(sunElements.M + sunElements.w);
        const elongation = norm(moonMeanLongitude - sunMeanLongitude);
        const argumentLatitude = norm(moonMeanLongitude - moonElements.N);
        moonLongitude += -1.274*Math.sin(rad(moonElements.M-2*elongation)) + .658*Math.sin(rad(2*elongation)) - .186*Math.sin(rad(sunElements.M)) - .059*Math.sin(rad(2*moonElements.M-2*elongation)) - .057*Math.sin(rad(moonElements.M-2*elongation+sunElements.M)) + .053*Math.sin(rad(moonElements.M+2*elongation)) + .046*Math.sin(rad(2*elongation-sunElements.M)) + .041*Math.sin(rad(moonElements.M-sunElements.M)) - .035*Math.sin(rad(elongation)) - .031*Math.sin(rad(moonElements.M+sunElements.M)) - .015*Math.sin(rad(2*argumentLatitude-2*elongation)) + .011*Math.sin(rad(moonElements.M-4*elongation));
        positions.moon = norm(moonLongitude);
        positions.node = norm(moonElements.N);
        return positions;
      };

      const calculateAscendant = (julianDate, latitude, longitude) => {
        const centuries = (julianDate - 2451545) / 36525;
        const gmst = norm(280.46061837 + 360.98564736629 * (julianDate - 2451545) + .000387933*centuries*centuries - centuries*centuries*centuries/38710000);
        const localSidereal = norm(gmst + longitude);
        const obliquity = rad(23.439291 - .0130042 * centuries);
        const theta = rad(localSidereal);
        const phi = rad(Math.max(-66, Math.min(66, latitude)));
        return norm(deg(Math.atan2(-Math.cos(theta), Math.sin(theta)*Math.cos(obliquity) + Math.tan(phi)*Math.sin(obliquity))) + 180);
      };

      const calculateChart = () => {
        const [year, month, day] = dateInput.value.split('-').map(Number);
        const usedTime = unknownInput.checked ? '12:00' : timeInput.value;
        const [hour, minute] = usedTime.split(':').map(Number);
        const offset = Number(offsetInput.value);
        const utcMilliseconds = Date.UTC(year, month - 1, day, hour, minute) - offset * 3600000;
        const julianDate = utcMilliseconds / 86400000 + 2440587.5;
        const planets = calculateLongitudes(julianDate);
        const ascendant = unknownInput.checked ? null : calculateAscendant(julianDate, place.lat, place.lon);
        return { planets, ascendant, julianDate, unknownTime: unknownInput.checked, place: { ...place }, date: dateInput.value, time: usedTime, offset };
      };

      const houseFor = (longitude, ascendant) => ascendant === null ? null : Math.floor(norm(longitude - ascendant) / 30) + 1;
      const aspectsFor = (planets) => {
        const definitions = [
          { name:'conjunction', angle:0, orb:8, kind:'join' }, { name:'sextile', angle:60, orb:4, kind:'easy' },
          { name:'square', angle:90, orb:6, kind:'hard' }, { name:'trine', angle:120, orb:6, kind:'easy' },
          { name:'opposition', angle:180, orb:8, kind:'hard' }
        ];
        const entries = Object.entries(planets).filter(([name]) => name !== 'node');
        const aspects = [];
        entries.forEach(([firstName, firstLongitude], firstIndex) => entries.slice(firstIndex + 1).forEach(([secondName, secondLongitude]) => {
          const distance = angularDistance(firstLongitude, secondLongitude);
          const match = definitions.find((definition) => Math.abs(distance - definition.angle) <= definition.orb);
          if (match) aspects.push({ ...match, firstName, secondName, orbValue: Math.abs(distance-match.angle), firstLongitude, secondLongitude });
        }));
        return aspects.sort((a,b) => a.orbValue-b.orbValue);
      };

      const chartPoint = (longitude, radius, ascendant = 0) => {
        const angle = rad(norm(longitude - ascendant) - 180);
        return { x: 300 + Math.cos(angle)*radius, y: 300 + Math.sin(angle)*radius };
      };

      const renderWheel = (data) => {
        const asc = data.ascendant === null ? 0 : data.ascendant;
        const planetRadius = 176;
        const aspects = aspectsFor(data.planets);
        const chartMarker = (text, point) => {
          const width = Math.max(42, Math.min(92, text.length*6.2+18));
          const x = Math.max(width/2+7, Math.min(600-width/2-7, point.x));
          const y = Math.max(14, Math.min(586, point.y));
          return `<g class="tec-astro-chart__chart-marker"><rect x="${x-width/2}" y="${y-10}" width="${width}" height="20" rx="10"/><text x="${x}" y="${y+3}">${escapeHtml(text)}</text></g>`;
        };
        const parts = ['<svg viewBox="0 0 600 600" role="img" aria-label="'+escapeHtml(labels.navigation || '')+'">'];
        parts.push('<circle class="tec-astro-chart__chart-ring" cx="300" cy="300" r="286"/><circle class="tec-astro-chart__chart-ring" cx="300" cy="300" r="238"/><circle class="tec-astro-chart__chart-ring" cx="300" cy="300" r="199"/><circle class="tec-astro-chart__chart-ring" cx="300" cy="300" r="92"/>');
        signs.forEach((sign, index) => {
          const boundary = chartPoint(index*30,286,asc), inner = chartPoint(index*30,238,asc), labelPoint = chartPoint(index*30+15,261,asc);
          parts.push(`<line class="tec-astro-chart__chart-line" x1="${boundary.x}" y1="${boundary.y}" x2="${inner.x}" y2="${inner.y}"/><text class="tec-astro-chart__chart-zodiac" x="${labelPoint.x}" y="${labelPoint.y+8}">${sign.glyph}</text>`);
        });
        if (data.ascendant !== null) {
          for (let index=0; index<12; index+=1) {
            const boundary = chartPoint(data.ascendant+index*30,238,asc), inner = chartPoint(data.ascendant+index*30,92,asc), labelPoint = chartPoint(data.ascendant+index*30+15,218,asc);
            parts.push(`<line class="tec-astro-chart__chart-line" x1="${boundary.x}" y1="${boundary.y}" x2="${inner.x}" y2="${inner.y}"/><text class="tec-astro-chart__chart-house" x="${labelPoint.x}" y="${labelPoint.y+3}">${index+1}</text>`);
          }
          parts.push(chartMarker(labels.ascendant, { x: 53, y: 300 }));
        }
        aspects.slice(0,22).forEach((aspect) => {
          const first = chartPoint(aspect.firstLongitude,planetRadius,asc), second = chartPoint(aspect.secondLongitude,planetRadius,asc);
          parts.push(`<line class="tec-astro-chart__chart-aspect tec-astro-chart__chart-aspect--${aspect.kind}" x1="${first.x}" y1="${first.y}" x2="${second.x}" y2="${second.y}"/>`);
        });
        const occupied = [];
        Object.entries(data.planets).forEach(([name, longitude], index) => {
          let radius = planetRadius;
          const angle = norm(longitude-asc);
          if (occupied.some((other) => angularDistance(angle,other) < 7)) radius -= index % 2 ? 26 : -22;
          occupied.push(angle);
          const point = chartPoint(longitude,radius,asc);
          parts.push(`<g class="tec-astro-chart__chart-planet"><circle cx="${point.x}" cy="${point.y}" r="16"/><text x="${point.x}" y="${point.y+5}">${planetMeta[name].glyph}</text></g>`);
          if (name === 'sun' || name === 'moon') {
            parts.push(chartMarker(labels[name], chartPoint(longitude,Math.max(112,radius-36),asc)));
          }
        });
        parts.push('<circle cx="300" cy="300" r="7" fill="#c6963b"/><circle cx="300" cy="300" r="2" fill="#143e65"/></svg>');
        wheel.innerHTML = parts.join('');
        if (wheelLegend) {
          const legendItems = [
            { glyph:planetMeta.sun.glyph, label:labels.sun },
            { glyph:planetMeta.moon.glyph, label:labels.moon }
          ];
          if (data.ascendant !== null) legendItems.push({ glyph:'ASC', label:labels.ascendant, isAscendant:true });
          wheelLegend.innerHTML = legendItems.map((item) => `<span><b class="${item.isAscendant ? 'tec-astro-chart__wheel-legend-asc' : ''}" aria-hidden="true">${item.glyph}</b>${escapeHtml(item.label)}</span>`).join('');
        }
      };

      const renderBigThree = (data) => {
        const items = [
          { label: labels.solar, value: data.planets.sun, house: houseFor(data.planets.sun,data.ascendant) },
          { label: labels.lunar, value: data.planets.moon, house: houseFor(data.planets.moon,data.ascendant) }
        ];
        if (data.ascendant !== null) items.push({ label: labels.ascendant, value: data.ascendant, house: 1, isAscendant: true });
        const cards = items.map((item) => {
          const signIndex = signFor(item.value);
          const houseTitle = item.house
            ? fillTokens(item.isAscendant ? labels.bigThreeAscHouse : labels.bigThreeHouse, { HOUSE:String(item.house) })
            : labels.bigThreeHouseUnknown;
          const houseMeaning = item.house ? labels.houseMeanings[item.house-1] : '';
          return `<div class="tec-astro-chart__big-card"><span>${escapeHtml(item.label)}</span><div class="tec-astro-chart__big-value"><span aria-hidden="true">${signs[signIndex].glyph}</span><strong>${escapeHtml(labels.signs[signIndex])}</strong></div><small>${formatPosition(item.value)} — ${escapeHtml(labels.signStyles[signIndex])}</small><div class="tec-astro-chart__big-house"><span>${escapeHtml(houseTitle)}</span>${houseMeaning ? `<small>${escapeHtml(houseMeaning)}</small>` : ''}</div></div>`;
        }).join('');
        bigThree.innerHTML = `${cards}<p class="tec-astro-chart__big-three-help">${escapeHtml(labels.bigThreeHouseHelp)}</p>`;
      };

      const renderSummary = (data) => {
        const sunSignIndex = signFor(data.planets.sun);
        const moonSignIndex = signFor(data.planets.moon);
        const sunSign = labels.signs[sunSignIndex];
        const moonSign = labels.signs[moonSignIndex];
        const sunHouse = houseFor(data.planets.sun, data.ascendant);
        const moonHouse = houseFor(data.planets.moon, data.ascendant);
        const ascendantSignIndex = data.ascendant === null ? null : signFor(data.ascendant);
        const opening = data.ascendant === null
          ? fillTokens(labels.summaryOpeningUnknown, {
            SUN_SIGN:sunSign, SUN_TONE:labels.signTones[sunSignIndex], MOON_SIGN:moonSign, MOON_TONE:labels.signTones[moonSignIndex]
          })
          : fillTokens(labels.summaryOpeningKnown, {
            SUN_SIGN:sunSign, SUN_TONE:labels.signTones[sunSignIndex], MOON_SIGN:moonSign, MOON_TONE:labels.signTones[moonSignIndex],
            ASC_SIGN:labels.signs[ascendantSignIndex], ASC_TONE:labels.signTones[ascendantSignIndex]
          });
        const houses = sunHouse && moonHouse
          ? fillTokens(labels.summaryHousesKnown, {
            SUN_HOUSE:String(sunHouse), SUN_AREA:labels.houseMeanings[sunHouse-1],
            MOON_HOUSE:String(moonHouse), MOON_AREA:labels.houseMeanings[moonHouse-1]
          })
          : labels.summaryHousesUnknown;
        const aspects = aspectsFor(data.planets);
        const strongestAspect = aspects.find((aspect) => ['sun','moon'].includes(aspect.firstName) || ['sun','moon'].includes(aspect.secondName)) || aspects[0];
        const aspectSummary = strongestAspect
          ? fillTokens(labels.summaryAspect, {
            FIRST:labels[strongestAspect.firstName], SECOND:labels[strongestAspect.secondName],
            ASPECT:labels.aspects[strongestAspect.name], FIRST_ACTION:planetActions[strongestAspect.firstName],
            SECOND_ACTION:planetActions[strongestAspect.secondName], EFFECT:labels.aspectMeanings[strongestAspect.name]
          })
          : labels.summaryNoAspect;
        const formulaItems = [
          { title:labels.formulaPlanet, text:labels.formulaPlanetText },
          { title:labels.formulaSign, text:labels.formulaSignText },
          { title:labels.formulaHouse, text:labels.formulaHouseText }
        ];
        summary.innerHTML = `<h3>${escapeHtml(labels.summaryTitle)}</h3><div class="tec-astro-chart__summary-formula">${formulaItems.map((item,index) => `${index ? `<span class="tec-astro-chart__summary-chevron" aria-hidden="true">${formulaChevron}</span>` : ''}<div class="tec-astro-chart__summary-step"><strong>${escapeHtml(item.title)}</strong><span>${escapeHtml(item.text)}</span></div>`).join('')}</div><div class="tec-astro-chart__summary-narrative"><p>${escapeHtml(opening)}</p><p>${escapeHtml(houses)}</p><p>${escapeHtml(aspectSummary)}</p></div>`;
      };

      const renderPlanets = (data) => {
        panel.innerHTML = Object.entries(data.planets).map(([name, longitude]) => {
          const signIndex = signFor(longitude);
          const house = houseFor(longitude,data.ascendant);
          const teaser = fillTokens(labels.placementTeaser, { PLANET:labels[name], ACTION:planetActions[name] });
          const signExplanation = fillTokens(labels.placementSign, { SIGN:labels.signs[signIndex], TONE:labels.signTones[signIndex] });
          const houseExplanation = house ? labels.houseMeanings[house-1] : labels.placementHouseUnknown;
          const interpretation = house
            ? fillTokens(labels.planetKnown, {
              PLANET:labels[name], SIGN:labels.signs[signIndex], HOUSE:String(house), ACTION:planetActions[name],
              TONE:labels.signTones[signIndex], AREA:labels.houseMeanings[house-1]
            })
            : fillTokens(labels.planetUnknown, {
              PLANET:labels[name], SIGN:labels.signs[signIndex], ACTION:planetActions[name], TONE:labels.signTones[signIndex]
            });
          const houseMarker = house ? String(house) : '—';
          const placementDetails = `<details class="tec-astro-chart__placement"><summary><span class="tec-astro-chart__placement-open">${escapeHtml(labels.placementOpen)}</span><span class="tec-astro-chart__placement-close">${escapeHtml(labels.placementClose)}</span><span class="tec-astro-chart__placement-toggle-icon" aria-hidden="true">${formulaChevron}</span></summary><div class="tec-astro-chart__placement-body"><div class="tec-astro-chart__placement-map"><div class="tec-astro-chart__placement-step"><span class="tec-astro-chart__placement-marker">${planetMeta[name].glyph}</span><div><strong>${escapeHtml(labels.formulaPlanet)}</strong><p>${escapeHtml(planetMeta[name].role)}</p></div></div><span class="tec-astro-chart__placement-chevron" aria-hidden="true">${formulaChevron}</span><div class="tec-astro-chart__placement-step"><span class="tec-astro-chart__placement-marker">${signs[signIndex].glyph}</span><div><strong>${escapeHtml(labels.formulaSign)}</strong><p>${escapeHtml(signExplanation)}</p></div></div><span class="tec-astro-chart__placement-chevron" aria-hidden="true">${formulaChevron}</span><div class="tec-astro-chart__placement-step"><span class="tec-astro-chart__placement-marker tec-astro-chart__placement-marker--house">${escapeHtml(houseMarker)}</span><div><strong>${escapeHtml(labels.formulaHouse)}</strong><p>${escapeHtml(houseExplanation)}</p></div></div></div><div class="tec-astro-chart__placement-synthesis"><span>${escapeHtml(labels.placementSynthesis)}</span><p>${escapeHtml(interpretation)}</p></div></div></details>`;
          return `<article class="tec-astro-chart__reading-item"><span class="tec-astro-chart__reading-symbol">${planetMeta[name].glyph}</span><div class="tec-astro-chart__reading-copy"><strong>${escapeHtml(labels[name])} · ${escapeHtml(labels.signs[signIndex])}</strong><p class="tec-astro-chart__placement-teaser">${escapeHtml(teaser)}</p></div><small>${formatPosition(longitude)}${house ? `<br>${escapeHtml(labels.house)} ${house}` : ''}</small>${placementDetails}</article>`;
        }).join('');
      };

      const renderHouses = (data) => {
        if (data.ascendant === null) { panel.innerHTML = `<p>${escapeHtml(labels.emptyHouses)}</p>`; return; }
        panel.innerHTML = Array.from({length:12},(_,index) => {
          const cusp = norm(data.ascendant + index*30), signIndex = signFor(cusp);
          const residents = Object.entries(data.planets)
            .filter(([,longitude]) => houseFor(longitude,data.ascendant) === index+1)
            .map(([name]) => ({ name:labels[name], action:planetActions[name] }));
          const area = fillTokens(labels.houseIntro, { HOUSE:String(index+1), AREA:labels.houseMeanings[index] });
          const approach = fillTokens(labels.houseApproach, { SIGN:labels.signs[signIndex], TONE:labels.signTones[signIndex] });
          let activity = fillTokens(labels.houseUnoccupied, { SIGN:labels.signs[signIndex] });
          if (residents.length === 1) activity = fillTokens(labels.houseOccupiedOne, { PLANET:residents[0].name, ACTION:residents[0].action });
          if (residents.length > 1) activity = fillTokens(labels.houseOccupiedMany, {
            PLANETS:residents.map((resident) => resident.name).join(', '),
            ACTIONS:residents.map((resident) => `${resident.name} : ${resident.action}`).join(' ; ')
          });
          return `<article class="tec-astro-chart__reading-item"><span class="tec-astro-chart__reading-symbol tec-astro-chart__reading-symbol--house"><span>${index+1}</span></span><div class="tec-astro-chart__reading-copy"><strong>${escapeHtml(labels.house)} ${index+1} · ${escapeHtml(labels.signs[signIndex])}</strong><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.houseAreaHeading)}</span>${escapeHtml(area)}</p><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.houseApproachHeading)}</span>${escapeHtml(approach)}</p><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.houseActivityHeading)}</span>${escapeHtml(activity)}</p></div><small>${formatPosition(cusp)}</small></article>`;
        }).join('');
      };

      const renderAspects = (data) => {
        const aspects = aspectsFor(data.planets);
        if (!aspects.length) { panel.innerHTML = `<p>${escapeHtml(labels.noAspects)}</p>`; return; }
        panel.innerHTML = aspects.map((aspect) => {
          const connection = fillTokens(labels.aspectConnection, {
            FIRST:labels[aspect.firstName], SECOND:labels[aspect.secondName],
            FIRST_ACTION:planetActions[aspect.firstName], SECOND_ACTION:planetActions[aspect.secondName]
          });
          const firstSignIndex = signFor(aspect.firstLongitude), secondSignIndex = signFor(aspect.secondLongitude);
          const interpretation = fillTokens(labels.aspectInterpretation, {
            FIRST:labels[aspect.firstName], SECOND:labels[aspect.secondName], ASPECT:labels.aspects[aspect.name],
            FIRST_SIGN:labels.signs[firstSignIndex], SECOND_SIGN:labels.signs[secondSignIndex],
            FIRST_TONE:labels.signTones[firstSignIndex], SECOND_TONE:labels.signTones[secondSignIndex],
            FIRST_ACTION:planetActions[aspect.firstName], SECOND_ACTION:planetActions[aspect.secondName],
            EFFECT:labels.aspectMeanings[aspect.name]
          });
          const firstHouse = houseFor(aspect.firstLongitude,data.ascendant), secondHouse = houseFor(aspect.secondLongitude,data.ascendant);
          const areas = firstHouse && secondHouse
            ? fillTokens(labels.aspectAreaKnown, {
              FIRST:labels[aspect.firstName], SECOND:labels[aspect.secondName], FIRST_HOUSE:String(firstHouse), SECOND_HOUSE:String(secondHouse),
              FIRST_AREA:trimSentence(labels.houseMeanings[firstHouse-1]), SECOND_AREA:trimSentence(labels.houseMeanings[secondHouse-1])
            })
            : labels.aspectAreaUnknown;
          return `<article class="tec-astro-chart__reading-item"><span class="tec-astro-chart__reading-symbol">${planetMeta[aspect.firstName].glyph}</span><div class="tec-astro-chart__reading-copy"><strong>${escapeHtml(labels[aspect.firstName])} · ${escapeHtml(labels.aspects[aspect.name])} · ${escapeHtml(labels[aspect.secondName])}</strong><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.aspectConnectionHeading)}</span>${escapeHtml(connection)}</p><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.aspectEffectHeading)}</span>${escapeHtml(interpretation)}</p><p><span class="tec-astro-chart__reading-label">${escapeHtml(labels.aspectAreaHeading)}</span>${escapeHtml(areas)}</p></div><small>${aspect.orbValue.toFixed(1)}°</small></article>`;
        }).join('');
      };

      const activateTab = (name) => {
        generator.querySelectorAll('[data-astro-tab]').forEach((button) => {
          const selected = button.dataset.astroTab === name;
          button.setAttribute('aria-selected', String(selected));
          button.tabIndex = selected ? 0 : -1;
        });
        if (!chart) return;
        if (name === 'houses') { renderHouses(chart); tabHelp.textContent = labels.housesHelp; }
        else if (name === 'aspects') { renderAspects(chart); tabHelp.textContent = labels.aspectsHelp; }
        else { renderPlanets(chart); tabHelp.textContent = labels.planetsHelp; }
      };

      placeButton.addEventListener('click', () => searchPlace());
      placeInput.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); searchPlace(); } });
      placeInput.addEventListener('input', () => {
        predictiveRequest += 1;
        place = null; selectedPlace.hidden = true; clearTimeout(predictiveTimer);
        const query = placeInput.value.trim();
        if (query.length < 3) { placeResults.hidden = true; return; }
        predictiveTimer = window.setTimeout(() => searchPlace({ predictive:true }), 650);
      });
      dateInput.addEventListener('change', refreshOffset);
      timeInput.addEventListener('change', refreshOffset);
      unknownInput.addEventListener('change', () => {
        timeInput.disabled = unknownInput.checked;
        timeInput.required = !unknownInput.checked;
        timeField.classList.toggle('is-disabled',unknownInput.checked);
        refreshOffset();
      });
      generator.querySelectorAll('[data-astro-tab]').forEach((button) => button.addEventListener('click', () => activateTab(button.dataset.astroTab)));
      $('[data-astro-restart]')?.addEventListener('click', () => {
        output.hidden = true;
        form.scrollIntoView({ behavior:'smooth', block:'start' });
      });

      if (generator.matches('[data-astro-trio]')) {
        form.addEventListener('input', () => { output.hidden = true; });
      }
      form.addEventListener('submit', (event) => {
        event.preventDefault(); clearError();
        if (generator.matches('[data-astro-trio]') && !form.reportValidity()) return;
        if (!dateInput.value) { showError(labels.errorDate); dateInput.focus(); return; }
        if (!unknownInput.checked && !timeInput.value) { showError(labels.errorTime); timeInput.focus(); return; }
        if (!place || !Number.isFinite(place.lat) || !Number.isFinite(place.lon)) { showError(labels.errorPlace); placeInput.focus(); return; }
        if (!Number.isFinite(Number(offsetInput.value))) { showError(labels.offsetCheck); offsetInput.focus(); return; }
        chart = calculateChart();
        if (generator.matches('[data-astro-trio]')) {
          chart.trioPolar = Math.abs(chart.place.lat) > 66;
          if (chart.trioPolar) chart.ascendant = null;
          if (chart.unknownTime) {
            chart.trioPossibilities = {sun: [], moon: []};
            for (let hour = -12; hour <= 12; hour += 1) {
              const sample = calculateLongitudes(chart.julianDate + hour / 24);
              ['sun', 'moon'].forEach(key => {
                const index = signFor(sample[key]);
                if (!chart.trioPossibilities[key].includes(index)) chart.trioPossibilities[key].push(index);
              });
            }
          }
          renderAstroTrio(generator, chart, labels, signs);
        } else {
          renderWheel(chart); renderBigThree(chart); renderSummary(chart); activateTab('planets');
          timeNotice.hidden = !chart.unknownTime;
        }
        resultContext.textContent = (chart.unknownTime ? labels.contextUnknown : labels.context).replace('__PLACE__',chart.place.name).replace('__DATE__',chart.date);
        output.hidden = false;
        window.requestAnimationFrame(() => output.scrollIntoView({ behavior:'smooth', block:'start' }));
      });
    });
  };

  const pageToolInitializers = [
    ['[data-tarot-guide]', bindTarotGuides],
    ['[data-tarot-spread-tool]', bindTarotSpreadTools],
    ['[data-tarot-reading]', bindTarotReadings],
    ['[data-wax-reader]', bindWaxReaders],
    ['[data-runes-home]', bindRunesHomes],
    ['[data-lunar-calendar]', bindLunarCalendars],
    ['[data-year-wheel]', bindYearWheels],
    ['[data-astrology-home]', bindAstrologyHomes],
    ['[data-astro-chart]', bindAstrologyChartGenerators]
  ];
  const pageToolSelector = pageToolInitializers.map(([selector]) => selector).join(',');
  const bindPageTools = (root = document) => {
    const tools = [...root.querySelectorAll(pageToolSelector)];
    if (root.matches?.(pageToolSelector)) tools.unshift(root);
    if (!tools.length) return;

    pageToolInitializers.forEach(([selector, initialize]) => {
      if (tools.some(tool => tool.matches(selector))) initialize(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    bindAnimatedLinkUnderlines();
    bindProductInformationTabScroller();
    bindPowderUsageShortcuts();
    bindOilUsageShortcuts();
    bindCandleUsageShortcuts();
    bindSaltUsageShortcuts();
    bindMineralQualityTriggers();
    bindLoyaltyTriggers();
    bootCercleAeternum();

    bindAllRecommendationsPanels();
    bindAllNewsletterSignups();
    bindAllBackInStockAlerts();
    bindFloatingControlsToStickyCart();
    stabilizeShopifyChatActivator();
    bindIntegratedProductCart();
    bindProductBenefitsSliders();
    bindProductCardReviewRatings();
    productCardReviewMobile.addEventListener?.('change', () => bindProductCardReviewRatings());
    bindProductSummaries();
    bindProductReviewEmptyState();
    bindRelatedArticles();
    bindMineralArticleProducts();
    bindAllSameAuthorProducts();
    bindGrimoireHomes();
    bindBotanicalIndexes();
    bindGrimoireProductCardHeights();
    bindHomeNewArrivalsExternalLinks();
    bindHomeNewArrivalsTrackpads();
    bindArcanePantheonStacks();
    bindHomeReviewCarousels();
    bindHomeReviewModals();
    bindNoAiPhotoTriggers();
    bindArticleLikes();
    bindAeternumAllReviews();
    bindPageTools();
    initializeProductPairing();

    const pendingRoots = new Set();
    let observerFrame = null;

    const bindDynamicRoot = (node) => {
      if (node.matches('cart-drawer [data-recommendations-panel] product-recommendations')) {
        bindRecommendationsPanel(node);
      } else {
        bindAllRecommendationsPanels(node);
      }

      if (node.matches('[data-nexus-newsletter-signup]')) {
        bindNewsletterSignup(node);
      } else {
        bindAllNewsletterSignups(node);
      }

      if (node.matches('[data-back-in-stock-alert]')) {
        bindBackInStockAlert(node);
      } else {
        bindAllBackInStockAlerts(node);
      }

      bindProductInformationTabScroller(node);
      bindPowderUsageShortcuts(node);
      bindOilUsageShortcuts(node);
      bindCandleUsageShortcuts(node);
      bindSaltUsageShortcuts(node);
      bindMineralQualityTriggers(node);
      bindLoyaltyTriggers(node);
      bindCercleAeternum();
      bindIntegratedProductCart(node);
      bindProductBenefitsSliders(node);
      bindProductCardReviewRatings(node);
      bindProductSummaries(node);
      bindProductReviewEmptyState(node);
      bindRelatedArticles(node);
      bindMineralArticleProducts(node);
      bindAllSameAuthorProducts(node);
      bindGrimoireHomes(node);
      bindBotanicalIndexes(node);
      bindGrimoireProductCardHeights(node);
      bindHomeNewArrivalsExternalLinks(node);
      bindHomeNewArrivalsTrackpads(node);
      bindArcanePantheonStacks(node);
      bindHomeReviewCarousels(node);
      bindHomeReviewModals(node);
      bindNoAiPhotoTriggers(node);
      bindAnimatedLinkUnderlines(node);
      bindArticleLikes(node);
      bindAeternumAllReviews(node);
      bindPageTools(node);
    };

    const flushPendingRoots = () => {
      observerFrame = null;
      const roots = Array.from(pendingRoots).filter((root) => root.isConnected).filter((root, index, allRoots) => {
        return !allRoots.some((otherRoot) => otherRoot !== root && otherRoot.contains(root));
      });
      pendingRoots.clear();
      roots.forEach(bindDynamicRoot);
    };

    const rootObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement) || !node.isConnected || node.matches('script, style, link, meta, noscript, template')) {
            return;
          }
          pendingRoots.add(node);
        });
      });

      if (pendingRoots.size > 0 && observerFrame === null) {
        observerFrame = window.requestAnimationFrame(flushPendingRoots);
      }
    });

    rootObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
})();

(() => {
  let observer;
  let cancelHomeScroll;
  const initHomeMarkers = (root = document) => {
    if (!document.body.classList.contains('template-index')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    document.body.classList.add('tec-home-markers-ready');
    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll('.animated-marker').forEach((marker) => marker.classList.add('tec-home-marker--visible'));
          observer.unobserve(entry.target);
        });
      }, {threshold: 0.75, rootMargin: '-100px 0px -60px 0px'});
    }
    root.querySelectorAll('#main-content .animated-marker').forEach((marker) => {
      const heading = marker.closest('h1, h2, h3, h4, h5, h6');
      if (!heading || heading.dataset.homeMarkerBound || heading.closest('[id$="__16614195534fb74bc3"]')) return;
      heading.dataset.homeMarkerBound = 'true';
      observer.observe(heading);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initHomeMarkers(), {once: true});
  else initHomeMarkers();
  document.addEventListener('shopify:section:load', (event) => initHomeMarkers(event.target));
  document.addEventListener('click', (event) => {
    if (!document.body.classList.contains('template-index')) return;
    const link = event.target.closest('a[data-home-scroll]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = document.getElementById(link.hash.slice(1));
    if (!target) return;
    event.preventDefault();
    cancelHomeScroll?.();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'start'});
    history.pushState(null, '', link.hash);
    if (!reducedMotion) {
      let settleTimer;
      let expiryTimer;
      let layoutObserver;
      let settled = false;
      const alignTarget = () => {
        const current = document.getElementById(link.hash.slice(1));
        if (!current) return;
        const headerBottom = Math.max(0, document.querySelector('.header')?.getBoundingClientRect().bottom || 0);
        const offset = current.getBoundingClientRect().top - headerBottom - 25;
        if (Math.abs(offset) > 2) window.scrollTo({top: window.scrollY + offset, behavior: 'instant'});
      };
      const stop = () => {
        window.clearTimeout(settleTimer);
        window.clearTimeout(expiryTimer);
        layoutObserver?.disconnect();
        document.removeEventListener('scrollend', settle);
        ['wheel', 'touchstart', 'keydown'].forEach((name) => document.removeEventListener(name, stop));
      };
      const settle = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(settleTimer);
        document.removeEventListener('scrollend', settle);
        alignTarget();
        if ('ResizeObserver' in window) {
          layoutObserver = new ResizeObserver(alignTarget);
          layoutObserver.observe(document.getElementById('main-content'));
        }
        expiryTimer = window.setTimeout(stop, 3000);
      };
      cancelHomeScroll = stop;
      document.addEventListener('scrollend', settle, {once: true});
      ['wheel', 'touchstart', 'keydown'].forEach((name) => document.addEventListener(name, stop, {once: true, passive: true}));
      settleTimer = window.setTimeout(settle, 1200);
    }
  });
})();

(() => {
  const newArrivalsSelector = '[id$="__16614195534fb74bc3"]';
  const sectionSelector = [
    newArrivalsSelector,
    '[id$="__featured_collection_kcw3LX"]',
    '[id$="__collection_tabs_VQXDVX"]',
    '.tec-intention__shop'
  ].join(',');
  const scheduled = new WeakMap();

  function findSections(scope = document) {
    const sections = Array.from(scope.querySelectorAll?.(sectionSelector) || []);
    if (scope.matches?.(sectionSelector)) sections.unshift(scope);
    return sections;
  }

  function resizeCarousel(rail) {
    const carousel = rail._flkty || window.Flickity?.data?.(rail);
    if (!carousel) return;
    carousel.resize();
    carousel.reposition();
  }

  function normalizeSecondaryCards(rail) {
    if (rail.closest(newArrivalsSelector)) {
      resizeCarousel(rail);
      return;
    }

    rail.style.removeProperty('--aeternum-home-secondary-card-height');

    requestAnimationFrame(() => {
      const cards = Array.from(
        rail.querySelectorAll('.carousel__slide:not(.aeternum-home-product-lead) > .product-card')
      );
      if (!cards.length) return;

      const height = Math.max(...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)));
      rail.style.setProperty('--aeternum-home-secondary-card-height', `${height}px`);
      requestAnimationFrame(() => resizeCarousel(rail));
    });
  }

  function scheduleNormalization(rail, delay = 0) {
    window.clearTimeout(scheduled.get(rail));
    scheduled.set(rail, window.setTimeout(() => normalizeSecondaryCards(rail), delay));
  }

  function initHomeProductRails(scope = document) {
    findSections(scope).forEach((section) => {
      const rail = section.querySelector('.products.carousel');
      if (!rail) return;

      if (!rail.dataset.aeternumHeightSync) {
        rail.dataset.aeternumHeightSync = 'true';

        const sizeObserver = section.matches(newArrivalsSelector)
          ? new ResizeObserver(() => scheduleNormalization(rail))
          : null;
        const observeCards = () => {
          rail.querySelectorAll('.carousel__slide > .product-card').forEach((card) => sizeObserver?.observe(card));
        };
        observeCards();

        const observer = new MutationObserver(() => {
          observeCards();
          scheduleNormalization(rail, 80);
        });
        observer.observe(rail, { childList: true, subtree: true });

        section.addEventListener('contentvisibilityautostatechange', (event) => {
          if (!event.skipped) scheduleNormalization(rail);
        });

        section.querySelectorAll('.collection-tabs__list-link').forEach((button) => {
          button.addEventListener('click', () => {
            scheduleNormalization(rail, 120);
            window.setTimeout(() => scheduleNormalization(rail), 600);
          });
        });
      }

      scheduleNormalization(rail);
    });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      findSections().forEach((section) => {
        const rail = section.querySelector('.products.carousel');
        if (rail) scheduleNormalization(rail);
      });
    }, 180);
  });

  document.addEventListener('load', (event) => {
    const rail = event.target.closest?.('.products.carousel');
    if (rail?.closest(sectionSelector)) scheduleNormalization(rail, 40);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHomeProductRails());
  } else {
    initHomeProductRails();
  }
  window.addEventListener('load', () => initHomeProductRails());
  document.fonts?.ready.then(() => initHomeProductRails());
  document.addEventListener('shopify:section:load', (event) => initHomeProductRails(event.target));
})();
;(() => {
  function initAstroTutorial(scope = document) {
    const pages = [...scope.querySelectorAll('[data-astro-learn]')];
    if (scope.matches?.('[data-astro-learn]')) pages.unshift(scope);
    pages.forEach((page) => {
      if (page.dataset.learnBound) return;
      const strings = JSON.parse(page.querySelector('[data-learn-strings]').textContent);
      page.dataset.learnBound = 'true';
      page.classList.add('is-enhanced');
      const toc = page.querySelector('.tec-astro-learn__toc');
      const tocMobile = window.matchMedia('(max-width: 767px)');
      const syncTocViewport = () => { toc.open = !tocMobile.matches; };
      syncTocViewport();
      tocMobile.addEventListener('change', syncTocViewport);
      page.querySelectorAll('.tec-astro-learn__toc a').forEach((link) => {
        link.addEventListener('click', () => {
          if (window.matchMedia('(max-width: 767px)').matches) toc.open = false;
        });
      });
      const wheel = page.querySelector('[data-learn-wheel]');
      const chooseLayer = (layer) => {
        wheel.querySelector('[data-learn-diagram]').dataset.activeLayer = layer;
        wheel.querySelectorAll('[data-learn-choose]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.learnChoose === layer)));
        wheel.querySelectorAll('[data-learn-explanation]').forEach((copy) => { copy.hidden = copy.dataset.learnExplanation !== layer; });
      };
      wheel.querySelectorAll('[data-learn-choose]').forEach((button) => button.addEventListener('click', () => chooseLayer(button.dataset.learnChoose)));
      chooseLayer('planets');
      const angle = page.querySelector('[data-learn-angles]');
      const angleSelect = angle.querySelector('[data-learn-angle-select]');
      const updateAngle = () => {
        const degrees = Number(angleSelect.value);
        const radians = degrees * Math.PI / 180;
        const x = 160 + 118 * Math.cos(radians);
        const y = 160 - 118 * Math.sin(radians);
        const ray = angle.querySelector('[data-learn-ray]');
        ray.setAttribute('x2', x.toFixed(2));
        ray.setAttribute('y2', y.toFixed(2));
        const dot = angle.querySelector('[data-learn-dot]');
        dot.setAttribute('cx', x.toFixed(2));
        dot.setAttribute('cy', y.toFixed(2));
        angle.querySelector('[data-learn-arc]').setAttribute('d', degrees === 0 ? '' : `M200 160 A40 40 0 0 0 ${(160 + 40 * Math.cos(radians)).toFixed(2)} ${(160 - 40 * Math.sin(radians)).toFixed(2)}`);
        angle.querySelector('[data-learn-degrees]').textContent = `${degrees}°`;
        angle.querySelectorAll('[data-learn-angle-copy]').forEach((copy) => { copy.hidden = Number(copy.dataset.learnAngleCopy) !== degrees; });
      };
      angleSelect.addEventListener('change', updateAngle);
      updateAngle();
      const lab = page.querySelector('[data-learn-lab]');
      const updateLab = () => {
        let result = strings.formula;
        ['planet', 'sign', 'house'].forEach((part) => {
          const select = lab.querySelector(`[data-learn-lab-select="${part}"]`);
          result = result.replace(`__${part.toUpperCase()}__`, select.selectedOptions[0].dataset.phrase);
          lab.querySelector(`[data-learn-selected="${part}"]`).textContent = select.selectedOptions[0].dataset.phrase;
        });
        lab.querySelector('[data-learn-lab-output]').textContent = result;
      };
      lab.querySelectorAll('select').forEach((select) => select.addEventListener('change', updateLab));
      updateLab();
      const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/œ/g, 'oe');
      const definitions = [...page.querySelectorAll('[data-learn-definition]')].map((element) => ({ element, text: normalize(element.textContent) }));
      const search = page.querySelector('[data-learn-search]');
      search.addEventListener('input', () => {
        const query = normalize(search.value.trim());
        let count = 0;
        definitions.forEach(({ element, text }) => {
          element.hidden = !text.includes(query);
          if (!element.hidden) count++;
        });
        page.querySelector('[data-learn-count]').textContent = String(count);
        page.querySelector('[data-learn-empty]').hidden = count !== 0;
      });
      const quizzes = [...page.querySelectorAll('[data-learn-quiz]')];
      const resetQuizzes = () => quizzes.forEach((quiz) => {
        quiz.querySelectorAll('input').forEach((input) => { input.checked = false; });
        quiz.querySelector('[data-learn-feedback]').hidden = true;
      });
      quizzes.forEach((quiz) => quiz.addEventListener('change', (event) => {
        const correct = event.target.value === quiz.dataset.learnQuiz;
        quiz.querySelector('[data-learn-feedback]').hidden = false;
        quiz.querySelector('[data-learn-correct]').hidden = !correct;
        quiz.querySelector('[data-learn-incorrect]').hidden = correct;
      }));
      page.querySelector('[data-learn-reset]').addEventListener('click', resetQuizzes);
      resetQuizzes();
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (!visible.length) return;
          const id = visible[0].target.id;
          toc.querySelectorAll('a').forEach((link) => {
            if (link.hash === `#${id}`) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
          });
        }, { rootMargin: '-140px 0px -55% 0px' });
        page.querySelectorAll('.tec-astro-learn__chapter[id]').forEach((chapter) => observer.observe(chapter));
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(page)) observer.disconnect();
        }, { once: true });
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAstroTutorial());
  else initAstroTutorial();
  document.addEventListener('shopify:section:load', (event) => initAstroTutorial(event.target));
})();
;(() => {
  function initTutorialDiagrams(scope=document) {
    const roots=[...scope.querySelectorAll('[data-astro-learn]')];
    if(scope.matches?.('[data-astro-learn]')) roots.unshift(scope);
    roots.forEach(root=>{
      if(root.dataset.diagramsBound) return;
      root.dataset.diagramsBound='true';
      const elements=root.querySelector('[data-learn-elements]');
      if(!elements) return;
      function signChoice(number) {
        const target=elements.querySelector(`[data-learn-sign="${number}"]`);
        const family=target.dataset.element;
        elements.querySelectorAll('[data-learn-sign]').forEach(button=>{button.setAttribute('aria-pressed',String(button===target));button.classList.toggle('is-family',button.dataset.element===family);});
        elements.querySelectorAll('[data-learn-element]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.learnElement===family)));
        elements.querySelectorAll('[data-learn-sign-copy]').forEach(copy=>{copy.hidden=copy.dataset.learnSignCopy!==String(number);});
      }
      elements.querySelectorAll('[data-learn-sign]').forEach(button=>button.addEventListener('click',()=>signChoice(button.dataset.learnSign)));
      elements.querySelectorAll('[data-learn-element]').forEach(button=>button.addEventListener('click',()=>signChoice(Number(button.dataset.learnElement)+1)));
      signChoice(1);
      const houses=root.querySelector('[data-learn-house-map]');
      function houseChoice(number) {
        houses.querySelectorAll('[data-learn-house]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.learnHouse===String(number))));
        houses.querySelectorAll('[data-learn-house-copy]').forEach(copy=>{copy.hidden=copy.dataset.learnHouseCopy!==String(number);});
      }
      houses.querySelectorAll('[data-learn-house]').forEach(button=>button.addEventListener('click',()=>houseChoice(button.dataset.learnHouse)));
      houseChoice(1);
      const transit=root.querySelector('[data-learn-transit]'),range=transit.querySelector('[data-transit-range]');
      const aspectNames={};
      root.querySelectorAll('[data-learn-angle-select] option').forEach(option=>{aspectNames[option.value]=option.textContent;});
      function updateTransit() {
        const longitude=Number(range.value),angle=Math.min(longitude,360-longitude),rad=longitude*Math.PI/180;
        const x=160+110*Math.cos(rad),y=160-110*Math.sin(rad);
        const dot=transit.querySelector('[data-transit-dot]'),ray=transit.querySelector('[data-transit-ray]');
        dot.setAttribute('cx',x);dot.setAttribute('cy',y);ray.setAttribute('x2',x);ray.setAttribute('y2',y);
        transit.querySelector('[data-transit-arc]').setAttribute('d',angle===0?'':`M200 160 A40 40 0 0 ${longitude>180?1:0} ${160+40*Math.cos(rad)} ${160-40*Math.sin(rad)}`);
        transit.querySelector('[data-transit-degrees]').textContent=`${angle}°`;
        transit.querySelector('[data-transit-aspect]').textContent=aspectNames[angle]||transit.querySelector('[data-transit-between]').textContent;
      }
      range.addEventListener('input',updateTransit);updateTransit();
    });
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>initTutorialDiagrams());else initTutorialDiagrams();
  document.addEventListener('shopify:section:load',event=>initTutorialDiagrams(event.target));
})();

(() => {
  function initEsoteric(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const strings = JSON.parse(root.querySelector('[data-eh-strings]').textContent);
    const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const classify = title => {
      const s = normalize(title);
      if (/tarot|oracle|rune|pendule|cauri|divination|miroir.*vision|marc de cafe/.test(s)) return 'divination';
      if (/hoodoo|vaudou|litha|beltane|neuvaine|saint |saint-|marie qui|eglise|eau benite|tradition chretienne/.test(s)) return 'traditions';
      if (/bougie|dagyde|cire|paraffine|flamme/.test(s)) return 'candles';
      if (/amethyste|crist|pierre|minera|orgonite|nazar|œil turc|oeil turc|talisman|amulette|bijou/.test(s)) return 'stones';
      if (/encens|fumigation|palo santo|herbe|fleur|rose de jericho|resine|huile|eau de |eau d|cascarilla|poudre|riz magique/.test(s)) return 'plants';
      return 'ritual';
    };
    const status = root.querySelector('[data-eh-status]');
    const results = root.querySelector('[data-eh-results]');
    const controls = root.querySelector('.tec-eh__controls');
    const pagination = root.querySelector('[data-eh-pagination]');
    const search = root.querySelector('#eh-search');
    const retry = root.querySelector('[data-eh-retry]');
    let data = [], active = 'all', page = 1;
    function render() {
      const terms = normalize(search.value.trim()).split(/\s+/).filter(Boolean);
      const filtered = data.filter(a => (active === 'all' || a.group === active) && terms.every(term => normalize(a.title).includes(term)));
      const pages = Math.max(1, Math.ceil(filtered.length / 12));
      page = Math.min(page, pages);
      const frag = document.createDocumentFragment();
      filtered.slice((page - 1) * 12, page * 12).forEach(a => {
        const card = root.querySelector('[data-eh-card]').content.cloneNode(true);
        card.querySelectorAll('a').forEach(link => { link.href = a.url; });
        const image = card.querySelector('img');
        if (a.image) image.src = a.image; else image.remove();
        card.querySelector('h3 a').textContent = a.title;
        card.querySelector('.tec-eh__label').textContent = strings.groups[a.group];
        card.querySelector('[data-eh-read]').textContent = strings.read;
        frag.appendChild(card);
      });
      results.replaceChildren(frag);
      status.textContent = filtered.length ? `${filtered.length} ${strings.count}` : strings.empty;
      const numbers = document.createDocumentFragment();
      const windowSize = matchMedia('(max-width:700px)').matches ? 3 : 9;
      let start = Math.max(1, page - Math.floor(windowSize / 2));
      const end = Math.min(pages, start + windowSize - 1);
      start = Math.max(1, end - windowSize + 1);
      const addPage = n => {
        const item = document.createElement(n === page ? 'span' : 'button');
        item.textContent = n;
        if (n === page) item.setAttribute('aria-current', 'page');
        else { item.type = 'button'; item.dataset.ehPage = n; }
        numbers.appendChild(item);
      };
      const dots = () => { const item = document.createElement('span'); item.textContent = '…'; item.className = 'tec-eh__ellipsis'; item.setAttribute('aria-hidden', 'true'); numbers.appendChild(item); };
      if (start > 1) { addPage(1); if (start > 2) dots(); }
      for (let n = start; n <= end; n++) addPage(n);
      if (end < pages) { if (end < pages - 1) dots(); addPage(pages); }
      root.querySelector('[data-eh-pages]').replaceChildren(numbers);
      root.querySelector('[data-eh-prev]').disabled = page === 1;
      root.querySelector('[data-eh-next]').disabled = page === pages;
      pagination.hidden = filtered.length <= 12;
      root.querySelectorAll('[data-eh-filter]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.ehFilter === active)));
    }
    function setGroup(group) { active = group; page = 1; if (data.length) render(); }
    controls.addEventListener('click', e => { const b = e.target.closest('[data-eh-filter]'); if (b) setGroup(b.dataset.ehFilter); });
    root.querySelectorAll('[data-eh-filter-link]').forEach(link => link.addEventListener('click', () => { search.value = ''; setGroup(link.dataset.ehFilterLink); }));
    search.addEventListener('input', () => { page = 1; render(); });
    root.querySelectorAll('[data-eh-prev],[data-eh-next]').forEach(button => button.addEventListener('click', () => { page += button.hasAttribute('data-eh-next') ? 1 : -1; render(); root.querySelector('#eh-catalogue').scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'}); }));
    pagination.addEventListener('click', e => {
      const button = e.target.closest('[data-eh-page]');
      if (!button) return;
      page = Number(button.dataset.ehPage);
      render();
      root.querySelector('#eh-catalogue').scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    });
    matchMedia('(max-width:700px)').addEventListener('change', () => { if (data.length) render(); });
    async function load() {
      retry.hidden = true;
      status.textContent = strings.loading;
      try {
        const url = new URL(root.dataset.blogUrl, location.origin);
        url.searchParams.set('view', 'esoteric-data');
        if (window.Shopify?.theme?.role !== 'main') url.searchParams.set('preview_theme_id', root.dataset.themeId);
        let all = [], totalPages = 1;
        for (let n = 1; n <= totalPages; n++) {
          url.searchParams.set('page', n);
          const response = await fetch(url, {credentials:'same-origin'});
          if (!response.ok) throw new Error('Catalogue request failed');
          const payload = await response.json();
          totalPages = payload.pages;
          all.push(...payload.articles);
        }
        data = all.map(a => ({...a, group:classify(a.title)}));
        controls.hidden = false;
        render();
      } catch (error) { status.textContent = strings.error; retry.hidden = false; }
    }
    retry.addEventListener('click', load);
    load();
  }
  function start() { document.querySelectorAll('[data-esoteric-home]').forEach(initEsoteric); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', start);
})();


(() => {
  const bind = () => document.querySelectorAll('[data-practice]').forEach(module => {
    const track = module.querySelector('.tec-practice__grid');
    if (!track || track.dataset.sliderBound) return;
    track.dataset.sliderBound = 'true';
    const prev = module.querySelector('[data-practice-prev]');
    const next = module.querySelector('[data-practice-next]');
    const update = () => {
      const progress = Math.min(100, Math.max(0, (track.scrollLeft + track.clientWidth) / Math.max(1, track.scrollWidth) * 100));
      module.style.setProperty('--practice-progress', `${progress}%`);
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    [prev, next].forEach((button, i) => button.addEventListener('click', () => {
      const item = track.querySelector('.tec-practice__item');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({left: (i ? 1 : -1) * (item.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }));
    track.addEventListener('scroll', update, {passive:true});
    new ResizeObserver(update).observe(track);
    update();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, {once:true});
  else bind();
  document.addEventListener('shopify:section:load', bind);
})();


(() => {
  const init = async (root) => {
    if (root.dataset.repliesBound) return;
    root.dataset.repliesBound = 'true';
    const targetId = new URL(location.href).searchParams.get('aeternum_reply');
    const node = (tag, className, text) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    };
    const render = (reply, parent) => {
      const item = node('div', 'aeternum-team-reply');
      item.id = `AeternumReply-${reply.id}`;
      item.dataset.aeternumReplyId = reply.id;
      const heading = node('div', 'aeternum-team-reply__header');
      const icon = node('span', 'aeternum-team-reply__icon', 'A');
      icon.setAttribute('aria-hidden', 'true');
      heading.append(icon, node('strong', '', root.dataset.teamLabel), node('span', 'aeternum-team-reply__badge', root.dataset.badgeLabel));
      const date = node('time', 'aeternum-team-reply__date');
      date.dateTime = reply.createdAt;
      try { date.textContent = new Intl.DateTimeFormat(root.dataset.locale, { dateStyle: 'medium' }).format(new Date(reply.createdAt)); }
      catch { date.textContent = reply.createdAt.slice(0, 10); }
      item.append(heading, node('p', 'aeternum-team-reply__body', reply.body), date);
      parent.append(item);
    };
    try {
      const endpoint = new URL(root.dataset.endpoint);
      const localPreview = ['127.0.0.1', 'localhost', '::1'].includes(location.hostname) && endpoint.hostname === location.hostname;
      if (endpoint.protocol !== 'https:' && !(localPreview && endpoint.protocol === 'http:')) return;
      endpoint.pathname = '/api/public/replies';
      endpoint.search = '';
      endpoint.searchParams.set('article_id', root.dataset.articleId);
      const commentIds = Array.from(root.querySelectorAll('[data-aeternum-comment-id]')).map((item) => item.dataset.aeternumCommentId);
      if (commentIds.length) endpoint.searchParams.set('comment_ids', commentIds.join(','));
      if (targetId && /^[a-f0-9-]{36}$/.test(targetId)) endpoint.searchParams.set('reply_id', targetId);
      const response = await fetch(endpoint, { credentials: 'omit', signal: AbortSignal.timeout(8000) });
      if (!response.ok) return;
      const data = await response.json();
      for (const reply of data.replies || []) {
        if (!/^[a-f0-9-]{36}$/.test(reply.id) || !/^\d+$/.test(reply.commentId) || typeof reply.body !== 'string') continue;
        const parent = root.querySelector(`[data-aeternum-comment-id="${reply.commentId}"] .comment-body`);
        if (parent) render(reply, parent);
        else if (reply.id === targetId && reply.comment) {
          const target = node('article', 'aeternum-target-conversation');
          target.append(node('h5', '', root.dataset.targetLabel), node('strong', '', reply.comment.author), node('p', 'aeternum-target-conversation__body', reply.comment.body));
          render(reply, target);
          root.prepend(target);
        }
      }
      if (targetId) {
        const target = root.querySelector(`[data-aeternum-reply-id="${targetId.replace(/[^a-f0-9-]/g, '')}"]`);
        if (target) target.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    } catch {}
  };
  const start = () => document.querySelectorAll('[data-aeternum-blog-replies]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
(() => {
  const start = () => {
    document.querySelectorAll('[data-julio-directory]').forEach(root => {
      if (root.dataset.julioBound) return;
      root.dataset.julioBound = 'true';
      const search = root.querySelector('[data-julio-search]');
      const cards = Array.from(root.querySelectorAll('[data-julio-card]'));
      const filters = Array.from(root.querySelectorAll('[data-julio-filter]'));
      const sealPool = cards.filter(card => card.dataset.julioSealSrc);
      const heroSeals = Array.from(root.querySelectorAll('[data-julio-hero-seal] img'));
      for (const image of heroSeals) {
        if (!sealPool.length) break;
        const [card] = sealPool.splice(Math.floor(Math.random() * sealPool.length), 1);
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.width = Number(card.dataset.julioSealWidth);
        image.height = Number(card.dataset.julioSealHeight);
        const sealRatio = image.width / image.height;
        image.parentElement.classList.toggle('is-round', sealRatio >= .9 && sealRatio <= 1.1);
        image.src = card.dataset.julioSealSrc;
      }
      const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
      let intention = 'all';
      const update = () => {
        const query = normalize(search.value.trim());
        let count = 0;
        for (const card of cards) {
          const matchesIntention = intention === 'all' || card.dataset.intentions.split(' ').includes(`julio-intention-${intention}`);
          card.hidden = !(matchesIntention && normalize(card.dataset.search).includes(query));
          if (!card.hidden) count++;
        }
        root.querySelector('[data-julio-count]').textContent = (count === 1 ? root.dataset.countOne : root.dataset.countOther).replace('[count]', count);
        root.querySelector('[data-julio-empty]').hidden = count !== 0;
        filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.julioFilter === intention)));
      };
      search.addEventListener('input', update);
      filters.forEach(button => button.addEventListener('click', () => { intention = button.dataset.julioFilter; update(); }));
      root.querySelector('[data-julio-reset]').addEventListener('click', () => { search.value = ''; intention = 'all'; update(); search.focus(); });
      update();
    });
    document.querySelectorAll('[data-julio-article]').forEach(root => {
      if (root.dataset.julioBound) return;
      root.dataset.julioBound = 'true';
      const dialog = root.querySelector('[data-julio-dialog]');
      root.querySelectorAll('[data-julio-enlarge]').forEach(button => button.addEventListener('click', () => dialog.showModal()));
      root.querySelector('[data-julio-close]').addEventListener('click', () => dialog.close());
      const download = root.querySelector('[data-julio-download]');
      download.addEventListener('click', async event => {
        event.preventDefault();
        if (download.getAttribute('aria-busy') === 'true') return;
        const status = root.querySelector('[data-julio-download-status]');
        if (status) status.hidden = true;
        download.setAttribute('aria-busy', 'true');
        download.setAttribute('aria-disabled', 'true');
        let decoded;
        try {
          const response = await fetch(download.href, {credentials: 'omit', signal: AbortSignal.timeout(10000)});
          if (!response.ok) throw new Error('Image download failed');
          let file = await response.blob();
          if (download.dataset.downloadSignature) {
            decoded = await createImageBitmap(file);
            const canvas = document.createElement('canvas');
            const fontSize = Math.max(12, Math.min(40, Math.round(decoded.width / 38)));
            const footerHeight = Math.max(44, fontSize * 3);
            canvas.width = decoded.width;
            canvas.height = decoded.height + footerHeight;
            const context = canvas.getContext('2d');
            if (!context) throw new Error('Canvas unavailable');
            context.drawImage(decoded, 0, 0);
            context.fillStyle = '#f6f5ef';
            context.fillRect(0, decoded.height, canvas.width, footerHeight);
            context.fillStyle = '#435b51';
            context.font = `${fontSize}px Arial, sans-serif`;
            context.textAlign = 'center';
            context.textBaseline = 'middle';
            context.fillText(download.dataset.downloadSignature, canvas.width / 2, decoded.height + footerHeight / 2, canvas.width - 24);
            file = await new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('PNG export failed')), 'image/png'));
          }
          const url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.download = download.download;
          document.body.append(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch {
          if (status) {
            status.textContent = download.dataset.downloadError;
            status.hidden = false;
          } else {
            window.open(download.href, '_blank', 'noopener');
          }
        } finally {
          decoded?.close();
          download.removeAttribute('aria-busy');
          download.removeAttribute('aria-disabled');
        }
      });
      dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
  document.addEventListener('shopify:section:load', start);
})();

// Recueil chrétien: one directory, existing URLs, and an isolated CMS review in development themes.
(() => {
  const normalize = value => (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/œ/g, 'oe');
  let draftPromise;
  const drafts = root => {
    if (!root.dataset.christianDrafts || !document.documentElement.lang.startsWith('fr')) return Promise.resolve(null);
    if (!draftPromise) draftPromise = fetch(root.dataset.christianDrafts).then(response => response.ok ? response.json() : null).catch(() => null);
    return draftPromise;
  };
  const localPath = (root, path) => `${(root.dataset.rootUrl || '/').replace(/\/$/, '')}${path}`;
  const previewPath = (root, entry) => entry.existing ? localPath(root, entry.path) : `${localPath(root, '/blogs/sainte-trinite/jesus-christ')}?recueil_notice=${encodeURIComponent(entry.handle)}`;
  const initDirectory = root => {
    if (root.dataset.christianReady) return;
    root.dataset.christianReady = 'true';
    const search = root.querySelector('[data-christian-search]');
    const cards = [...root.querySelectorAll('[data-christian-card]')];
    const controls = [...root.querySelectorAll('[data-christian-filter]')];
    let family = 'all';
    const update = () => {
      const query = normalize(search?.value).trim();
      let count = 0;
      cards.forEach(card => {
        const matches = (family === 'all' || card.dataset.family === family) && normalize(card.dataset.search).includes(query);
        card.hidden = !matches;
        if (matches) count++;
      });
      controls.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.christianFilter === family)));
      const countNode = root.querySelector('[data-christian-count]');
      if (countNode) countNode.textContent = `${count} ${count === 1 ? root.dataset.resultOne : root.dataset.resultsLabel}`;
      const empty = root.querySelector('[data-christian-empty]');
      if (empty) empty.hidden = count > 0;
      const note = root.querySelector('[data-christian-history-note]');
      if (note) note.hidden = family !== 'history';
    };
    search?.addEventListener('input', update);
    controls.forEach(button => button.addEventListener('click', () => { family = button.dataset.christianFilter; update(); }));
    root.querySelectorAll('[data-christian-chapter]').forEach(link => link.addEventListener('click', () => { family = link.dataset.christianChapter; if (search) search.value = ''; update(); }));
    root.querySelector('[data-christian-reset]')?.addEventListener('click', () => { family = 'all'; if (search) { search.value = ''; search.focus(); } update(); });
    update();
    drafts(root).then(data => {
      if (!data) return;
      const grid = root.querySelector('[data-christian-grid]');
      const template = root.querySelector('[data-christian-card-template]');
      const added = document.createDocumentFragment();
      data.articles.forEach(entry => {
        if (root.dataset.directoryBlog && entry.blog !== root.dataset.directoryBlog) return;
        let card = cards.find(node => new URL(node.querySelector('a').href).pathname.endsWith(entry.path));
        if (!card && !entry.existing && template) {
          card = template.content.firstElementChild.cloneNode(true);
          card.querySelector('a').href = previewPath(root, entry);
          added.append(card); cards.push(card);
        }
        if (!card) return;
        card.dataset.family = entry.family;
        card.dataset.search = `${entry.title} ${entry.summary} ${entry.kind}`;
        card.querySelector('h3').textContent = entry.title;
        const art = root.querySelector(`template[data-christian-art="${CSS.escape(entry.handle)}"]`);
        if (art?.content.firstElementChild) card.querySelector('.tec-botanical-index__card-media').replaceWith(art.content.firstElementChild.cloneNode(true));
        const label = controls.find(button => button.dataset.christianFilter === entry.family)?.textContent;
        card.querySelector('.tec-botanical-index__card-category').textContent = label || '';
      });
      grid.prepend(added);
      root.dataset.editorialPreview = 'true';
      update();
    });
  };
  const initArticle = async root => {
    if (root.dataset.christianReady) return;
    root.dataset.christianReady = 'true';
    const content = root.querySelector('[data-christian-content]');
    if (!content) return;
    const requested = new URLSearchParams(location.search).get('recueil_notice');
    const heroArt = requested && root.querySelector(`template[data-christian-hero-template="${CSS.escape(requested)}"]`);
    if (heroArt?.content.firstElementChild) {
      const hero = document.querySelector('.tec-christian-article-page [data-aeternum-unified-article-hero]');
      hero?.querySelector('.tec-mineral-hero__media')?.remove();
      hero?.prepend(heroArt.content.firstElementChild.cloneNode(true));
      hero?.classList.remove('tec-mineral-hero--without-image');
    }
    const data = await drafts(root);
    if (data) {
      const draft = data.articles.find(entry => requested ? (!entry.existing && entry.handle === requested) : root.dataset.articlePath.endsWith(entry.path));
      if (draft?.body) {
        content.innerHTML = draft.body;
        root.dataset.editorialPreview = 'true';
        root.dataset.previewHandle = draft.handle;
        const stickyTitle = document.querySelector('.blog-post-detail--navigation--title > div');
        if (stickyTitle) stickyTitle.textContent = draft.title;
        if (!draft.existing) document.querySelector('.blog-post-detail--navigation .share-article')?.remove();
        const hero = document.querySelector('.tec-christian-article-page [data-aeternum-unified-article-hero]');
        if (hero) {
          hero.querySelector('h1').textContent = draft.title;
          hero.querySelector('.tec-mineral-hero__eyebrow').textContent = draft.kind;
          // A new editorial guide must not inherit Jesus' portrait or published engagement counters.
          if (!draft.existing) {
            hero.classList.add('tec-christian-preview-guide');
            hero.querySelector('.tec-mineral-hero__media:not([data-christian-hero-art])')?.remove();
            hero.querySelector('.tec-mineral-hero__sharing')?.remove();
            hero.querySelector('.tec-mineral-hero__meta')?.remove();
            const crumbs = document.querySelector('.breadcrumbs');
            if (crumbs) {
              const ancestors = [...crumbs.querySelectorAll('a')].slice(0, 2).map(link => link.cloneNode(true));
              crumbs.replaceChildren();
              ancestors.forEach(link => { crumbs.append(link, document.createTextNode(' / ')); });
              const current = document.createElement('span');
              current.setAttribute('aria-current', 'page'); current.textContent = draft.title; crumbs.append(current);
            }
            document.title = `${draft.title} | Aeternum`;
            const track = document.querySelector('.tec-christian-article-page .tec-mineral-related-articles__track');
            const cardModel = track?.querySelector('a');
            if (track && cardModel) {
              const model = cardModel.cloneNode(true);
              track.replaceChildren();
              data.articles.filter(entry => !entry.existing && entry.family === draft.family && entry.handle !== draft.handle).slice(0, 8).forEach(entry => {
                const card = model.cloneNode(true);
                card.dataset.christianPreviewLink = 'true';
                card.href = previewPath(root, entry); card.setAttribute('aria-label', entry.title);
                card.querySelector('.tec-mineral-related-article-card__title').textContent = entry.title;
                card.querySelector('.tec-mineral-related-article-card__media').replaceChildren();
                const symbol = root.querySelector('[data-christian-related-symbol]');
                if (symbol) card.querySelector('.tec-mineral-related-article-card__media').append(symbol.content.cloneNode(true));
                track.append(card);
              });
            }
          }
        }
        document.querySelectorAll('.tec-christian-article-page .tec-mineral-related-article-card').forEach(card => {
          if (card.dataset.christianPreviewLink) return;
          const related = data.articles.find(entry => new URL(card.href).pathname.endsWith(entry.path));
          if (related?.existing) {
            if (related.family !== draft.family) { card.remove(); return; }
            card.querySelector('.tec-mineral-related-article-card__title').textContent = related.title;
            card.setAttribute('aria-label', related.title);
          }
        });
        const relatedSection = document.querySelector('.tec-christian-article-page .tec-mineral-related-articles');
        if (relatedSection) relatedSection.hidden = !relatedSection.querySelector('.tec-mineral-related-article-card');
        content.querySelectorAll('a[href^="/blogs/"]').forEach(link => {
          const target = data.articles.find(entry => entry.path === link.getAttribute('href'));
          link.href = target ? previewPath(root, target) : localPath(root, link.getAttribute('href'));
        });
      }
    }
    const list = root.querySelector('[data-christian-toc] ol');
    const toc = root.querySelector('[data-christian-toc]');
    if (!list) return;
    list.replaceChildren();
    content.querySelectorAll('h2').forEach((heading, index) => {
      if (!heading.id) heading.id = `christian-section-${index + 1}`;
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'aeternum-horizontal-toc__link';
      link.href = `#${heading.id}`;
      const label = document.createElement('span');
      label.className = 'aeternum-horizontal-toc__text';
      label.textContent = heading.textContent;
      const arrow = document.createElement('span');
      arrow.className = 'aeternum-horizontal-toc__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>';
      link.append(label, arrow);
      li.append(link); list.append(li);
    });
    if (toc) toc.hidden = !list.children.length;
  };
  const init = scope => {
    scope.querySelectorAll('[data-christian-recueil]').forEach(initDirectory);
    scope.querySelectorAll('[data-christian-article]').forEach(initArticle);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document)); else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

if (!customElements.get('tec-collection-pagination')) {
  customElements.define('tec-collection-pagination', class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.button = this.querySelector('[data-collection-load]');
      this.label = this.querySelector('[data-collection-load-label]');
      this.status = this.querySelector('[data-collection-load-status]');
      this.progress = this.querySelector('progress');
      this.count = this.querySelector('[data-collection-count]');
      if (!this.grid || !this.button) return;
      this.originalLabel = this.label.textContent;
      this.button.addEventListener('click', (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this.load(true);
      });
      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load(false);
        }, { rootMargin: '200px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.request?.abort();
      this.observer?.disconnect();
    }

    async load(moveFocus) {
      if (this.busy || !this.dataset.nextUrl || !this.isConnected) return;
      this.busy = true;
      this.request = new AbortController();
      this.setAttribute('aria-busy', 'true');
      this.button.setAttribute('aria-disabled', 'true');
      this.grid.setAttribute('aria-busy', 'true');
      this.label.textContent = this.dataset.loadingLabel;
      this.status.textContent = '';
      try {
        const url = new URL(this.dataset.nextUrl, window.location.origin);
        const pageUrl = url.pathname + url.search;
        url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: this.request.signal });
        if (!response.ok) throw new Error('Collection response failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected) return;
        const nextGrid = html.querySelector('#product-grid');
        const nextPagination = html.querySelector('tec-collection-pagination');
        const cards = nextGrid ? Array.from(nextGrid.children).filter((card) => card.classList.contains('column')) : [];
        if (!cards.some((card) => card.hasAttribute('data-collection-product'))) throw new Error('Collection products missing');
        this.grid.append(...cards);
        this.dataset.nextUrl = nextPagination?.dataset.nextUrl || '';
        const count = this.grid.querySelectorAll('[data-collection-product]').length;
        this.progress.value = count;
        this.count.textContent = this.dataset.countLabel.replace('[count]', String(count));
        this.status.textContent = this.dataset.nextUrl ? '' : this.dataset.completeLabel;
        this.button.hidden = !this.dataset.nextUrl;
        if (this.dataset.nextUrl) this.button.href = this.dataset.nextUrl;
        else this.observer?.disconnect();
        document.dispatchEvent(new CustomEvent('pagination:page-changed', { detail: { url: pageUrl } }));
        if (moveFocus) {
          const first = cards.find((card) => card.hasAttribute('data-collection-product'));
          first?.querySelector('a[href]')?.focus({ preventScroll: true });
          first?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.label.textContent = this.dataset.retryLabel;
          this.status.textContent = this.dataset.errorLabel;
          this.observer?.disconnect();
        }
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        this.grid.removeAttribute('aria-busy');
        this.button.removeAttribute('aria-disabled');
        if (this.label.textContent !== this.dataset.retryLabel) this.label.textContent = this.originalLabel;
      }
    }
  });
}

/* Aeternum incense guide: accessible format selector, enhanced only when JS is ready. */
(() => {
  const init = (scope) => {
    scope.querySelectorAll('[data-fumigation-selector]').forEach((root) => {
      if (root.dataset.fumigationReady) return;
      const tablist = root.querySelector('[data-fumigation-tabs]');
      const tabs = Array.from(root.querySelectorAll('[data-fumigation-tab]')).filter((tab) => tab.closest('[data-fumigation-selector]') === root);
      const panels = Array.from(root.querySelectorAll('[data-fumigation-panel]')).filter((panel) => panel.closest('[data-fumigation-selector]') === root);
      if (!tablist || !tabs.length || tabs.length !== panels.length) return;
      root.dataset.fumigationReady = 'true';
      const select = (index, focus = false) => {
        tabs.forEach((tab, i) => {
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
          panels[i].setAttribute('role', 'tabpanel');
          panels[i].setAttribute('aria-labelledby', tab.id);
          panels[i].tabIndex = 0;
        });
        if (focus) tabs[index].focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(index));
        tab.addEventListener('keydown', (event) => {
          let next = index;
          const rtl = getComputedStyle(root).direction === 'rtl';
          if (event.key === 'ArrowRight') next = (index + (rtl ? tabs.length - 1 : 1)) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : tabs.length - 1)) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault();
          select(next, true);
        });
      });
      select(0);
      tablist.hidden = false;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

(() => {
  const recoverReviews = async () => {
    const dataNode = document.getElementById('aeternum-air-reviews-recovery-data');
    if (!dataNode || window.AIR_REVIEWS?.settings || window.__aeternumAirRecoveryStarted) return;
    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      console.error('Aeternum review recovery data', error);
      return;
    }
    if (!data.settings?.shopId) return;
    window.__aeternumAirRecoveryStarted = true;
    window.AIR_REVIEWS = {
      ...data,
      branding: Boolean(data.statusFields?.isRemoveBranding),
      appBlock: Boolean(data.statusFields?.supportAppBlock)
    };
    const stylesheetPreload = document.getElementById('aeternum-air-reviews-preload-style');
    const bundleVersion = stylesheetPreload?.dataset.bundleVersion || '111';
    const loadBundle = (file) => new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://cdn-air-reviews.avada.io/scripttag/${file}?v=${bundleVersion}`;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    try {
      // Shopify normally includes this stylesheet with the app extension.
      // Preserve the vendor's existing layout when the extension is unavailable.
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = stylesheetPreload?.href || 'https://cdn.shopify.com/extensions/01a110a9-e235-78dc-88b3-a16bf01a1552/air-reviews-1-111/assets/air-reviews.min.css';
      await new Promise((resolve, reject) => {
        stylesheet.onload = resolve;
        stylesheet.onerror = reject;
        document.head.appendChild(stylesheet);
      });
      if (!window.__airReviewsMainLoaded) {
        window.__airReviewsMainLoaded = true;
        await loadBundle('air-reviews-main.min.js');
      }
      const widgets = [];
      if (document.querySelector('.AirReviews-Widget--AppBlockSummary')) widgets.push('air-reviews-summary-main.min.js');
      if (document.querySelector('.AirReviews-Embed-BlockWrapper')) widgets.push('air-reviewsblock-main.min.js');
      if (document.querySelector('.AirReviews-Embed-CarouselWrapper')) widgets.push('air-reviewscarousel-main.min.js');
      await Promise.all(widgets.map(loadBundle));
      if (document.querySelector('.AirReviews-Embed-BlockAllWrapper')) {
        await window.airReviewDisplayManager?.insertAllReviewBlocks();
      }
    } catch (error) {
      console.error('Aeternum review recovery loading', error);
    }
  };
  if (document.getElementById('aeternum-air-reviews-recovery-data')) recoverReviews();
  else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', recoverReviews, { once: true });
  else recoverReviews();
})();

(() => {
  const normalise = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/œ/g, 'oe').toLocaleLowerCase();
  const init = scope => {
    const roots = Array.from(scope.querySelectorAll('[data-pendulum-guide]'));
    if (scope.matches?.('[data-pendulum-guide]')) roots.unshift(scope);
    roots.forEach(root => {
      if (root.dataset.pendulumBound) return;
      root.dataset.pendulumBound = 'true';
      const cards = Array.from(root.querySelectorAll('[data-pendulum-card]'));
      const filters = Array.from(root.querySelectorAll('[data-pendulum-filter]'));
      const search = root.querySelector('[data-pendulum-search]');
      const count = root.querySelector('[data-pendulum-count]');
      const chapters = Array.from(root.querySelectorAll('[data-pendulum-chapter]'));
      let level = 'all';
      cards.forEach(card => { card.searchText = normalise(card.dataset.search); });
      const update = () => {
        const query = normalise(search.value.trim());
        let visible = 0;
        cards.forEach(card => {
          const matches = (level === 'all' || card.dataset.level === level) && (!query || card.searchText.includes(query));
          card.hidden = !matches;
          if (matches) visible++;
        });
        count.textContent = (visible === 1 ? root.dataset.countOne : root.dataset.countOther).replace('[count]', String(visible));
        root.querySelector('[data-pendulum-empty]').hidden = visible !== 0;
        filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.pendulumFilter === level)));
      };
      const openChapter = (hash, focus = false) => {
        const chapter = chapters.find(item => `#${item.id}` === hash);
        if (!chapter) return false;
        chapter.open = true;
        requestAnimationFrame(() => {
          if (focus) chapter.querySelector('summary').focus({ preventScroll: true });
          chapter.scrollIntoView({ block: 'start', behavior: 'auto' });
        });
        return true;
      };
      root.addEventListener('click', event => {
        const link = event.target.closest('a[href^="#"]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        const hash = link.getAttribute('href');
        if (link.dataset.pendulumLevelJump) {
          level = link.dataset.pendulumLevelJump;
          search.value = '';
          update();
        }
        if (chapters.some(chapter => `#${chapter.id}` === hash)) {
          event.preventDefault();
          event.stopPropagation();
          if (location.hash !== hash) history.pushState(null, '', hash);
          openChapter(hash, true);
        }
      });
      search.addEventListener('input', update);
      filters.forEach(button => button.addEventListener('click', () => { level = button.dataset.pendulumFilter; update(); }));
      root.querySelector('[data-pendulum-reset]').addEventListener('click', () => {
        search.value = '';
        level = 'all';
        update();
        search.focus();
      });
      window.addEventListener('hashchange', () => { if (root.isConnected) openChapter(location.hash); });
      window.addEventListener('popstate', () => { if (root.isConnected) openChapter(location.hash); });
      root.querySelector('[data-pendulum-tools]').hidden = false;
      update();
      if (location.hash) openChapter(location.hash);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

(function () {
  function initIntentionGenerator(scope) {
    scope.querySelectorAll('[data-intention-generator]').forEach(function (root) {
      if (root.dataset.intentionReady) return;
      root.dataset.intentionReady = 'true';
      var config = JSON.parse(root.querySelector('[data-intention-config]').textContent);
      var current = config.initial;
      var text = root.querySelector('[data-intention-text]');
      var select = root.querySelector('[data-intention-select]');
      var status = root.querySelector('[data-intention-status]');
      var copyButton = root.querySelector('[data-intention-copy]');
      var copyLabel = copyButton.textContent;
      var products = root.querySelector('[data-intention-products]');
      var buttons = root.querySelectorAll('[data-intention-goal]');
      var person = root.querySelector('[data-intention-person]');
      function fitIntentionText() {
        if (!root.isConnected) return;
        text.style.height = 'auto';
        text.style.height = (text.scrollHeight + 2) + 'px';
      }
      window.addEventListener('resize', fitIntentionText);
      requestAnimationFrame(fitIntentionText);
      function intentionText(goal) {
        return config.goals[goal][person.checked ? 1 : 0];
      }
      person.addEventListener('change', function () {
        text.value = intentionText(current);
        copyButton.textContent = copyLabel;
        status.textContent = config.updated;
        fitIntentionText();
      });
      function update(goal) {
        if (!config.goals[goal] || goal === current) return;
        current = goal;
        text.value = intentionText(goal);
        fitIntentionText();
        copyButton.textContent = copyLabel;
        select.value = goal;
        buttons.forEach(function (button) {
          button.setAttribute('aria-pressed', String(button.dataset.intentionGoal === goal));
        });
        var template = Array.from(root.querySelectorAll('[data-intention-template]')).find(function (item) {
          return item.dataset.intentionTemplate === goal;
        });
        products.replaceChildren(template.content.cloneNode(true));
        products.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        products.querySelectorAll('.product-card--add-to-cart-button').forEach(function (button) { button.tabIndex = 0; });
        status.textContent = config.updated;
      }
      products.querySelectorAll('.product-card--add-to-cart-button').forEach(function (button) { button.tabIndex = 0; });
      buttons.forEach(function (button) { button.addEventListener('click', function () { update(button.dataset.intentionGoal); }); });
      select.addEventListener('change', function () { update(select.value); });
      copyButton.addEventListener('click', async function () {
        try {
          await navigator.clipboard.writeText(text.value);
          status.textContent = config.copied;
          copyButton.textContent = config.copied;
        } catch (error) {
          text.focus();
          text.select();
          status.textContent = config.copyError;
        }
      });
      text.addEventListener('input', function () { status.textContent = ''; copyButton.textContent = copyLabel; fitIntentionText(); });
      document.addEventListener('shopify:block:select', function (event) {
        if (root.contains(event.target)) update(event.detail.blockId);
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initIntentionGenerator(document); });
  else initIntentionGenerator(document);
  document.addEventListener('shopify:section:load', function (event) { initIntentionGenerator(event.target); });
})();

/* Enochian: manuscript outlines, not a substitute Unicode font. */
(function () {
  'use strict';
  var ns = 'http://www.w3.org/2000/svg';
  var aliases = { J: 'I', K: 'C', V: 'U', W: 'U' };
  function normalizeEnochian(value) {
    var expanded = value.replace(/œ/gi, 'oe').replace(/æ/gi, 'ae').replace(/ß/g, 'ss')
      .replace(/ı/g, 'i').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
    var omitted = Array.from(new Set(Array.from(expanded).filter(function (c) { return !/[A-Z\s]/.test(c); }))).join(' ');
    var letters = expanded.replace(/[^A-Z\s]/g, ' ').replace(/[JKVW]/g, function (c) { return aliases[c]; }).replace(/\s+/g, ' ').trim();
    return { letters: letters, omitted: omitted };
  }
  function enochianLines(letters, limit) {
    var lines = [], line = '';
    letters.split(' ').forEach(function (word) {
      if (!word) return;
      if (line && line.length + word.length + 1 > limit) { lines.push(line); line = ''; }
      while (word.length > limit) { lines.push(word.slice(0, limit)); word = word.slice(limit); }
      if (word) line += (line ? ' ' : '') + word;
    });
    if (line) lines.push(line);
    return lines;
  }
  function drawEnochian(letters, glyphs, limit) {
    var lines = enochianLines(letters, limit);
    var columns = Math.max(1, ...lines.map(function (line) { return line.length; }));
    var width = columns * 72 + 40, height = Math.max(1, lines.length) * 92 + 40;
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('xmlns', ns); svg.setAttribute('viewBox', '0 0 ' + width + ' ' + height);
    svg.setAttribute('width', width); svg.setAttribute('height', height); svg.setAttribute('aria-hidden', 'true');
    lines.forEach(function (line, row) {
      Array.from(line).forEach(function (letter, col) {
        if (letter === ' ') return;
        var path = document.createElementNS(ns, 'path');
        path.setAttribute('d', glyphs[letter].path);
        // First letter at the right; translate only, never reflect a glyph.
        path.setAttribute('transform', 'translate(' + (width - 92 - col * 72) + ' ' + (20 + row * 92) + ') scale(.72)');
        path.setAttribute('fill', '#24392d'); path.dataset.letter = letter;
        svg.appendChild(path);
      });
    });
    return svg;
  }
  function init(scope) {
    scope.querySelectorAll('[data-enochian]').forEach(async function (root) {
      if (root.dataset.enochianReady) return;
      root.dataset.enochianReady = 'loading';
      var config = JSON.parse(root.querySelector('[data-enochian-config]').textContent);
      var input = root.querySelector('[data-enochian-input]'), output = root.querySelector('[data-enochian-output]');
      var empty = root.querySelector('[data-enochian-empty]'), normalized = root.querySelector('[data-enochian-normalized]');
      var omitted = root.querySelector('[data-enochian-omitted]'), download = root.querySelector('[data-enochian-download]');
      var status = root.querySelector('[data-enochian-status]');
      try {
        var response = await fetch(config.glyphsUrl);
        if (!response.ok) throw new Error('Glyph asset unavailable');
        var data = await response.json(), glyphs = data.glyphs;
        var alphabet = root.querySelector('[data-enochian-alphabet]');
        Object.keys(glyphs).forEach(function (letter) {
          var item = document.createElement('div'), label = document.createElement('strong'), name = document.createElement('span');
          label.textContent = letter === 'U' ? 'U / V' : letter;
          name.textContent = glyphs[letter].name;
          item.append(drawEnochian(letter, glyphs, 1), label, name); alphabet.appendChild(item);
        });
        function render() {
          var result = normalizeEnochian(input.value);
          var limit = Math.max(4, Math.min(18, Math.floor(output.clientWidth / 58)));
          output.replaceChildren();
          if (result.letters) output.appendChild(drawEnochian(result.letters, glyphs, limit));
          output.setAttribute('aria-label', config.result + ' : ' + result.letters);
          empty.hidden = Boolean(result.letters); download.disabled = !result.letters;
          normalized.hidden = !result.letters;
          normalized.textContent = config.normalized + ' ' + result.letters;
          omitted.hidden = !result.omitted;
          omitted.textContent = config.omitted + ' ' + result.omitted;
          status.textContent = '';
        }
        input.addEventListener('input', render);
        var observer = new ResizeObserver(function () { if (root.isConnected) render(); else observer.disconnect(); });
        observer.observe(output);
        download.addEventListener('click', async function () {
          var svg = output.querySelector('svg');
          if (!svg) return;
          download.disabled = true;
          var svgCopy = svg.cloneNode(true), metadata = document.createElementNS(ns, 'metadata');
          metadata.textContent = data.credit + ' ' + data.source + ' ' + data.license;
          svgCopy.prepend(metadata);
          var blob = new Blob([new XMLSerializer().serializeToString(svgCopy)], { type: 'image/svg+xml;charset=utf-8' });
          var url = URL.createObjectURL(blob);
          try {
            var img = new Image();
            await new Promise(function (resolve, reject) { img.onload = resolve; img.onerror = reject; img.src = url; });
            var canvas = document.createElement('canvas');
            canvas.width = img.width * 2; canvas.height = img.height * 2 + 70;
            var ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, img.width * 2, img.height * 2);
            ctx.fillStyle = '#687168'; ctx.font = '14px sans-serif'; ctx.textAlign = 'center';
            ctx.fillText('Enochian-3188 · Joseph H. Peterson · CC BY 4.0', canvas.width / 2, canvas.height - 44, canvas.width - 20);
            ctx.fillText('esotericarchives.com/fonts · creativecommons.org/licenses/by/4.0 · Aeternum', canvas.width / 2, canvas.height - 22, canvas.width - 20);
            var png = await new Promise(function (resolve) { canvas.toBlob(resolve, 'image/png'); });
            if (!png) throw new Error('PNG export failed');
            var pngUrl = URL.createObjectURL(png), a = document.createElement('a');
            a.href = pngUrl; a.download = 'aeternum-enochian.png'; document.body.appendChild(a); a.click(); a.remove();
            setTimeout(function () { URL.revokeObjectURL(pngUrl); }, 30000);
          } catch (error) { status.textContent = config.error; }
          finally { URL.revokeObjectURL(url); download.disabled = !normalizeEnochian(input.value).letters; }
        });
        render(); root.dataset.enochianReady = 'true';
      } catch (error) { empty.textContent = config.error; root.dataset.enochianReady = 'error'; }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();

/* Ritual pack builder: curated shopping selections, live variants and native Ajax cart. */
(() => {
  const init = (scope = document) => scope.querySelectorAll('[data-pack-generator]').forEach((root) => {
    if (root.dataset.packReady) return;
    root.dataset.packReady = 'true';
    const config = JSON.parse(root.querySelector('[data-pack-config]').textContent);
    const extraCopy = document.querySelector('[data-pack-extra-text]');
    const text = { ...config.text, ...(extraCopy ? JSON.parse(extraCopy.textContent) : {}) };
    const shopRoot = config.root.replace(/\/?$/, '/');
    const libraryEnabled = config.locale.split('-')[0] === 'fr' && ['FR', 'CH', 'BE', 'LU', 'MC'].includes(config.country);
    const isLibraryItem = (item) => item.library || ['book', 'tarot_kit', 'oracle'].includes(item.role);
    const find = (name) => root.querySelector(`[data-pack-${name}]`);
    root.querySelectorAll('.tec-pack__stage-picto path').forEach((path) => path.setAttribute('pathLength', '1'));
    const status = find('status');
    const select = find('intent');
    const theme = find('theme');
    const money = new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency });
    const currency = (cents) => money.format(cents / 100);
    let recipes = [], current = null, entries = [], generation = 0, busy = false, restoreSelection = null;
    const node = (tag, className, value) => {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (value !== undefined) element.textContent = value;
      return element;
    };
    const productUrl = (handle) => `${shopRoot}products/${encodeURIComponent(handle)}`;
    const fetchProduct = async (item, requests = null) => {
      const load = async () => {
        const response = await fetch(`${productUrl(item.handle)}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Product load failed');
        return response.json();
      };
      if (requests && !requests.has(item.handle)) requests.set(item.handle, load());
      const product = await (requests ? requests.get(item.handle) : load());
      if (!product) return { ...item, product: null, variant: null, available: false };
      const variant = product.variants.find((value) => String(value.id) === String(item.variant));
      return { ...item, variantId: item.variant, product, variant: variant || null, available: Boolean(variant?.available) };
    };
    const storageKey = 'aeternum.pack-favorites.v1';
    const readFavorites = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
        if (!Array.isArray(saved)) return [];
        return saved.filter((value) => value && recipes.some((recipe) => recipe.id === value.id) && Array.isArray(value.variants))
          .map((value) => ({ id: value.id, variants: [...new Set(value.variants.filter((id) => Number.isSafeInteger(id) && id > 0 && (libraryEnabled || !recipes.find((recipe) => recipe.id === value.id).items.some((item) => item.variant === id && isLibraryItem(item)))))], managedVariants: (value.managedVariants || []).filter((id) => Number.isSafeInteger(id) && value.variants.includes(id)) }))
          .filter((value) => value.variants.length);
      } catch (_error) { return []; }
    };
    const setStage = (state) => {
      const stage = find('stage');
      if (!stage) return;
      stage.hidden = state === 'ready';
      stage.classList.toggle('is-loading', state === 'loading');
      find('stage-text').textContent = state === 'loading' ? text.loading : text.placeholder;
    };
    const selected = () => entries.filter((entry) => entry.selected && entry.available);
    const summary = () => {
      const chosen = selected();
      find('count').textContent = `${text.selected} : ${chosen.length}`;
      if (find('save')) {
        const match = readFavorites().find((value) => value.id === current?.id);
        const nativeIds = window.AeternumWishlist?.getIds() || [];
        const same = Boolean(match && chosen.length === match.variants.length && chosen.every((entry) => match.variants.includes(entry.variant.id) && nativeIds.includes(String(entry.variant.id))));
        find('save').disabled = busy || !chosen.length;
        find('save').setAttribute('aria-pressed', String(same));
        const label = same ? text.remove_pack : text.save_pack;
        find('save-label').textContent = label;
        find('save').setAttribute('aria-label', label);
        find('save').title = label;
      }
      const total = currency(chosen.reduce((sum, entry) => sum + entry.variant.price, 0));
      find('total').textContent = total;
      if (find('mobile-total')) find('mobile-total').textContent = total;
      [find('add-all'), find('add-mobile')].filter(Boolean).forEach((button) => {
        button.disabled = busy || !chosen.length;
        button.querySelector('[data-pack-add-label]').textContent = busy ? text.adding : text.add_all;
      });
    };
    const setBusy = (value) => {
      busy = value;
      root.querySelectorAll('[data-pack-add], [data-pack-include]').forEach((element) => {
        element.disabled = value || element.dataset.available !== 'true';
      });
      find('compose').disabled = value;
      select.disabled = value;
      theme.disabled = value;
      summary();
    };
    const cartMessage = (message) => {
      find('cart-status').textContent = message;
      status.textContent = message;
    };
    const updateCart = (sections) => {
      let complete = true;
      [
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ].forEach(({ id, section, selector }) => {
        const target = document.getElementById(id);
        const html = sections?.[section];
        if (!target || !html) { complete = false; return; }
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        if (source) (target.querySelector(selector) || target).innerHTML = source.innerHTML;
        else complete = false;
      });
      if (!complete) document.dispatchEvent(new CustomEvent('cart:refresh'));
    };
    const add = async (chosen) => {
      if (busy || !chosen.length) return;
      setBusy(true);
      cartMessage(text.adding);
      try {
        // Refresh every requested variant before adding; do not silently replace it.
        const fresh = await Promise.all(chosen.map((entry) => fetchProduct({ ...entry, variant: entry.variantId })));
        const changed = fresh.some((entry, index) => !entry.available || entry.variant.price !== chosen[index].variant.price);
        if (changed) {
          fresh.forEach((entry, index) => {
            const original = chosen[index];
            Object.assign(original, entry, { selected: original.selected && entry.available });
          });
          render();
          cartMessage(text.changed);
          return;
        }
        const items = [...new Map(fresh.map((entry) => [entry.variant.id, { id: entry.variant.id, quantity: 1, properties: { _aeternum_source: 'ritual_pack_generator' } }])).values()];
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: ['cart-drawer', 'cart-bubble'], sections_url: window.location.pathname })
        });
        const payload = await response.json();
        if (!response.ok || payload.status) throw new Error('Cart add not confirmed');
        updateCart(payload.sections);
        cartMessage(text.added);
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { products: payload.items } }));
        document.getElementById('Cart-Drawer')?.open?.();
      } catch (_error) {
        // An interrupted request may already have changed the cart. Never retry it automatically.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        cartMessage(text.cart_error);
      } finally {
        setBusy(false);
      }
    };
    const card = (entry, index) => {
      const article = node('article', `tec-pack__card${entry.selected ? ' is-selected' : ''}`);
      const title = entry.product?.title || entry.title || text.unavailable;
      const url = productUrl(entry.handle);
      if (entry.product?.featured_image) {
        const link = node('a', 'tec-pack__image');
        link.href = url;
        link.target = '_blank'; link.rel = 'noopener'; link.tabIndex = -1;
        const image = node('img');
        const imageUrl = new URL(entry.product.featured_image, window.location.origin);
        imageUrl.searchParams.set('width', '360');
        image.src = imageUrl.href; image.alt = ''; image.width = 180; image.height = 180; image.loading = 'lazy';
        link.append(image); article.append(link);
      }
      const body = node('div', 'tec-pack__card-body');
      if (entry.product?.vendor) body.append(node('p', 'tec-pack__vendor', entry.product.vendor));
      const heading = node('h3');
      const link = node('a', 'product-card-title', title);
      link.href = url; link.target = '_blank'; link.rel = 'noopener'; heading.append(link); body.append(heading);
      if (entry.variant?.public_title) body.append(node('p', 'tec-pack__variant', entry.variant.public_title));
      body.append(node('p', 'price', entry.available ? currency(entry.variant.price) : text.unavailable));
      if (entry.libraryNotice) body.append(node('p', 'tec-pack__variant', text[entry.libraryNotice]));
      if (entry.role === 'ritual_kit') body.append(node('p', 'tec-pack__included', text.included));
      const why = node('p', 'tec-pack__why', text[`why_${entry.why || entry.role}`]);
      body.append(why);
      const how = node('div', 'tec-pack__use');
      how.append(node('strong', '', text.use), node('p', '', text[`use_${entry.use || entry.role}`]));
      body.append(how);
      const controls = node('div', 'tec-pack__card-controls');
      const label = node('label', 'tec-pack__check');
      const checkbox = node('input'); checkbox.type = 'checkbox'; checkbox.checked = entry.selected;
      checkbox.id = `pack-${root.closest('[id]')?.id || 'main'}-${index}`;
      checkbox.dataset.packInclude = ''; checkbox.dataset.available = String(entry.available);
      checkbox.disabled = busy || !entry.available;
      checkbox.addEventListener('change', () => {
        entry.selected = checkbox.checked;
        article.classList.toggle('is-selected', entry.selected);
        find('cart-status').textContent = '';
        if (find('save-status')) find('save-status').textContent = '';
        summary();
      });
      label.append(checkbox, node('span', '', text.include));
      const button = node('button', 'button accent', text.add_one);
      button.type = 'button'; button.dataset.packAdd = ''; button.dataset.available = String(entry.available);
      button.disabled = busy || !entry.available;
      button.setAttribute('aria-label', `${text.add_one} : ${title}`);
      button.addEventListener('click', () => add([entry]));
      controls.append(label, button); body.append(controls); article.append(body);
      return article;
    };
    const render = () => {
      find('products').replaceChildren(); find('extras').replaceChildren();
      entries.forEach((entry, index) => { if (!entry.optional || entry.available) find(entry.optional ? 'extras' : 'products').append(card(entry, index)); });
      find('extras-wrap').hidden = !entries.some((entry) => entry.optional && entry.available);
      find('kind').textContent = current.kind === 'kit' ? text.kit : text.composition;
      find('title').textContent = text[`goal_${current.id}`];
      if (find('intro')) find('intro').textContent = text[`intro_${current.id}`] || '';
      const timing = find('timing');
      if (timing) {
        const profile = entries.find((entry) => entry.available && !entry.optional && entry.candleTiming)?.candleTiming || current.timing;
        const day = text[`timing_${profile}_day`];
        const note = text[`timing_${profile}_note`];
        timing.hidden = !day || !note;
        find('timing-day').textContent = day || '';
        find('timing-note').textContent = note || '';
      }
      const needs = (current.needs || [current.kind]).filter((kind) => {
        const requirement = current.conditionalNeeds?.[kind];
        return !requirement || entries.some((entry) => entry.available && (requirement.groups || [requirement.group]).includes(entry.choiceGroup) && (!requirement.method || entry.method === requirement.method));
      });
      find('needs').replaceChildren(...needs.map((kind) => node('span', '', text[`need_${kind}`])));
      const learning = current.learning;
      const stepsPrefix = current.stepsPrefix || (learning ? `library_${learning}` : '');
      if (find('steps-title')) find('steps-title').textContent = stepsPrefix ? text[`${stepsPrefix}_begin`] : text.begin;
      root.querySelectorAll('[data-pack-step-title]').forEach((element) => { const index = element.dataset.packStepTitle; element.textContent = text[stepsPrefix ? `${stepsPrefix}_step${index}_title` : `step${index}_title`]; });
      root.querySelectorAll('[data-pack-step-text]').forEach((element) => { const index = element.dataset.packStepText; element.textContent = text[stepsPrefix ? `${stepsPrefix}_step${index}` : `step${index}`]; });
      find('result').hidden = false;
      if (find('faq')) find('faq').hidden = false;
      setStage('ready');
      summary();
    };
    const compose = async (id, focus = false, savedVariants = null) => {
      if (busy) return;
      const recipe = recipes.find((value) => value.id === id);
      if (!recipe) return;
      restoreSelection = savedVariants;
      const request = ++generation;
      select.value = id;
      find('result').hidden = true; find('retry').hidden = true;
      if (find('faq')) find('faq').hidden = true;
      if (find('save-status')) find('save-status').textContent = '';
      setStage('loading');
      if (focus) find('stage')?.scrollIntoView?.({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      root.setAttribute('aria-busy', 'true');
      status.textContent = '';
      find('cart-status').textContent = '';
      try {
        const minimum = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? Math.min(config.minimumPreparationMs ?? 2000, 400) : (config.minimumPreparationMs ?? 2000);
        const requests = new Map();
        const availableItems = recipe.items.filter((item) => (libraryEnabled || !isLibraryItem(item)) && (!item.legacyOnly || savedVariants?.includes(item.variant)));
        const loadItems = async () => {
          const regular = await Promise.all(availableItems.filter((item) => !isLibraryItem(item) && !item.mineral).map((item) => fetchProduct(item, requests)));
          const mineralChoices = [];
          const reserved = new Map();
          [...regular].sort((a, b) => Number(Boolean(savedVariants?.includes(b.variant?.id))) - Number(Boolean(savedVariants?.includes(a.variant?.id)))).forEach((entry) => {
            if (entry.available && (!entry.choiceGroup || !reserved.has(entry.choiceGroup))) reserved.set(entry.choiceGroup || entry.handle, entry);
          });
          const occupied = () => [...(recipe.includedMinerals || []), ...[...reserved.values(), ...mineralChoices].flatMap((entry) => entry.mineralIdentities || [])];
          const mineralItems = availableItems.filter((item) => item.mineral);
          const mineralGroups = [...new Set(mineralItems.map((item) => item.choiceGroup))].sort((a, b) => ['mineral_pendulum', 'mineral', 'mineral_wearable', 'mineral_plate', 'mineral_storage'].indexOf(a) - ['mineral_pendulum', 'mineral', 'mineral_wearable', 'mineral_plate', 'mineral_storage'].indexOf(b));
          for (const group of mineralGroups) {
            const candidates = mineralItems.filter((item) => item.choiceGroup === group).sort((a, b) => Number(Boolean(savedVariants?.includes(b.variant))) - Number(Boolean(savedVariants?.includes(a.variant))));
            for (const item of candidates) {
              const parent = mineralChoices.find((entry) => entry.choiceGroup === item.requiresChoiceGroup);
              if (item.requiresChoiceGroup && !parent) continue;
              if (item.requiresSmallMineral && !parent?.mineralSmall) continue;
              if (item.unlessMineralPouch && parent?.mineralPouch) continue;
              const identities = item.mineralIdentities || [];
              const used = occupied();
              if (identities.length && (used.includes('*') || (identities.includes('*') && used.length) || identities.some((identity) => used.includes(identity)))) continue;
              const entry = await fetchProduct(item, requests);
              if (request !== generation) return [];
              if (entry.available) { mineralChoices.push(entry); break; }
            }
          }
          const reading = availableItems.filter(isLibraryItem);
          const groups = [...new Set(reading.map((item) => item.choiceGroup || `library_${item.role}`))].sort((a, b) => (a === 'library_deck' ? -1 : 0) - (b === 'library_deck' ? -1 : 0));
          const chosen = [];
          for (const group of groups) {
            const candidates = reading.filter((item) => (item.choiceGroup || `library_${item.role}`) === group).sort((a, b) => Number(Boolean(savedVariants?.includes(b.variant))) - Number(Boolean(savedVariants?.includes(a.variant))));
            for (const item of candidates) {
              const deck = chosen.find((entry) => entry.choiceGroup === 'library_deck');
              if (item.requiresChoiceGroup === 'library_deck' && (!deck || !item.requiresMethods.includes(deck.method))) continue;
              const entry = await fetchProduct(item, requests);
              if (request !== generation) return [];
              if (entry.available) { chosen.push(entry); break; }
            }
          }
          return [...regular, ...mineralChoices, ...chosen];
        };
        const [loaded] = await Promise.all([loadItems(), new Promise((resolve) => window.setTimeout(resolve, minimum))]);
        if (request !== generation) return;
        if (recipe.mineralRequired && !loaded.some((entry) => entry.available && entry.choiceGroup === 'mineral_pendulum')) { entries = []; status.textContent = text.mineral_missing; setStage('idle'); return; }
        const missingReading = availableItems.some((item) => isLibraryItem(item) && !item.optional) && !loaded.some((item) => isLibraryItem(item) && !item.optional);
        if (missingReading) { entries = []; status.textContent = text.library_missing; setStage('idle'); return; }
        current = recipe;
        const unique = [...new Map(loaded.map((entry) => [entry.variantId || entry.handle, entry])).values()];
        const choices = new Map();
        const preference = (entry) => (entry.choiceGroup === 'candle' ? -30 : entry.choiceGroup === 'resin' ? -20 : 0) + (savedVariants?.includes(entry.variant?.id) ? -1 : 0);
        const candidates = [...unique].sort((first, second) => preference(first) - preference(second));
        candidates.forEach((entry) => {
          if (!entry.choiceGroup || !entry.available) return;
          const required = choices.get(entry.requiresChoiceGroup);
          if (entry.requiresChoiceGroup && (!required || (entry.requiresMethods && !entry.requiresMethods.includes(required.method)) || (entry.requiresMethod && required.method !== entry.requiresMethod))) return;
          if (entry.unlessSupportKind && choices.get('smoke_vessel')?.supportKind === entry.unlessSupportKind) return;
          if (entry.requiresAvailableSupport && !unique.some((support) => support.available && entry.requiresAvailableSupport.includes(support.variant?.id))) return;
          const candle = choices.get('candle');
          const ritualDay = (!candle?.optional && candle?.candleTiming) || recipe.timing;
          if (entry.smokeTiming && ritualDay && entry.smokeTiming !== ritualDay && !savedVariants?.includes(entry.variant?.id)) return;
          const previous = choices.get(entry.choiceGroup);
          const wasSaved = savedVariants?.includes(entry.variant.id);
          if (entry.botanicalIdentity && [...choices.values()].some((choice) => choice !== previous && choice.botanicalIdentity === entry.botanicalIdentity)) return;
          if (!previous || (wasSaved && !savedVariants.includes(previous.variant.id))) choices.set(entry.choiceGroup, entry);
        });
        entries = unique.filter((entry) => {
          if (entry.unlessSupportKind && choices.get('smoke_vessel')?.supportKind === entry.unlessSupportKind) return false;
          if (entry.choiceGroup && choices.get(entry.choiceGroup) !== entry) return false;
          if (entry.requiresAnyChoiceGroup && !entry.requiresAnyChoiceGroup.some((group) => choices.has(group))) return false;
          const required = choices.get(entry.requiresChoiceGroup);
          return !entry.requiresChoiceGroup || Boolean(required && (!entry.requiresMethod || required.method === entry.requiresMethod) && (!entry.requiresMethods || entry.requiresMethods.includes(required.method)));
        });
        entries.forEach((entry) => { entry.selected = entry.available && (savedVariants ? savedVariants.includes(entry.variant.id) : !entry.optional); });
        status.textContent = entries.some((entry) => !entry.optional && !entry.available) || (availableItems.some((item) => isLibraryItem(item) && !item.optional) && !entries.some((entry) => isLibraryItem(entry) && !entry.optional)) ? text.missing : '';
        render();
        if (focus) find('title').focus({ preventScroll: true });
      } catch (_error) {
        if (request !== generation) return;
        status.textContent = text.error; find('retry').hidden = false;
        setStage('idle');
      } finally {
        if (request === generation) root.removeAttribute('aria-busy');
      }
    };
    const resetResult = () => {
      generation += 1; restoreSelection = null;
      find('result').hidden = true; find('retry').hidden = true;
      root.removeAttribute('aria-busy'); status.textContent = '';
      if (find('faq')) find('faq').hidden = true;
      setStage('idle');
    };
    const filterIntentions = (preferred) => {
      select.replaceChildren();
      recipes.filter((recipe) => recipe.family === theme.value).forEach((recipe) => {
        const option = node('option', '', text[`goal_${recipe.id}`]);
        option.value = recipe.id; select.append(option);
      });
      if (preferred) select.value = preferred;
    };
    theme.addEventListener('change', () => { resetResult(); filterIntentions(); });
    select.addEventListener('change', resetResult);
    const renderFavorites = () => {
      const wrapper = find('favorites');
      if (!wrapper) return;
      const saved = readFavorites();
      wrapper.hidden = !saved.length;
      find('favorite-count').textContent = saved.length ? `(${saved.length})` : '';
      find('favorite-list').replaceChildren();
      saved.forEach((value) => {
        const row = node('div', 'tec-pack__favorite');
        const content = node('div');
        const restore = node('button', 'tec-pack__favorite-restore', text[`goal_${value.id}`]);
        restore.type = 'button'; restore.setAttribute('aria-label', `${text.restore_pack} : ${text[`goal_${value.id}`]}`);
        restore.addEventListener('click', () => {
          if (busy) return;
          const recipe = recipes.find((item) => item.id === value.id);
          theme.value = recipe.family; filterIntentions(value.id);
          wrapper.open = false;
          compose(value.id, true, value.variants);
        });
        content.append(restore, node('p', '', `${text.selected} : ${value.variants.length}`));
        const remove = node('button', 'tec-pack__favorite-remove', '×');
        remove.type = 'button'; remove.setAttribute('aria-label', `${text.remove_pack} : ${text[`goal_${value.id}`]}`);
        remove.addEventListener('click', () => {
          try {
            removeSavedPack(value);
            renderFavorites(); summary();
            if (find('save-status')) find('save-status').textContent = '';
          } catch (_error) { if (find('save-status')) find('save-status').textContent = text.save_error; }
        });
        row.append(content, remove); find('favorite-list').append(row);
      });
    };
    const removeSavedPack = (value) => {
      const others = readFavorites().filter((item) => item.id !== value.id);
      const shared = new Set(others.flatMap((item) => item.variants));
      const removable = value.managedVariants.filter((id) => !shared.has(id));
      window.AeternumWishlist?.removeItems(removable.map((variantId) => ({ variantId })));
      localStorage.setItem(storageKey, JSON.stringify(others));
    };
    find('save')?.addEventListener('click', () => {
      if (busy || !current || !selected().length) return;
      const value = { id: current.id, variants: selected().map((entry) => entry.variant.id) };
      try {
        if (!window.AeternumWishlist) throw new Error('wishlist_not_ready');
        const saved = readFavorites();
        const match = saved.find((item) => item.id === value.id);
        const nativeIds = window.AeternumWishlist.getIds();
        const same = Boolean(match && value.variants.length === match.variants.length && value.variants.every((id) => match.variants.includes(id) && nativeIds.includes(String(id))));
        const others = saved.filter((item) => item.id !== value.id);
        if (same) removeSavedPack(match);
        else {
          const result = window.AeternumWishlist.addItems(selected().map((entry) => ({
            variantId: entry.variant.id, productId: entry.product.id, productTitle: entry.product.title,
            productHandle: entry.handle, variantTitle: entry.variant.public_title || entry.variant.title
          })));
          const managed = new Set([...saved.flatMap((item) => item.managedVariants), ...result.changedIds.map(Number)]);
          const shared = new Set(others.flatMap((item) => item.variants));
          const removed = (match?.managedVariants || []).filter((id) => !value.variants.includes(id) && !shared.has(id));
          window.AeternumWishlist.removeItems(removed.map((variantId) => ({ variantId })));
          value.managedVariants = value.variants.filter((id) => managed.has(id));
          try { localStorage.setItem(storageKey, JSON.stringify([value, ...others])); }
          catch (_error) { summary(); find('save-status').textContent = text.save_products_note; return; }
        }
        renderFavorites(); summary(); find('save-status').textContent = same ? '' : text.saved_note;
      } catch (_error) { find('save-status').textContent = text.save_error; }
    });
    window.addEventListener('storage', (event) => {
      if (event.key === storageKey) { renderFavorites(); if (current) summary(); }
    });
    document.addEventListener('aeternum:wishlist-updated', () => { if (current) summary(); });
    const start = async () => {
      find('retry').hidden = true;
      status.textContent = '';
      setStage('idle');
      try {
        const response = await fetch(config.dataUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Selection load failed');
        const data = await response.json(); recipes = data.recipes.filter((recipe) => libraryEnabled || !recipe.libraryOnly);
        recipes = (await Promise.all(recipes.map(async (recipe) => {
          if (!recipe.libraryStockCheck) return recipe;
          const stock = await Promise.all(recipe.libraryStockCheck.map((item) => fetchProduct(item)));
          return stock.some((entry) => entry.available) ? recipe : null;
        }))).filter(Boolean);
        theme.replaceChildren();
        [...new Set(recipes.map((recipe) => recipe.family))].forEach((family) => {
          const option = node('option', '', text[`family_${family}`]);
          option.value = family; theme.append(option);
        });
        const initial = new URL(window.location.href).searchParams.get('intention');
        const first = recipes.find((recipe) => recipe.id === initial) || recipes[0];
        theme.value = first.family;
        filterIntentions(first.id);
        theme.disabled = false; select.disabled = false; find('compose').disabled = false;
        status.textContent = '';
        renderFavorites();
      } catch (_error) { status.textContent = text.error; find('retry').hidden = false; }
    };
    find('form').addEventListener('submit', (event) => { event.preventDefault(); compose(select.value, true); });
    find('retry').addEventListener('click', () => recipes.length ? compose(select.value, false, restoreSelection) : start());
    find('add-all').addEventListener('click', () => add(selected()));
    find('add-mobile')?.addEventListener('click', () => add(selected()));
    start();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
// Keep the seasonal selection open while visitors explore products and related pages.
(() => {
  const initDefuntsLinks = () => {
    const content = document.getElementById('main-content');
    if (!content?.querySelector('.tec-defunts')) return;
    const updateLinks = (root) => {
      const links = root.matches?.('a[href]') ? [root] : [];
      links.push(...root.querySelectorAll?.('a[href]') || []);
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (!href || href.startsWith('#') || !/^https?:$/.test(new URL(href, location.href).protocol)) return;
        if (link.closest('facet-filters-form, facet-remove, .pagination') || link.hasAttribute('download')) return;
        link.target = '_blank';
        link.relList.add('noopener');
      });
    };
    updateLinks(content);
    new MutationObserver((changes) => changes.forEach((change) => change.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) updateLinks(node);
    }))).observe(content, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDefuntsLinks, { once: true });
  else initDefuntsLinks();
})();

(() => {
  const normalize = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/œ/g, 'oe').replace(/æ/g, 'ae').trim();
  const init = (scope = document) => {
    const dictionaries = [...scope.querySelectorAll('[data-dictionary]')];
    if (scope.matches?.('[data-dictionary]')) dictionaries.push(scope);
    dictionaries.forEach((dictionary) => {
      if (dictionary.dataset.dictionaryBound) return;
      dictionary.dataset.dictionaryBound = 'true';
      const input = dictionary.querySelector('[data-dictionary-search]');
      const status = dictionary.querySelector('[data-dictionary-status]');
      const results = dictionary.querySelector('[data-dictionary-results]');
      const clear = dictionary.querySelector('[data-dictionary-clear]');
      const lookup = dictionary.querySelector('[data-dictionary-lookup]');
      const letterLinks = new Map([...dictionary.querySelectorAll('[data-dictionary-letter-link]')].map((link) => [link.dataset.dictionaryLetterLink, link]));
      const cache = new Map();
      let generation = 0;
      let timer;
      const preview = new URL(location.href).searchParams.get('preview_theme_id') || dictionary.dataset.previewTheme;
      if (preview) {
        dictionary.querySelectorAll('a[href]').forEach((link) => {
          if (link.getAttribute('href').startsWith('#')) return;
          const url = new URL(link.href, location.href);
          if (url.origin !== location.origin) return;
          url.searchParams.set('preview_theme_id', preview);
          link.href = url.href;
        });
      }
      const readEntries = (root, url) => [...root.querySelectorAll('[data-dictionary-entry]')].map((entry) => {
        const card = entry.querySelector('[data-grimoire-glossary-trigger]');
        const content = card.querySelector('template[data-grimoire-glossary-content]').content;
        const definition = content.querySelector('[data-dictionary-definition]');
        return {
          name: entry.querySelector('[data-dictionary-term]').textContent.trim(),
          indexName: entry.dataset.dictionaryIndex || '',
          definition: (definition.querySelector('p') || definition).textContent.trim(),
          card: card.cloneNode(true),
          url: `${url.split('#')[0]}#${entry.id}`
        };
      });
      if (dictionary.dataset.dictionaryLetter) cache.set(dictionary.dataset.dictionaryLetter, Promise.resolve(readEntries(dictionary, location.href)));
      const entriesFor = (letter) => {
        if (cache.has(letter)) return cache.get(letter);
        const link = letterLinks.get(letter);
        if (!link) return Promise.resolve([]);
        const task = fetch(link.href, { credentials: 'same-origin' }).then(async (response) => {
          if (!response.ok) throw new Error('Dictionary response unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!doc.querySelector('[data-dictionary]')) throw new Error('Dictionary section unavailable');
          return readEntries(doc, link.href);
        }).catch((error) => { cache.delete(letter); throw error; });
        cache.set(letter, task);
        return task;
      };
      const search = async () => {
        const request = ++generation;
        const query = normalize(input.value);
        results.replaceChildren();
        results.hidden = true;
        clear.hidden = !query;
        status.textContent = '';
        lookup.removeAttribute('aria-busy');
        if (!query) return;
        lookup.setAttribute('aria-busy', 'true');
        status.textContent = dictionary.dataset.loading;
        try {
          const letters = new Set(letterLinks.has(query[0]) ? [query[0]] : []);
          ['a', 'f', 't'].forEach((letter) => {
            const alias = normalize(dictionary.getAttribute(`data-starter-${letter}`) || '');
            if (alias.startsWith(query)) letters.add(letter);
          });
          const entries = (await Promise.all([...letters].map(entriesFor))).flat();
          if (request !== generation) return;
          const matches = entries.filter((entry) => normalize(entry.name).startsWith(query) || normalize(entry.indexName).startsWith(query));
          status.textContent = matches.length ? dictionary.dataset.resultsLabel.replace('__COUNT__', String(matches.length)) : dictionary.dataset.empty;
          matches.slice(0, 8).forEach((entry) => {
            const card = entry.card.cloneNode(true);
            delete card.dataset.grimoireGlossaryBound;
            card.setAttribute('aria-controls', dictionary.querySelector('[data-grimoire-glossary-dialog]').id);
            results.append(card);
          });
          dictionary.dispatchEvent(new Event('aeternum:glossary:refresh', { bubbles: true }));
          if (matches.length > 8) {
            const letterLink = letterLinks.get(query[0]).cloneNode(true);
            letterLink.removeAttribute('aria-current');
            letterLink.className = 'tec-dictionary__more';
            letterLink.textContent = `${dictionary.querySelector('.tec-dictionary__alphabet').getAttribute('aria-label')} · ${query[0].toUpperCase()}`;
            results.append(letterLink);
          }
          results.hidden = !matches.length;
        } catch (_error) {
          if (request === generation) status.textContent = dictionary.dataset.error;
        } finally {
          if (request === generation) lookup.removeAttribute('aria-busy');
        }
      };
      lookup.hidden = false;
      input.addEventListener('input', () => { generation += 1; clearTimeout(timer); timer = setTimeout(search, 180); });
      clear.addEventListener('click', () => { clearTimeout(timer); input.value = ''; search(); input.focus(); });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); clear.click(); }
      });
      dictionary.querySelector('[data-dictionary-form]').addEventListener('submit', async (event) => {
        event.preventDefault(); clearTimeout(timer); await search();
        const first = results.querySelector('button');
        if (first) { first.focus(); first.click(); }
      });
      let initialHashHandled = false;
      const openHash = () => {
        if (!location.hash.startsWith('#mot-')) return;
        const entry = dictionary.querySelector(`[id="${CSS.escape(decodeURIComponent(location.hash.slice(1)))}"]`);
        if (!entry?.matches('[data-dictionary-entry]')) return;
        initialHashHandled = true;
        requestAnimationFrame(() => {
          entry.scrollIntoView({ block: 'center', behavior: 'instant' });
          entry.querySelector('[data-grimoire-glossary-trigger]')?.click();
        });
      };
      dictionary.dispatchEvent(new Event('aeternum:glossary:refresh', { bubbles: true }));
      openHash();
      window.addEventListener('load', () => { if (!initialHashHandled) openHash(); }, { once: true });
      window.addEventListener('hashchange', openHash);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

(() => {
  const initialized = new WeakSet();
  const init = (scope = document) => {
    const roots = scope.matches?.('[data-aeternum-referral]') ? [scope] : scope.querySelectorAll('[data-aeternum-referral]');
    roots.forEach((root) => {
      if (initialized.has(root)) return;
      initialized.add(root);
      const enhance = () => {
        const input = root.querySelector('.essential-copy-input input');
        if (input && input.getAttribute('aria-label') !== root.dataset.linkLabel) input.setAttribute('aria-label', root.dataset.linkLabel);
        const button = root.querySelector('.essential-copy-input button');
        if (button) {
          const label = button.disabled ? root.dataset.copiedLabel : root.dataset.copyLabel;
          if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
          let caption = button.querySelector('[data-referral-copy-caption]');
          if (!caption) {
            caption = document.createElement('span');
            caption.dataset.referralCopyCaption = '';
            button.append(caption);
          }
          if (caption.textContent !== label) caption.textContent = label;
        }
        root.querySelectorAll('.essential-referral-shareable a[href]').forEach((link) => {
          try {
            const url = new URL(link.href);
            const channel = url.protocol === 'mailto:' ? root.dataset.emailLabel : url.hostname;
            const label = `${root.dataset.shareLabel} · ${channel}`;
            if (link.getAttribute('aria-label') !== label) link.setAttribute('aria-label', label);
          } catch {}
        });
      };
      enhance();
      new MutationObserver(enhance).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'href'] });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

(() => {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.tec-payment-help__nav a[href^="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const root = link.closest('.tec-payment-help');
    const target = document.getElementById(link.hash.slice(1));
    if (!target || !root.contains(target)) return;
    event.preventDefault();
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    if (location.hash !== link.hash) history.pushState(null, '', link.hash);
  });
})();

(() => {
  const initialized = new WeakSet();
  const init = (scope = document) => {
    const roots = scope.matches?.('[data-carnets-browse]') ? [scope] : scope.querySelectorAll('[data-carnets-browse]');
    roots.forEach((root) => {
      if (initialized.has(root)) return;
      initialized.add(root);
      const nav = root.querySelector('.tec-carnets__nav');
      const toggle = root.querySelector('[data-carnets-toggle]');
      const links = [...nav.children];
      let expanded = false;
      let frame;
      let previousWidth;
      const update = () => {
        frame = null;
        if (!root.isConnected) return;
        links.forEach((link) => { link.hidden = false; });
        const rowTops = [];
        const overflow = links.filter((link) => {
          const top = link.offsetTop;
          if (!rowTops.some((row) => Math.abs(row - top) < 2)) rowTops.push(top);
          return rowTops.length > 2;
        });
        toggle.hidden = overflow.length === 0;
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.textContent = expanded ? toggle.dataset.showLess : toggle.dataset.showAll;
        if (!expanded) overflow.forEach((link) => { link.hidden = true; });
      };
      const schedule = () => {
        if (frame == null) frame = requestAnimationFrame(update);
      };
      toggle.addEventListener('click', () => {
        expanded = !expanded;
        update();
        if (!expanded) toggle.scrollIntoView({ block: 'nearest' });
      });
      const observer = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width;
        if (width === previousWidth) return;
        previousWidth = width;
        schedule();
      });
      observer.observe(nav);
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target.contains(root)) return;
        observer.disconnect();
        if (frame != null) cancelAnimationFrame(frame);
      });
      document.fonts?.ready.then(schedule);
      update();
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

(() => {
  const initializeMineralTabs = (root = document) => {
    root.querySelectorAll('tabbed-content.tec-mineral-tabs').forEach((tabs) => {
      if (tabs.dataset.mineralAccessible) return;
      tabs.dataset.mineralAccessible = 'true';
      const rail = tabs.querySelector('.tabbed-content--tabs');
      const buttons = Array.from(rail.querySelectorAll('button'));
      const panels = Array.from(tabs.querySelectorAll(':scope > .tabbed-content--content'));
      const prefix = tabs.closest('[id]')?.id || `mineral-${Math.random().toString(36).slice(2)}`;
      rail.setAttribute('role', 'tablist');
      const synchronize = () => {
        const selected = Number(tabs.getAttribute('selected-index')) || 0;
        buttons.forEach((button, index) => {
          const panel = panels[index];
          if (!panel) return;
          button.id ||= `${prefix}-tab-${index}`;
          panel.id ||= `${prefix}-panel-${index}`;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', panel.id);
          button.setAttribute('aria-selected', String(index === selected));
          button.tabIndex = index === selected ? 0 : -1;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', button.id);
          panel.setAttribute('aria-hidden', String(index !== selected));
          panel.tabIndex = 0;
        });
      };
      rail.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const available = buttons.filter((button) => !button.hidden && !button.disabled);
        const current = available.indexOf(event.target);
        if (current < 0) return;
        event.preventDefault();
        const direction = getComputedStyle(rail).direction === 'rtl' ? -1 : 1;
        let next = event.key === 'Home' ? 0 : event.key === 'End' ? available.length - 1 :
          (current + (event.key === 'ArrowRight' ? direction : -direction) + available.length) % available.length;
        available[next].focus({ preventScroll: true });
        available[next].click();
        available[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      });
      new MutationObserver(synchronize).observe(tabs, { attributes: true, attributeFilter: ['selected-index'] });
      synchronize();
    });
  };
  customElements.whenDefined('tabbed-content').then(() => initializeMineralTabs());
  document.addEventListener('shopify:section:load', (event) => initializeMineralTabs(event.target));
})();

if (!customElements.get('tec-shipping-progress')) {
  customElements.define('tec-shipping-progress', class extends HTMLElement {
    static get observedAttributes() { return ['data-cart-total']; }
    connectedCallback() { this.updateProgress(); }
    attributeChangedCallback() { if (this.isConnected) this.updateProgress(); }
    updateProgress() {
      const total = Math.max(0, Number(this.dataset.cartTotal) || 0);
      const maximum = Number(this.dataset.maximum) || 7900;
      const value = Math.min(total, maximum);
      const format = window.theme?.settings?.money_format;
      const money = (amount) => typeof window.formatMoney === 'function' && format
        ? window.formatMoney(amount, format)
        : new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: 'EUR' }).format(amount / 100);
      this.style.setProperty('--shipping-progress', value / maximum);
      const track = this.querySelector('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', value);
        track.setAttribute('aria-valuetext', `${money(value).replace(/<[^>]*>/g, '')} / ${money(maximum).replace(/<[^>]*>/g, '')}`);
      }
      this.querySelectorAll('[data-shipping-threshold]').forEach((milestone) => {
        milestone.classList.toggle('is-unlocked', total >= Number(milestone.dataset.shippingThreshold));
      });
      this.querySelectorAll('[data-shipping-amount]').forEach((amount) => {
        amount.innerHTML = money(Math.max(0, Number(amount.dataset.shippingAmount) - total));
      });
      const twoMilestones = maximum === 11900;
      const visible = {
        both: twoMilestones && total < 7900,
        'relay-remaining': !twoMilestones && total < 7900,
        'relay-unlocked': total >= 7900,
        'home-remaining': twoMilestones && total >= 7900 && total < 11900,
        'home-unlocked': twoMilestones && total >= 11900
      };
      this.querySelectorAll('[data-shipping-message]').forEach((message) => {
        message.hidden = !visible[message.dataset.shippingMessage];
      });
    }
  });
}
